import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import WorkforceScheduleGrid from './WorkforceScheduleGrid';
import LinkedSopDetails, { NI_PROCEDURE_SOPS } from './LinkedSopDetails';
import PharmacyOpeningHours from './PharmacyOpeningHours';
import { isSopManagement, SopManagementDetails } from './ComplianceSopManagement';
import { VERSION as CRITERIA_VERSION, catalogue as JURISDICTION_CATALOGUE, rowApplicability, locationOf, detailsFor, decorateGroups, badge } from './complianceJurisdiction';
import InspectorWorkingGrid from "./InspectorWorkingGrid";
// src/RegulatoryCompliance.js
// =============================================================================
// CHANGE (this version): consistent "Data source" model across every branch
// drill-down dialog and every data element.
//   • Every element row has a DATA SOURCE column:
//       – periodic-event elements show an "Update" button (record a value with
//         a chosen date, posted to periodic-tasks/entries);
//       – attestable elements show the recorded source (Head office / Paper /
//         3rd party…) or "Set source" if none is recorded yet;
//       – live Pharmace-driven elements show a static "Head office" tag —
//         "Head office holds evidence" MEANS the data is in Pharmace (the
//         head-office data areas), so there is no separate "Pharmace (live
//         data)" option anywhere.
//   • The ▸ toggle column is headed "Review data" (opens the source/review
//     panel). The old trailing "State" column is removed — it only repeated
//     the data source; done/not-done is carried by the row colour and ✓/✗.
//   • Attestations are stored via compliance/attestations (backend
//     headoffice.js v42) and count as compliant. A live stream that answers —
//     even "not done" — always outranks an attestation, except for
//     ALT_OVERRIDES_LIVE elements where the recorded system choice wins.
// =============================================================================
// Regulatory Compliance dashboard.
//
// A four-level drill-down for tracking UK community-pharmacy regulatory
// compliance across the fleet:
//
//   GROUP (tab)  →  ITEM  →  BRANCH  →  DATA ELEMENT
//
//   • GROUP  — a compliance area, shown as a tab. The tab header carries a
//              rolled-up % (mean of its items) with a green→red background.
//   • ITEM   — a specific requirement within the area (e.g. "Responsible
//              Pharmacist record"). Listed inside the tab, each with its own
//              rolled-up % (mean across branches) and colour.
//   • BRANCH — a pharmacy. Clicking an item shows every branch with its % for
//              that item (fraction of the item's data elements that are "done"
//              at that branch).
//   • DATA ELEMENT — the actual checkable things. Clicking a branch shows the
//              elements, green (done) / red (not done). Each element also has:
//                – a requirement badge: Mandatory / Recommended / Optional
//                – floating help text on hover (the ⓘ icon)
//                – an optional external link to source guidance
//
// Percentages roll UP from the data elements, so the whole tree is consistent:
// a tab's number is the honest mean of what's underneath it.
//
// DATA IS PLACEHOLDER for now. Every data element currently gets a synthetic
// done/not-done from a stable hash of (item + branch + element) so the colours
// look realistic and the roll-ups are believable while we wire up real sources
// one element at a time. Search for `PLACEHOLDER` to find the seam where each
// element's real value will eventually be fetched.
//
// The compliance taxonomy (groups/items/elements) is grounded in:
//   • GPhC Standards for Registered Pharmacies — the five Principles
//     (governance, staff, premises, services/medicines, equipment).
//   • Statutory/legal duties that sit alongside the GPhC standards:
//     controlled drugs, information governance (DSPT/UK GDPR), health & safety,
//     and NHS Terms of Service / CPAF.
// These are a sensible working structure, not a substitute for the source
// documents — each item links out to the relevant guidance where available.
// =============================================================================

import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useMaskNames, displayName } from "./nameMask";
import PeriodicTaskEntryDialog from "./PeriodicTaskEntryDialog";
import ComplianceOverview, { OVERVIEW_VIEWS } from "./ComplianceOverview";
import ComplianceWorkingGrid, { NearMissPanel, CleaningPanel, FridgePanel, partsStatus, PARTS_PARENTS, LIVE_ALIAS, DSP_GROUPS, PQS_GROUPS, webRowStatus, evidenceClickCapture, EvidenceViewerHost, openEvidence, PARTS_OF } from "./ComplianceWorkingGrid";
const branchLocation = (name, extra) => extra?.__grid?.jurisdictionByName?.[name] || null;
const applicableAt = (element, name, extra) => rowApplicability(element, branchLocation(name, extra)).applicable;
const API_BASE = apiUrl("");

// Fetch the periodic-task grid and index it for the compliance views.
async function fetchTaskGrid(token) {
  const gres = await fetch(`${API_BASE}/api/headoffice/${withOrgScope("periodic-tasks/grid")}`, {
    headers: token ? {
      Authorization: `Bearer ${token}`
    } : {}
  });
  if (!gres.ok) return null;
  const g = await gres.json();
  const rowsByName = {};
  for (const r of g.rows || []) if (r && r.pharmacyName) rowsByName[r.pharmacyName] = r;
  const dataTypeById = {},
    optionsById = {},
    frequencyById = {},
    idByName = {},
    idByKey = {},
    nameById = {};
  for (const t of g.tasks || []) {
    dataTypeById[String(t.taskId)] = t.dataType;
    optionsById[String(t.taskId)] = t.options || [];
    frequencyById[String(t.taskId)] = t.frequency || null;
    if (t.description) idByName[t.description] = String(t.taskId);
    if (t.description) nameById[String(t.taskId)] = t.description;
    if (t.biKey) idByKey[t.biKey] = String(t.taskId);
  }
  return {
    rowsByName,
    dataTypeById,
    optionsById,
    frequencyById,
    idByName,
    idByKey,
    nameById
  };
}

// Record a periodic-task value for one branch, with a caller-chosen date.
async function postTaskEntry(token, {
  taskId,
  dbName,
  pharmacyId,
  value,
  recordedAt,
  note
}) {
  const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {})
    },
    body: JSON.stringify({
      taskId,
      dbName,
      pharmacyId,
      value,
      recordedAt,
      note: note || null,
      recordedBy: localStorage.getItem("userName") || null
    })
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}
async function postAttestation(token, path, body) {
  const res = await fetch(`${API_BASE}/api/headoffice/compliance/attestations/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {})
    },
    body: JSON.stringify(body)
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

// Data-source choices offered in the per-element dialog. "Head office holds
// evidence" means the data lives in Pharmace (the head-office panel data
// areas) — there is deliberately NO separate "Pharmace (live data)" option,
// because that would mean the same thing.
const ALT_SOURCES = ["Head office holds evidence", "Paper records", "Third-party system", "Other"];
// Short display label for a recorded alt source — shown on the row button and
// in the state column so the CHOICE is visible, not just a generic "Alt".
function shortAltLabel(source) {
  const sl = String(source || "").toLowerCase();
  if (sl.startsWith("paper")) return "Paper";
  if (sl.startsWith("third")) return "3rd party";
  if (sl.startsWith("head office")) return "Head office";
  if (!source) return "Source";
  return source.length > 12 ? source.slice(0, 12) + "…" : source;
}

// -----------------------------------------------------------------------------
// Organisation (member-group) scoping — mirrors HeadOffice.js.
//
// The viewer's organisation is stored in localStorage. When present, we pass it
// to the backend so the fleet fan-out is restricted to that group's member
// pharmacies. "Pharmace all" (admin) and an absent value both mean "whole
// fleet" server-side, so it is always safe to send.
//   - GET  endpoints: appended as ?organisationName=...
//   - POST endpoints: merged into the request body as { organisationName }.
// -----------------------------------------------------------------------------
function viewerOrg() {
  return localStorage.getItem("organisationName") || "";
}
function withOrgScope(path) {
  const org = viewerOrg();
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organisationName=${encodeURIComponent(org)}`;
}
function withOrgBody(body) {
  const org = viewerOrg();
  return org ? {
    ...(body || {}),
    organisationName: org
  } : body || {};
}

// -----------------------------------------------------------------------------
// Colour helpers (same green→red scheme used on the Head Office dashboard, so
// the visual language is consistent across the platform).
// -----------------------------------------------------------------------------

// Background for a 0–100 percentage: full green at 100, full red at <=10 (but
// >0), interpolated through amber between. Text stays dark for legibility.
function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return "#eee";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t); // 0 = red, 120 = green
  return `hsl(${hue}, 75%, 85%)`;
}
// Slightly stronger fill for the tab headers so they read as headers.
function pctBgStrong(pct) {
  if (pct == null || Number.isNaN(pct)) return "#ddd";
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 70%, 72%)`;
}
const fmtPct = n => n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`;

// Modules that expose the 12-month effectiveness endpoint (percentage metrics).
const EFFECTIVENESS_MODULES = ["fridge", "cleaning", "date-checking", "sop", "mhra"];

// Colour band for an effectiveness percentage against its threshold:
// green at/above threshold, amber within 10 points below, red beyond that.
function pctBand(pct, threshold) {
  if (pct == null) return {
    bg: "#F5F5F5",
    fg: "#5D5D5D",
    label: "No data"
  };
  if (pct >= threshold) return {
    bg: "#F6FFED",
    fg: "#389E0D",
    label: "Meeting target"
  };
  if (pct >= threshold - 10) return {
    bg: "#FFFBE6",
    fg: "#AD6800",
    label: "Below target"
  };
  return {
    bg: "#FFF1F0",
    fg: "#A8071A",
    label: "Well below target"
  };
}

// -----------------------------------------------------------------------------
// Requirement-level badge. Mandatory (legal/regulatory must), Recommended
// (best practice / strongly expected), Optional (nice to have).
// -----------------------------------------------------------------------------
const REQ_META = {
  mandatory: {
    label: "Mandatory",
    bg: "#FFF1F0",
    fg: "#A8071A",
    border: "#FFA39E"
  },
  recommended: {
    label: "Recommended",
    bg: "#FFF7E6",
    fg: "#AD4E00",
    border: "#FFD666"
  },
  optional: {
    label: "Optional",
    bg: "#F0F5FF",
    fg: "#1D39C4",
    border: "#ADC6FF"
  }
};
function ReqBadge({
  requirement
}) {
  const m = REQ_META[requirement] || REQ_META.optional;
  return <span style={{
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "1px 6px",
    borderRadius: 4,
    background: m.bg,
    color: m.fg,
    border: `1px solid ${m.border}`,
    whiteSpace: "nowrap"
  }}>
      {m.label}
    </span>;
}

// -----------------------------------------------------------------------------
// Floating help. A small ⓘ that reveals a tooltip on hover/focus, with the
// explanation and (optionally) a "More info" external link.
// -----------------------------------------------------------------------------
function HelpInfo({
  help,
  link
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // {top,left} in viewport coords
  const btnRef = React.useRef(null);
  if (!help && !link) return null;

  // Compute a fixed-position anchor from the button's rect so the tooltip is
  // never clipped by (or reflow-looped against) the scrolling modal frame.
  const show = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Prefer below; if too close to viewport bottom, place above.
      const below = r.bottom + 8;
      const placeAbove = below + 120 > window.innerHeight;
      setPos({
        top: placeAbove ? r.top - 8 : below,
        left: Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150),
        above: placeAbove
      });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);
  return <span style={{
    display: "inline-flex",
    alignItems: "center"
  }}>
      <button ref={btnRef} type="button" aria-label="Help" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onClick={e => {
      e.stopPropagation();
      open ? hide() : show();
    }} style={{
      width: 16,
      height: 16,
      lineHeight: "14px",
      padding: 0,
      borderRadius: "50%",
      border: "1px solid #8C8C8C",
      background: "#FAFAFA",
      color: "#5D5D5D",
      fontSize: 11,
      fontWeight: 700,
      cursor: "help"
    }}>
        i
      </button>
      {open && pos && <span role="tooltip" style={{
      position: "fixed",
      zIndex: 2000,
      top: pos.top,
      left: pos.left,
      transform: pos.above ? "translate(-50%, -100%)" : "translateX(-50%)",
      width: 280,
      background: "#262626",
      color: "#FAFAFA",
      fontSize: 12,
      lineHeight: 1.4,
      padding: "8px 10px",
      borderRadius: 6,
      boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
      fontWeight: 400,
      textTransform: "none",
      letterSpacing: 0,
      pointerEvents: "none"
    }}>
          {help}
          {link && <span style={{
        display: "block",
        marginTop: 6
      }}>
              <a href={link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{
          color: "#91D5FF",
          textDecoration: "underline",
          pointerEvents: "auto"
        }}>
                More info ↗
              </a>
            </span>}
        </span>}
    </span>;
}

// -----------------------------------------------------------------------------
// PLACEHOLDER branch list. Eventually this comes from the fleet (userData /
// an API). For now a fixed set so the drill-down is demonstrable.
// -----------------------------------------------------------------------------

// Stable string hash → 0..1, so placeholder "done" states are deterministic
// (a given element at a given branch always shows the same colour between
// renders) but still look varied across the grid.

// =============================================================================
// LIVE DATA WIRING (2026-06)
//
// Some compliance elements are backed by real Head Office module endpoints.
// Each "fleet-status" endpoint returns one row per active pharmacy:
//   [{ pharmacyId, pharmacyName, status: 'ok'|'unchecked'|'fail', ... }]
// We fetch each needed endpoint ONCE, key the rows by pharmacyName, and map a
// branch's module status to done/not-done for the relevant element(s).
//
// Everything NOT in LIVE_SOURCES still falls back to placeholder values, so the
// board stays complete while we wire elements one at a time. An element with a
// live source shows a small "live" tag in the UI.
//
// Mapping note: a module's fleet-status is a single pass/fail per pharmacy, but
// a tab may have several elements under one module (e.g. fridge has daily-log,
// excursions, date-check). Where the endpoint only supports a coarse signal we
// map the module status onto the closest element and leave the finer ones on
// placeholder until a dedicated metric exists.
//
// status → done:  'ok' = done(true); 'fail'/'unchecked' = not done(false).
// =============================================================================

// element id  →  { endpoint, done(statusRow) }
const LIVE_SOURCES = {
  "near-miss": {
    endpoint: "compliance/near-miss-status",
    done: r => r.recentIncidents > 0,
    usage: r => !r.hasRecords ? {
      usage: "never",
      note: "No near misses recorded in Pharmace at this branch"
    } : r.recentIncidents > 0 ? {
      usage: "ok",
      note: `${r.totalIncidents} near miss(es) recorded · ${r.recentIncidents} in the last 30 days`
    } : {
      usage: "lapsed",
      note: `${r.totalIncidents} near miss(es) recorded · none in the last 30 days`
    }
  },
  // NOTE: fridge "logged" + "in range" are no longer binary elements — they are
  // the two 12-month effectiveness metrics on the cold-chain item. The old
  // fridge/fleet-status point-in-time wiring was removed because it went red
  // simply when today's reading was missing, which isn't a compliance signal.

  // --- Fridge temperatures: every fridge's latest reading in range, no recheck
  // overdue. Not yet logged today is fine (the pharmacy may not have opened). ---
  "fridge-log": {
    endpoint: "fridge/fleet-status",
    done: r => (r.fridgeCount || 0) > 0 && r.status !== "fail",
    usage: r => {
      if (!(r.fridgeCount > 0)) return {
        usage: "never",
        note: "Pharmace fridge monitoring not used at this branch"
      };
      if (r.status === "fail") return {
        usage: "gaps",
        note: (r.reasons || []).filter(x => !/not logged today/.test(x)).join(" · ") || "A fridge is out of range"
      };
      return {
        usage: "ok",
        note: `${r.loggedCount || 0} of ${r.fridgeCount} fridge${r.fridgeCount === 1 ? "" : "s"} logged today · latest readings in range`
      };
    }
  },
  // --- Cleaning ---
  "env-clean": {
    endpoint: "cleaning/fleet-status",
    done: r => r.status === "ok"
  },
  // --- CD register: in place when every active register has been balance-
  // checked within the site's interval and no discrepancy is open. A register
  // never balance-checked counts as NOT in place. No registers at all = the
  // branch doesn't use Pharmace's CD register (the Working grid's task / note
  // covers it instead). ---
  "cd-register": {
    endpoint: "cd-register/fleet-status",
    done: r => r.status === "ok" && (r.registerCount || 0) > 0,
    usage: r => {
      if (!(r.registerCount > 0)) return {
        usage: "never",
        note: "Pharmace CD register not used at this branch"
      };
      if (r.status === "ok") return {
        usage: "ok",
        note: `All ${r.registerCount} register${r.registerCount === 1 ? "" : "s"} balance-checked within ${r.intervalDays || 7} days, no open discrepancies`
      };
      return {
        usage: "gaps",
        note: (r.reasons || []).join(" · ") || "Balance checks not up to date"
      };
    }
  },
  // CD destruction: every destruction in the last 12 months names who
  // destroyed it and a witness. No destructions in the year is fine.
  "cd-destruction": {
    endpoint: "cd-register/fleet-status",
    done: r => (r.registerCount || 0) > 0 && !r.destructionsNoWitness12m && !r.destructionsNoDestroyer12m,
    usage: r => {
      if (!(r.registerCount > 0)) return {
        usage: "never",
        note: "Pharmace CD register not used at this branch"
      };
      if (r.destructions12m == null) return {
        usage: "gaps",
        note: "Destruction figures not available — update the backend"
      };
      const bad = [r.destructionsNoWitness12m ? `${r.destructionsNoWitness12m} without a witness` : "", r.destructionsNoDestroyer12m ? `${r.destructionsNoDestroyer12m} without who destroyed it` : ""].filter(Boolean);
      if (bad.length) return {
        usage: "gaps",
        note: `${r.destructions12m} destruction${r.destructions12m === 1 ? "" : "s"} in the last 12 months — ${bad.join(", ")}`
      };
      return {
        usage: "ok",
        note: r.destructions12m ? `${r.destructions12m} destruction${r.destructions12m === 1 ? "" : "s"} in the last 12 months, all witnessed` : "No destructions in the last 12 months"
      };
    }
  },
  // CD Accountable Officer, part 2: no discrepancy left open over 7 days.
  "cdao--discrepancies": {
    endpoint: "cd-register/fleet-status",
    done: r => (r.registerCount || 0) > 0 && !r.openOver7Count,
    usage: r => {
      if (!(r.registerCount > 0)) return {
        usage: "never",
        note: "Pharmace CD register not used at this branch"
      };
      if (r.openOver7Count == null) return {
        usage: "gaps",
        note: "Discrepancy figures not available — update the backend"
      };
      if (r.openOver7Count) return {
        usage: "gaps",
        note: `${r.openOver7Count} discrepanc${r.openOver7Count === 1 ? "y" : "ies"} open for more than 7 days`
      };
      return {
        usage: "ok",
        note: r.openDiscrepancyCount ? `${r.openDiscrepancyCount} open, none older than 7 days` : "No open discrepancies"
      };
    }
  },
  // --- Handover diary (fleet-status: ok when nothing overdue) ---
  // Mapped to the CD-officer/handover governance proxy isn't right; handover
  // is its own concern. There's no handover element in the taxonomy yet, so
  // we surface it under the closest existing one only if added later.
  // (Endpoint available: handover/fleet-status.)

  // --- Patient safety / LFPSE (snapshot, per pharmacy) ---
  // done = no incomplete LFPSE entries outstanding.
  "lfpse": {
    endpoint: "patient-safety/snapshot",
    done: r => r.hasRecords === true && (r.lfpseIncompleteCount || 0) === 0,
    usage: r => r.hasRecords !== true ? {
      usage: "never",
      note: "No patient safety incidents recorded in Pharmace at this branch"
    } : (r.lfpseIncompleteCount || 0) > 0 ? {
      usage: "gaps",
      note: `${r.totalIncidents} incident(s) recorded · ${r.lfpseIncompleteCount} LFPSE submission(s) missing a reference`
    } : {
      usage: "ok",
      note: `${r.totalIncidents} patient safety incident(s) recorded in Pharmace · no incomplete LFPSE references`
    }
  },
  // --- Accident book / RIDDOR (snapshot, per pharmacy) ---
  // "Accident book maintained" = the book is in active use (has entries). A
  // pharmacy with accident records is maintaining the book, regardless of
  // whether individual reviews are overdue (review timeliness is a separate
  // concern, surfaced via reviewOverdueCount if needed).
  "riddor-book": {
    endpoint: "accident-book/snapshot",
    done: r => !!r.hasEntries || (r.totalEntries || 0) > 0
  },
  // "RIDDOR-reportable events reported" = no accident left awaiting a RIDDOR
  // decision (NOT_YET_KNOWN). Those are the genuine reporting-compliance risk.
  "riddor-report": {
    endpoint: "accident-book/snapshot",
    done: r => (r.notYetKnownCount || 0) === 0
  },
  // --- RP sign-in (snapshot, per pharmacy) ---
  // RP record kept: done if currently signed in, or RP activity recorded in
  // the period (daysCovered > 0).
  "rp-log": {
    endpoint: "rp-signin/snapshot",
    done: r => !!r.currentlySignedIn || (r.daysCovered || r.distinctDaysCovered || 0) > 0
  },
  // NOTE: "rp-absence" (Absence rules followed, max 2h) is deliberately NOT
  // linked to the RP sign-in log — it is a periodic event, recorded via a
  // periodic-task def (see TASK_DEF_BY_KEY below).

  // --- SOP per-staff sign-off (sop_assignments) ---
  // done = no assignments overdue (everything assigned is signed or not yet due).
  "sop-signed": {
    endpoint: "sop/sign-off-fleet-status",
    done: r => r.status === "ok" || (r.overdueCount || 0) === 0
  },
  // --- NHS Terms of Service: opening hours configured (organisations) ---
  // done = opening hours are recorded for the branch.
  "tos-hours": {
    endpoint: "compliance-grid/pharmacy-opening-hours",
    done: r => !!r.hasOpeningHours,
    usage: r => ({
      usage: r.hasOpeningHours ? "ok" : "missing",
      note: r.hasOpeningHours ? `${Number(r.weeklyHours.toFixed(2))} pharmacy opening hours per week` : "Pharmacy opening hours missing or invalid"
    })
  },
  "ni-tos-hours": {
    endpoint: "compliance-grid/pharmacy-opening-hours",
    done: r => !!r.hasOpeningHours,
    usage: r => ({
      usage: r.hasOpeningHours ? "ok" : "missing",
      note: r.hasOpeningHours ? `${Number(r.weeklyHours.toFixed(2))} pharmacy opening hours per week` : "Pharmacy opening hours missing or invalid"
    })
  },
  // --- Workforce (staffs + pharma_role_datas per tenant) ---
  // Adequate staffing: done if at least one active pharmacist on the books.
  "wb-staffing": {
    endpoint: "workforce/fleet-status",
    done: r => !!r.hasPharmacist
  },
  // --- MHRA safety alerts (mrha_safety_alert_logs per tenant) ---
  // done = no logged alert left without a recorded outcome.
  "sup-mhra": {
    endpoint: "mhra/fleet-status",
    done: r => (r.outstanding || 0) === 0
  }
};

// Distinct endpoints we need to fetch, derived from LIVE_SOURCES.
const LIVE_ENDPOINTS = Array.from(new Set(Object.values(LIVE_SOURCES).map(s => s.endpoint)));

// -----------------------------------------------------------------------------
// USAGE LEVELS (2026-10) — how a branch is using the Pharmace module behind an
// element, rather than a bare done / not done:
//   never  — Pharmace has never been used for this at the branch      (grey)
//   lapsed — used in the past, nothing in the recent window           (amber)
//   gaps   — in use now, but records are missing / out of date        (red)
//   ok     — in use now and up to date                                (green)
// Each entry: path(pharmacyId) → a per-branch head-office GET, and
// classify(json) → { usage, note } (null = no answer; fall back to the plain
// live rule). Fetched once per branch with the other feeds. Where a branch has
// a usage answer it decides the element's state. Add modules here one by one.
// -----------------------------------------------------------------------------
const USAGE_KEY = elementId => `usage:${elementId}`;
const ukDate = d => new Date(d).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
});
const daysAgo = (d, now = new Date()) => Math.max(0, Math.floor((now - new Date(d)) / 86400000));

// RP record: assess up to 28 days, starting at first-ever use for new branches. Today is
// not held against a branch until its RP has had the chance to sign in, so one
// uncovered day is allowed when nobody is signed in right now.
function rpUsage(r, now = new Date()) {
  if (!r || r.unresolved) return null;
  const sessions = Array.isArray(r.sessions) ? r.sessions : [];
  const covered = Number(r.daysCovered) || 0;
  const win = r.windowDays || 28;
  const trading = r.tradingDays ?? win;
  const unit = r.tradingDaysOnly ? "trading days" : "days";
  const period = r.newUserWindow ? `since first use (${win} days)` : `last ${win} days`;
  if (!sessions.length && !r.currentlySignedIn && covered === 0) {
    return {
      usage: "never",
      note: "RP sign-in has never been used at this branch"
    };
  }
  if (!r.currentlySignedIn && covered === 0) {
    let last = null;
    for (const x of sessions) {
      for (const t of [x.signOutTime, x.signInTime]) if (t && (!last || new Date(t) > last)) last = new Date(t);
    }
    return {
      usage: "lapsed",
      lastUsed: last ? last.toISOString() : null,
      note: last ? `Lapsed — last RP sign-in ${ukDate(last)} (${daysAgo(last, now)} days ago)` : `Lapsed — no RP sign-in in the last ${win} days`
    };
  }
  const allowance = r.currentlySignedIn ? 0 : 1;
  if (covered >= trading - allowance) {
    return {
      usage: "ok",
      note: `${r.currentlySignedIn ? "RP signed in now · " : ""}${covered} of ${trading} ${unit} covered (${period})`
    };
  }
  return {
    usage: "gaps",
    note: `In use, with gaps — ${covered} of ${trading} ${unit} covered (${period})`
  };
}
const USAGE_SOURCES = {
  "rp-log": {
    path: pid => `compliance/rp-status?pharmacyId=${pid}`,
    classify: rpUsage
  }
};
const USAGE_STYLE = {
  never: {
    row: "#FFF7E6",
    fg: "#AD4E00",
    mark: "!",
    tag: "Not yet used",
    tagBg: "#FFE58F",
    tagBorder: "#FA8C16"
  },
  lapsed: {
    row: "#FFF7E6",
    fg: "#AD4E00",
    mark: "!",
    tag: "Lapsed",
    tagBg: "#FFE58F",
    tagBorder: "#FA8C16"
  },
  gaps: {
    row: "#FFF1F0",
    fg: "#A8071A",
    mark: "✗",
    tag: "Gaps",
    tagBg: "#FFCCC7",
    tagBorder: "#FF7875"
  },
  ok: {
    row: "#F6FFED",
    fg: "#389E0D",
    mark: "✓",
    tag: "Live",
    tagBg: "#F6FFED",
    tagBorder: "#B7EB8F"
  },
  missing: {
    row: "#FFF1F0",
    fg: "#A8071A",
    mark: "✗",
    tag: "Missing",
    tagBg: "#FFCCC7",
    tagBorder: "#FF7875"
  },
  awaiting: {
    row: "#FFF7E6",
    fg: "#AD4E00",
    mark: "…",
    tag: "Requested",
    tagBg: "#FFE58F",
    tagBorder: "#FA8C16"
  }
};

// -----------------------------------------------------------------------------
// EXTRA live sources — POST endpoints that live at the API root (not under
// /api/headoffice) and return per-branch activity rather than a pass/fail
// status. We fetch each once, map its branch rows by pharmacyName, and derive
// done/not-done from activity. These come from routes.js, not headoffice.js.
//
// Each entry: { path, method, body, rowsOf(json), nameOf(row), done(row) }
//   path     — root-relative API path (POST)
//   rowsOf   — pull the per-branch array out of the response
//   nameOf   — branch name from a row
//   done     — compliance done/not-done for that branch
// keyed by a synthetic source id so multiple elements can share one fetch.
// -----------------------------------------------------------------------------
const EXTRA_SOURCES = {
  // Near-miss: branch is compliant on "log maintained" if it's actively using
  // the near-miss tool this month (isUsingTool) or has logged incidents.
  "nearmiss-grid": {
    path: "/near_miss_analysis_grid",
    body: {},
    rowsOf: j => j.branches || [],
    nameOf: r => r.pharmacyName,
    done: r => !!r.isUsingTool || (r.incidentsThisMonth || 0) > 0
  },
  // NMS: branch is compliant on "NMS records" if it has any NMS activity data.
  "nms-analysis": {
    path: "/nms_analysis",
    body: {},
    rowsOf: j => j.data || [],
    nameOf: r => r.pharmacyName,
    done: r => Array.isArray(r.nms) && r.nms.length > 0,
    usage: r => !Array.isArray(r.nms) || !r.nms.length ? {
      usage: "never",
      note: "NMS is connected — no records entered for this pharmacy yet"
    } : {
      usage: "ok",
      note: `${r.nms.reduce((total, group) => total + (group.breakdown || []).reduce((sum, item) => sum + (Number(item.count) || 0), 0), 0)} NMS record(s) in Pharmace`
    }
  }
};

// compliance element id → EXTRA_SOURCES key
const EXTRA_FOR = {
  "near-miss": "nearmiss-grid",
  "adv-nms": "nms-analysis"
};

// Some compliance elements are evidenced by a CORE periodic task, found by
// its biKey (a stable marker; never by name). EPS operational is evidenced by
// the monthly ITEM COUNT task (biKey "item_count" — item count submitted for
// claiming = the branch is active on EPS; the same task Workforce, Business
// Insights and the monthly report read). Its name and frequency are locked in
// Periodic Tasks. done = a value recorded in-date.
// "rp-absence": now evidenced by the Pharmace SOP covering RP absence (Working grid), not a periodic task.
// element id → periodic task biKey.
const TASK_DEF_BY_KEY = {
  "tos-eps": "item_count"
};
const TASK_KEY_LABEL = {
  item_count: "Item count"
};

// -----------------------------------------------------------------------------
// Periodic-task-backed elements.
//
// The 45 attestation elements are recorded as Periodic Task defs. Once a def
// exists, paste its _id (the $oid string) here against the compliance element.
// The dashboard then reads that task's value per branch from periodic-tasks/grid
// and treats a recorded "done"-ish value as complete.
//
// TASK_DEF_FOR: compliance element id → periodic_task_defs _id (string).
// Leave blank until the def is created; blank entries stay on placeholder.
//
// Example once created:
//   "indemnity": "6a1d22a2e534b456d409a75a",
// -----------------------------------------------------------------------------
const TASK_DEF_FOR = {
  // 45 compliance attestations → periodic_task_defs _id (imported 2026-06-29).
  // "cal-cmp", "cal-scales", "cal-thermometer": now yearly Working grid tasks, not periodic tasks.
  // "cd-officer": now two Working grid parts (contact details photo, discrepancies from the CD register).
  // "coshh-assess", "hs-*": now Working grid items (uploads, photo tasks), not periodic tasks.
  // "dspt-*": now Working grid items (DSPT confirmation, IG lead acceptance, training record), not periodic tasks.
  // "env-consultation": now a Working grid photo task (GRID_TASK_ONLY), not a periodic task.
  // "env-temperature": now two Working grid photo-task parts (thermometer, monthly records).
  // "gdpr-*": now Working grid items (ICO certificate, privacy notice in two parts, SOPs), not periodic tasks.

  // "it-backup" (Pharmace SOP / upload), "it-encryption" (yearly confirm task) and
  // "it-pmr-secure" (two parts) are now Working grid items, not periodic tasks.
  // "indemnity", "reg-pharmacist", "reg-technician", "rp-displayed", "sop-dispensing",
  // "wb-speakup": already set up in the Working grid — periodic tasks no longer used.
  // "adv-*", "cpaf-*", "sup-distance", "sup-frp": now yearly Working grid confirmation tasks.
  // "reg-revalidation": derived from the GPhC registrations (reg-pharmacist / reg-technician), not a periodic task.
  // "sec-cd-cabinet": now three Working grid photo-task parts (cabinet, keys, segregation).
  // "sec-pmedicine": now two Working grid parts (photo of display, risk assessment / SOP).
  // "sec-pom-access": now two Working grid parts (photo of arrangements, SOP).
  // "tos-disposal", "tos-signposting": now two Working grid parts each (yearly confirmation + SOP).
  // "trn-*": now Working grid uploads of the branch training record, not periodic tasks.
  // "waste-contract" (upload), "waste-segregation" (yearly photo task): now Working grid items.
};

// Whether ANY element is task-backed yet (controls whether we fetch the grid).
const ANY_TASK_BACKED = Object.values(TASK_DEF_FOR).some(Boolean) || Object.keys(TASK_DEF_BY_KEY).length > 0;
// Items evidenced only by a Working grid photo task (not Pharmace periodic
// tasks): the grid task decides their status.
// cd-register: Pharmace CD register where used, else a 3-monthly photo task.
const GRID_TASK_ONLY = new Set(["env-consultation", "cd-register", "cd-destruction", "cal-thermometer", "cal-scales", "cal-cmp", "it-encryption", "hs-firstaid", "hs-poster", "hs-elinsurance", "dspt-iao", "waste-segregation", "rp-displayed", "adv-dms", "adv-pf", "sup-frp", "cpaf-gateway", "cpaf-pages", "cpaf-questionnaire", "sup-distance"]);

// Map a periodic-task grid cell to done/not-done. A cell looks like
// { value, recordedAt, state }. We treat it as done when it's in-date AND the
// recorded value is affirmative (boolean true / "yes", a non-empty date, or a
// non-empty/!="no" text/choice). Overdue or never-recorded = not done.
// BUG FIX 2026-07: the backend emits the in-date state as "ok" (see
// ptDueState in headoffice.js) — this previously tested for "in-date", so
// value-less in-date cells were wrongly counted as not done.
function taskCellDone(cell, dataType) {
  if (!cell) return false;
  if (cell.state === "overdue" || cell.state === "never") return false;
  if (cell.state === "na") return true; // not relevant = not counted against
  const inDate = cell.state === "ok" || cell.state === "in-date" || cell.state === "due";
  const v = cell.value;
  if (v == null || v === "") return inDate;
  if (dataType === "boolean") return v === true || v === "true" || v === "yes" || v === 1;
  if (dataType === "numeric") return Number(v) > 0;
  if (dataType === "choice") return String(v).toLowerCase() !== "no" && String(v) !== "";
  return true; // date/text present
}

// Resolve done/not-done for an element at a branch. Order of preference:
//   1. A direct module endpoint (LIVE_SOURCES)
//   2. An extra POST endpoint (EXTRA_FOR / extraData)
//   3. A periodic-task def (TASK_DEF_FOR) read from the grid
//   4. Placeholder
// Returns { done, live }.
// Elements where the alt-evidence choice is offered ALONGSIDE a live Pharmace
// stream (the "which system does this branch use?" selector). For these, a
// recorded attestation (paper / third-party) OUTRANKS the live data — the
// branch has declared it runs this record elsewhere, so the Pharmace figures
// are informational, not the compliance state.
const ALT_OVERRIDES_LIVE = new Set(["near-miss", "lfpse", "sup-mhra", "riddor-book", "riddor-report", "adv-nms", "tos-hours"]);
function resolveDone(groupId, itemId, branchName, elementId, liveData, taskGrid, extraData, attData) {
  if (!applicableAt(elementId, branchName, extraData)) return {
    done: false,
    live: false,
    noData: true,
    notApplicable: true
  };
  // Attestation fallback: when this element has NO digital data stream for the
  // branch (not resolved by a live source or a recorded task), an active
  // "alternative evidence" attestation (paper records etc.) counts as done.
  // A live stream that answers — even "not done" — always outranks it,
  // EXCEPT for ALT_OVERRIDES_LIVE elements, where the recorded system choice
  // wins over the live stream.
  const att = attData && attData[branchName] && attData[branchName][elementId];
  if (att && ALT_OVERRIDES_LIVE.has(elementId)) {
    return {
      done: true,
      live: true,
      alt: att
    };
  }
  // Evidence set up in the Working grid (Pharmace SOP link / document upload).
  const gridEv = extraData?.__grid?.cells?.[`${elementId}|${branchName}`];
  if (gridEv) {
    if (gridEv.usage) return {
      done: gridEv.usage === "ok",
      live: true,
      usage: gridEv.usage,
      usageNote: gridEv.note,
      gridFiles: gridEv.files || null,
      nmGrid: gridEv.nm || null,
      gridParts: gridEv.parts || null
    };
    const ok = gridEv.status === "current" || gridEv.status === "due-soon" || gridEv.status === "linked" || gridEv.status === "na";
    return {
      done: ok,
      live: true,
      usage: ok ? "ok" : gridEv.status === "awaiting" ? "awaiting" : "missing",
      usageNote: gridEv.note,
      gridFiles: gridEv.files || null
    };
  }
  // Usage levels (never / lapsed / gaps / ok) outrank the plain live rule.
  // A branch that has never used the module can still be covered by
  // alternative evidence (paper records etc.).
  if (USAGE_SOURCES[elementId] && extraData && extraData[USAGE_KEY(elementId)]) {
    const u = extraData[USAGE_KEY(elementId)][branchName];
    if (u && u.usage) {
      if (u.usage === "never" && att) return {
        done: true,
        live: true,
        alt: att
      };
      return {
        done: u.usage === "ok",
        live: true,
        noData: u.usage === "never",
        usage: u.usage,
        usageNote: u.note
      };
    }
  }
  // Track whether this element is wired to a source that DID load — so a missing
  // branch row means "no data for this branch" (not done), not a fake placeholder.
  let wiredButNoRow = false;
  const src = LIVE_SOURCES[elementId];
  if (src) {
    if (liveData && liveData[src.endpoint]) {
      const row = liveData[src.endpoint][branchName];
      if (row) {
        try {
          if (src.usage) {
            const u = src.usage(row);
            if (u.usage === "never" && att) return {
              done: true,
              live: true,
              alt: att
            };
            return {
              done: u.usage === "ok",
              live: true,
              noData: u.usage === "never",
              usage: u.usage,
              usageNote: u.note
            };
          }
          return {
            done: !!src.done(row),
            live: true
          };
        } catch {/* fall through */}
      } else {
        wiredButNoRow = true; // endpoint loaded, but this branch wasn't in it
      }
    }
  }
  const exKey = EXTRA_FOR[elementId];
  if (exKey) {
    if (extraData && extraData[exKey]) {
      const ex = EXTRA_SOURCES[exKey];
      const row = extraData[exKey][branchName];
      if (row) {
        try {
          if (ex.usage) {
            const usage = ex.usage(row);
            return {
              done: usage.usage === "ok",
              live: true,
              noData: usage.usage === "never",
              usage: usage.usage,
              usageNote: usage.note
            };
          }
          return {
            done: !!ex.done(row),
            live: true
          };
        } catch {/* fall through */}
      } else {
        wiredButNoRow = true;
      }
    }
  }
  const defId = TASK_DEF_FOR[elementId];
  if (defId) {
    if (taskGrid && taskGrid.rowsByName) {
      const row = taskGrid.rowsByName[branchName];
      const dataType = taskGrid.dataTypeById?.[defId];
      if (row && row.cells && row.cells[defId]) {
        return {
          done: taskCellDone(row.cells[defId], dataType),
          live: true
        };
      }
      // Task is wired; a missing cell means "never recorded" = not done.
      wiredButNoRow = true;
    }
  }
  // Key-matched core task (EPS → the item count, biKey "item_count").
  const taskKey = TASK_DEF_BY_KEY[elementId];
  if (taskKey) {
    if (taskGrid && taskGrid.idByKey) {
      const tId = taskGrid.idByKey[taskKey];
      if (tId) {
        const row = taskGrid.rowsByName[branchName];
        const dataType = taskGrid.dataTypeById?.[tId];
        if (row && row.cells && row.cells[tId]) {
          return {
            done: taskCellDone(row.cells[tId], dataType),
            live: true
          };
        }
        wiredButNoRow = true;
      }
    }
  }

  // Wired to a real source, but no data for THIS branch → honestly "not done /
  // no data". This is the correct compliance signal (e.g. a pharmacy with no
  // accident-book activity), not a synthetic placeholder.
  if (wiredButNoRow) {
    if (att) return {
      done: true,
      live: true,
      alt: att
    };
    return {
      done: false,
      live: true,
      noData: true
    };
  }

  // Not wired to anything → attestation if present, else placeholder
  // (synthetic, clearly flagged in the UI).
  if (att) return {
    done: true,
    live: true,
    alt: att
  };
  // (2026-10) No synthetic values any more: an item with no data source and
  // no Working grid set-up counts as NOT in place and shows as "Not set".
  return {
    done: false,
    live: false,
    noData: true,
    notSet: true
  };
}

// Is this element wired to a live source (module endpoint, extra POST, or task)?
function elementIsLive(elementId) {
  return !!LIVE_SOURCES[elementId] || !!EXTRA_FOR[elementId] || !!TASK_DEF_FOR[elementId] || !!TASK_DEF_BY_KEY[elementId];
}

// Friendly name of the Pharmace module behind a live element (Working grid).
const MODULE_NAMES = {
  "cleaning": "Cleaning",
  "cd-register": "CD register",
  "patient-safety": "Patient safety (LFPSE)",
  "accident-book": "Accident book",
  "rp-signin": "RP sign-in",
  "sop": "SOP sign-off",
  "opening-hours": "Opening hours",
  "workforce": "Workforce",
  "mhra": "MHRA alerts",
  "nearmiss-grid": "Near-miss tool",
  "nms-analysis": "NMS"
};
function liveModuleName(elementId) {
  if (["tos-hours", "ni-tos-hours"].includes(elementId)) return "Opening hours";
  if (elementId === "reg-revalidation") return "GPhC registrations";
  const src = LIVE_SOURCES[elementId];
  if (src) return MODULE_NAMES[src.endpoint.split("/")[0]] || src.endpoint.split("/")[0];
  if (EXTRA_FOR[elementId]) return MODULE_NAMES[EXTRA_FOR[elementId]] || EXTRA_FOR[elementId];
  if (TASK_DEF_FOR[elementId]) return "Periodic tasks";
  if (TASK_DEF_BY_KEY[elementId]) return `Periodic task “${TASK_KEY_LABEL[TASK_DEF_BY_KEY[elementId]] || TASK_DEF_BY_KEY[elementId]}”`;
  return null;
}

// -----------------------------------------------------------------------------
// Source descriptor — for debugging/validation. For a given element, returns
// where the done/not-done value comes from: the API call, its parameters, the
// per-branch field consumed, and the rule that decides done. Used by the modal's
// expandable "source" panel. Returns null for placeholder-only elements.
// -----------------------------------------------------------------------------
// Per-element rule text (mirrors the done() lambdas above in plain English).
const RULE_TEXT = {
  // module fleet-status
  "env-clean": "done = status === 'ok' (nothing overdue/unlogged)",
  "cd-destruction": "not used = no active registers · done = every destruction in the last 12 months names who destroyed it and a witness (none in the year is fine)",
  "cdao--discrepancies": "not used = no active registers · done = no CD discrepancy open for more than 7 days",
  "cd-register": "not used = no active registers · done = every register balance-checked within the interval (default 7 days) and no open discrepancies · never-checked or overdue registers, or an open discrepancy = not done",
  "lfpse": "done = lfpseIncompleteCount === 0",
  "riddor-book": "done = hasEntries (the accident book has entries / is in use)",
  "riddor-report": "done = notYetKnownCount === 0 (no accident awaiting a RIDDOR decision)",
  "rp-log": "never used = no sessions ever · lapsed = none in the last 30 days · gaps = some trading days uncovered · done = every trading day covered (today allowed until the RP signs in)",
  "rp-absence": "done = the Pharmace SOP linked in the Working grid is held at the branch, in date and read & signed",
  "sop-signed": "done = status === 'ok', OR overdueCount === 0 (all assignments signed or not yet due)",
  "tos-hours": "done = hasOpeningHours === true (opening times recorded for the branch)",
  "wb-staffing": "done = hasPharmacist === true (≥1 active pharmacist on the books)",
  "sup-mhra": "done = outstanding === 0 (no logged alert without a recorded outcome)",
  // extra POST sources
  "near-miss": "Live near-miss records: recentIncidents > 0; earlier records remain visible as history",
  "adv-nms": "done = nms array present and non-empty (branch has NMS activity)",
  // task-by-name
  "tos-eps": "done = the core item count periodic task has a value recorded and in-date"
};
function sourceInfo(elementId, taskGrid) {
  // 1. Module fleet-status endpoint
  const src = LIVE_SOURCES[elementId];
  if (src) {
    return {
      kind: "Module endpoint",
      method: "GET",
      path: `/api/headoffice/${src.endpoint}`,
      params: "Auth: Bearer JWT. Scope derived from token (org or whole fleet). No query params — returns one row per active pharmacy.",
      field: "Matched to this branch by pharmacyName.",
      rule: RULE_TEXT[elementId] || "(see done() in LIVE_SOURCES)"
    };
  }
  // 2. Extra POST source (near-miss / NMS)
  const exKey = EXTRA_FOR[elementId];
  if (exKey) {
    const ex = EXTRA_SOURCES[exKey];
    return {
      kind: "Activity endpoint (routes.js)",
      method: ex.method || "POST",
      path: ex.path,
      params: `Auth: Bearer JWT (resolveScopeFromToken). Body: ${JSON.stringify(ex.body || {})}. Returns per-branch rows.`,
      field: "Matched to this branch by pharmacyName.",
      rule: RULE_TEXT[elementId] || "(see done() in EXTRA_SOURCES)"
    };
  }
  // 3. Periodic task def by _id
  const defId = TASK_DEF_FOR[elementId];
  if (defId) {
    const dt = taskGrid?.dataTypeById?.[defId];
    return {
      kind: "Periodic task (by _id)",
      method: "GET",
      path: "/api/headoffice/periodic-tasks/grid",
      params: `taskId (def _id): ${defId}${dt ? ` · dataType: ${dt}` : ""}. Read from the grid cell for this task × branch.`,
      field: "Grid row matched by pharmacyName; cell keyed by taskId.",
      rule: "done = cell state is in-date AND value affirmative (boolean true/'yes', non-empty date, numeric > 0, or non-'no' choice/text). overdue/never = not done; na = excluded."
    };
  }
  // 4. Periodic task by biKey (EPS → item count)
  const taskKey = TASK_DEF_BY_KEY[elementId];
  if (taskKey) {
    const tId = taskGrid?.idByKey?.[taskKey];
    return {
      kind: "Periodic task (core)",
      method: "GET",
      path: "/api/headoffice/periodic-tasks/grid",
      params: `Matched by biKey "${taskKey}"${tId ? ` → “${taskGrid?.nameById?.[tId] || ""}” (taskId ${tId})` : " (not found — the item count task is missing from the grid)"}.`,
      field: "Grid row matched by pharmacyName; cell keyed by resolved taskId.",
      rule: RULE_TEXT[elementId] || "done = task value recorded and in-date."
    };
  }
  // 5. Placeholder — no backend source yet.
  return {
    kind: "Placeholder (no live source)",
    method: "—",
    path: "(none)",
    params: "No API call. The value is generated deterministically from a hash of (group · item · branch · element) so the board renders a realistic mix.",
    field: "Not matched to any backend data.",
    rule: "done = hash(group|item|branch|element) < 0.7  →  ~70% appear done. NOT real compliance data.",
    placeholder: true
  };
}

// =============================================================================
// COMPLIANCE TAXONOMY (placeholder values; structure is real)
//
//   groups[] → items[] → elements[]
//
// Each element: { id, label, requirement, help, link? }
// Branches are applied uniformly (every item is assessed at every branch).
// =============================================================================
const GPHC_PRINCIPLES = "https://www.pharmacyregulation.org/pharmacies/standards-and-guidance-registered-pharmacies";
const GROUPS = [
// ---------------------------------------------------------------------------
{
  id: "governance",
  name: "Governance & Risk",
  subtitle: "GPhC Principle 1",
  items: [{
    id: "rp-record",
    name: "Responsible Pharmacist record",
    elements: [{
      id: "rp-displayed",
      label: "RP notice displayed",
      requirement: "mandatory",
      help: "A notice showing the name and registration number of the Responsible Pharmacist must be conspicuously displayed while the pharmacy is operating.",
      link: "https://www.pharmacyregulation.org/pharmacies/responsible-pharmacist"
    }, {
      id: "rp-log",
      label: "RP record kept (electronic/paper)",
      requirement: "mandatory",
      help: "A record identifying the RP in charge for each period must be kept and retained for at least 5 years."
    }, {
      id: "rp-absence",
      label: "Absence rules followed (max 2h)",
      requirement: "mandatory",
      help: "The RP may be absent for up to a maximum of two hours during the pharmacy's business hours, with arrangements in place."
    }]
  }, {
    id: "sops",
    name: "Standard Operating Procedures",
    // SOP reporting uses the same coloured %-bar format as the fridge
    // metrics (2026-07): % of SOPs reviewed/in date, and % of assignments
    // read & signed — both live figures from the SOP module.
    metrics: [{
      id: "sop-reviewed",
      label: "SOPs reviewed / in date",
      module: "sop",
      field: "pctReviewed",
      thresholdField: "reviewedThreshold",
      metField: "reviewedMet",
      help: "Of all live SOPs at this pharmacy, the % currently in date (review/expiry date not passed; SOPs without an expiry recorded count as in date). SOPs are commonly reviewed every 2 years or on process change."
    }, {
      id: "sop-read",
      label: "SOPs read & signed by staff",
      module: "sop",
      field: "pctRead",
      thresholdField: "readThreshold",
      metField: "readMet",
      help: "Of all live SOP assignments at this pharmacy, the % that staff have read and signed. Measures whether SOPs are actually being worked to, not just written."
    }],
    elements: [{
      id: "sop-dispensing",
      label: "Dispensing SOP covers transposition checks",
      requirement: "recommended",
      help: "Good-practice SOPs include an accuracy/clinical check step and near-miss capture."
    }]
  }, {
    id: "risk",
    name: "Risk management & incidents",
    elements: [{
      id: "near-miss",
      label: "Near-miss log maintained",
      requirement: "mandatory",
      help: "Errors and near misses should be recorded, reviewed and learned from to reduce recurrence.",
      link: GPHC_PRINCIPLES
    }, {
      id: "lfpse",
      label: "Patient safety incidents recorded (LFPSE)",
      requirement: "recommended",
      help: "Recording patient safety events via the national Learn from Patient Safety Events service supports learning and is increasingly expected."
    }, {
      id: "complaints",
      label: "Complaints procedure in place",
      requirement: "mandatory",
      help: "An accessible complaints procedure must be available, with complaints recorded and acted on."
    }, {
      id: "indemnity",
      label: "Professional indemnity in force",
      requirement: "mandatory",
      help: "Appropriate professional indemnity / insurance cover is a legal requirement for providing pharmacy services."
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "staff",
  name: "Staff & Training",
  subtitle: "GPhC Principle 2",
  items: [{
    id: "registration",
    name: "Professional registration",
    elements: [{
      id: "reg-pharmacist",
      label: "Pharmacist(s) GPhC registration current",
      requirement: "mandatory",
      help: "All pharmacists must hold current GPhC registration; revalidation must be up to date.",
      link: "https://www.pharmacyregulation.org/registration"
    }, {
      id: "reg-technician",
      label: "Technician(s) GPhC registration current",
      requirement: "mandatory",
      help: "Registered pharmacy technicians must also hold current GPhC registration and complete revalidation."
    }, {
      id: "reg-revalidation",
      label: "Revalidation records submitted",
      requirement: "mandatory",
      help: "Each pharmacist and technician submits their revalidation records every year. The GPhC only renews a registration once they are submitted, so this comes from the current registrations collected under the pharmacist and technician GPhC registration items — nothing extra to set up."
    }]
  }, {
    id: "training",
    name: "Role-based training",
    elements: [{
      id: "trn-counter",
      label: "Counter staff medicines-counter trained",
      requirement: "mandatory",
      help: "Counter and dispensary staff are on, or have completed, a GPhC-accredited course for their role (enrolled within 3 months). The training record is uploaded yearly."
    }, {
      id: "trn-safeguarding",
      label: "Safeguarding training (level appropriate)",
      requirement: "mandatory",
      help: "Safeguarding training at the right level for each role (usually Level 1 support staff, Level 2 registrants), refreshed at least every 3 years. The training record is uploaded yearly."
    }, {
      id: "trn-cpd",
      label: "Ongoing CPD evidenced",
      requirement: "recommended",
      help: "The whole team keeps learning, and it is recorded. The team’s training / CPD log is uploaded yearly."
    }]
  }, {
    id: "wellbeing",
    name: "Team & whistleblowing",
    elements: [{
      id: "wb-speakup",
      label: "Staff can raise concerns (speak-up route)",
      requirement: "mandatory",
      help: "Staff, including locums, must be able to raise concerns and have them taken seriously and acted on (GPhC standards 2.4 and 2.5). In place when all four parts are: a current speak-up / whistleblowing policy; a named Freedom to Speak Up Guardian (NHS pharmacies in England), with the speak-up poster naming them up in the staff room; staff and locums briefed; and concerns followed up and recorded confidentially.",
      link: "https://cpe.org.uk/quality-and-regulations/clinical-governance/raising-concerns-whistle-blowing/"
    }]
  }, {
    id: "staffing",
    name: "Adequate staffing",
    elements: [{
      id: "wb-staffing",
      label: "Adequate staffing for safe service",
      requirement: "mandatory",
      help: "There must be enough suitably trained staff to provide services safely and effectively."
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "premises",
  name: "Premises",
  subtitle: "GPhC Principle 3",
  items: [{
    id: "environment",
    name: "Environment & cleanliness",
    metrics: [{
      id: "cleaning-ontime",
      label: "Cleaning done on time",
      module: "cleaning",
      field: "pctOnTimeYear",
      thresholdField: "onTimeThreshold",
      metField: "onTimeMet",
      help: "Of all scheduled cleans across all areas over the last 12 months, the % completed on time. Measures whether the cleaning schedule is actually being kept."
    }],
    elements: [{
      id: "env-clean",
      label: "Cleaning log up to date",
      requirement: "mandatory",
      help: "The premises must be clean, safe and suitable; a cleaning record evidences ongoing upkeep."
    }, {
      id: "env-consultation",
      label: "Consultation room available & private",
      requirement: "mandatory",
      help: "A consultation room is required for many NHS services and to allow private conversations."
    }, {
      id: "env-temperature",
      label: "Premises temperature suitable",
      requirement: "recommended",
      help: "Medicines must be stored as the manufacturer says — most not above 25°C — and staff need a reasonable working temperature (normally at least 16°C). In place when both parts are: a min/max thermometer where medicines are kept (photo yearly), and the temperatures recorded each working day and kept in range (photo of the record monthly)."
    }]
  }, {
    id: "security",
    name: "Security & access",
    elements: [{
      id: "sec-cd-cabinet",
      label: "CD cabinet compliant & secure",
      requirement: "mandatory",
      help: "Schedule 2 and most Schedule 3 CDs must be kept in a locked cabinet meeting the Misuse of Drugs (Safe Custody) Regulations 1973 (or covered by a police certificate). In place when all three parts are: the cabinet itself (photo yearly), the CD keys held by the pharmacist and logged (photo of the key log quarterly), and out-of-date / returned CDs kept separate (photo quarterly).",
      link: "https://www.legislation.gov.uk/uksi/1973/798/made"
    }, {
      id: "sec-pom-access",
      label: "POM areas access-controlled",
      requirement: "mandatory",
      help: "Prescription-only and pharmacy medicines must be safeguarded from unauthorised access (GPhC 3.4 and 4.3). In place when both parts are: a yearly photo of the arrangements keeping the dispensary and POMs from the public, and a current access / security SOP."
    }, {
      id: "sec-pmedicine",
      label: "P-medicine display risk-assessed",
      requirement: "recommended",
      help: "P medicines may only be sold under a pharmacist’s supervision; the GPhC says self-selection conflicts with its standards unless safeguards are in place. In place when both parts are: a yearly photo of where P medicines are kept (behind the counter, or a supervised facilitated self-selection area), and a current P-medicine sales SOP plus a risk assessment if any are on display.",
      link: "https://www.pharmacyregulation.org/about-us/news-and-updates/statement-self-selection-pharmacy-medicines"
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "medicines",
  name: "Medicines & Services",
  subtitle: "GPhC Principle 4",
  items: [{
    id: "cd",
    name: "Controlled drugs",
    elements: [{
      id: "cd-register",
      label: "CD register balances reconciled",
      requirement: "mandatory",
      help: "A running balance is kept for every Schedule 2 CD and checked against the stock — weekly unless the site sets its own interval — with no discrepancy left open. From the Pharmace CD register where the branch uses it (a register never balance-checked counts as not in place); otherwise a photo of the latest balance checks every three months, or a note of the system that holds the register.",
      link: "https://www.cqc.org.uk/guidance-providers/controlled-drugs"
    }, {
      id: "cd-destruction",
      label: "CD destruction recorded (denatured)",
      requirement: "mandatory",
      help: "Every CD destruction is recorded with who destroyed it and a witness, and the drug denatured; out-of-date Schedule 2 stock only in front of an authorised witness. From the Pharmace CD register where the branch uses it; otherwise a photo or upload of the destruction records every three months, or a note of the system that holds them."
    }, {
      id: "cd-sop",
      label: "CD SOPs current",
      requirement: "mandatory",
      help: "Current SOPs for the whole CD process — receipt, safe custody, register and balance checks, supply and ID checks, returns, destruction, reporting — read and signed by staff. From Pharmace’s SOP module, or a signed copy uploaded yearly."
    }, {
      id: "cd-officer",
      label: "CD Accountable Officer notified of incidents",
      requirement: "mandatory",
      help: "In two parts: staff know who the area’s CD Accountable Officer is and how to report (a yearly photo of the contact details), and no CD discrepancy is left open over 7 days (from the Pharmace CD register, or a 3-monthly photo, upload or note elsewhere)."
    }]
  }, {
    id: "cold-chain",
    name: "Cold chain & storage",
    // Effectiveness metrics (12-month process measures), shown as coloured
    // % bars. These answer "is the process working over the year?", not
    // "was it OK yesterday". Fridge has TWO (frequency + in range); date-
    // checking has ONE consistency metric (its summary stores only activity
    // counts, no expected/scheduled denominator, so a true on-time % can't
    // be derived — we measure how consistently it's done instead).
    metrics: [{
      id: "fridge-checked",
      label: "Fridge checked on time",
      module: "fridge",
      field: "pctOnTimeYear",
      thresholdField: "onTimeThreshold",
      metField: "onTimeMet",
      help: "Of the days the pharmacy was open over the last 12 months, the % on which the fridge temperature was recorded. Measures whether the checking process is actually happening."
    }, {
      id: "fridge-inrange",
      label: "Fridge temperature in range",
      module: "fridge",
      field: "pctInRangeYear",
      thresholdField: "inRangeThreshold",
      metField: "inRangeMet",
      help: "Of all temperature readings over the last 12 months, the % that were within 2–8°C. Measures whether cold-chain integrity is being maintained."
    }, {
      id: "date-check-active",
      label: "Date-checking done consistently",
      module: "date-checking",
      field: "pctOnTimeYear",
      thresholdField: "onTimeThreshold",
      metField: "onTimeMet",
      help: "The % of the last 12 months in which date-checking was actually carried out. The date-checking record stores activity, not a schedule, so this measures consistency (is it done every month?) rather than per-shelf timeliness."
    }],
    elements: [
    // The two fridge measures and date-checking are 12-month metrics above.
    // This element is whether fridge temperatures are being recorded now:
    // Pharmace fridge monitoring, or the branch's own log uploaded and
    // analysed (which then also feeds the two fridge bars).
    {
      id: "fridge-log",
      label: "Fridge temperatures recorded",
      requirement: "mandatory",
      help: "Each medicines fridge is checked every day the pharmacy is open and the current, minimum and maximum temperatures recorded; readings outside 2–8 °C are acted on. From Pharmace fridge monitoring, or the branch’s own fridge log or data logger export, uploaded and analysed."
    }]
  }, {
    id: "supply",
    name: "Safe & effective supply",
    // MHRA alerts cascaded to staff as staff-app tasks: the % read, in the
    // same %-bar format as SOPs read & signed. Optional: a branch that has
    // never cascaded an alert isn't marked down for it.
    metrics: [{
      id: "mhra-read",
      label: "MHRA alerts read by staff",
      module: "mhra",
      field: "pctRead",
      thresholdField: "readThreshold",
      metField: "readMet",
      optional: true,
      help: "Where an MHRA alert has been cascaded to staff as a task in the staff app, the % of those staff who have read it (completed the task). Each person counts once per alert. Alerts that weren’t cascaded don’t count, and a branch that has never cascaded one isn’t marked down."
    }],
    elements: [{
      id: "sup-mhra",
      label: "MHRA drug/safety alerts actioned",
      requirement: "mandatory",
      help: "Drug alerts and recalls must be acted on promptly and the action recorded."
    }, {
      id: "sup-frp",
      label: "Pharmacy First clinical pathways followed",
      requirement: "recommended",
      help: "Where the pharmacy provides Pharmacy First: the manager confirms each year that consultations follow the national clinical pathways and PGDs, and are recorded. Not applicable if not signed up."
    }, {
      id: "sup-gp-notify",
      label: "GP notified of relevant supplies",
      requirement: "recommended",
      help: "Sharing supply information with the patient's GP supports continuity of care."
    }, {
      id: "sup-distance",
      label: "Distance-selling safeguards (if applicable)",
      requirement: "optional",
      help: "Distance-selling pharmacies: the manager confirms each year that online / distance services meet the GPhC guidance, including the safeguards for higher-risk medicines. Not applicable to other branches.",
      link: "https://assets.pharmacyregulation.org/files/2025-02/gphc-guidance-registered-pharmacies-providing-pharmacy-services-distance-february-2025.pdf"
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "equipment",
  name: "Equipment & Facilities",
  subtitle: "GPhC Principle 5",
  items: [{
    id: "calibration",
    name: "Calibration & maintenance",
    elements: [{
      id: "cal-thermometer",
      label: "Fridge thermometer calibrated",
      requirement: "recommended",
      help: "Each fridge thermometer or data logger is checked for accuracy at least yearly — against a calibrated reference, or by a calibration certificate. A yearly photo or certificate from a named person, or a note of where the certificates are held."
    }, {
      id: "cal-scales",
      label: "Measuring/weighing equipment fit for purpose",
      requirement: "recommended",
      help: "Scales approved for use and checked yearly with test weights (or calibrated by the supplier); stamped liquid measures; all kept clean and in good repair. A yearly photo or certificate from a named person, or a note of where the records are held."
    }, {
      id: "cal-cmp",
      label: "Compliance-aid sealing equipment maintained",
      requirement: "optional",
      help: "Where compliance aids (blister packs / MDS) are made, the sealer is cleaned, checked and serviced as the manufacturer says. A yearly photo or service certificate from a named person, or a note of where the records are held; not applicable where no compliance aids are made."
    }]
  }, {
    id: "it-systems",
    name: "Clinical IT & continuity",
    elements: [{
      id: "it-pmr-secure",
      label: "PMR access controlled (smartcards)",
      requirement: "mandatory",
      help: "In two parts: everyone has their own PMR login and smartcard, never shared, and leavers are removed (a yearly confirmation by the manager); and a current IT access / information governance SOP."
    }, {
      id: "it-backup",
      label: "Business continuity plan in place",
      requirement: "mandatory",
      help: "A current business continuity plan for the premises — loss of systems, power, premises or staff — reviewed yearly. From Pharmace’s SOP module, or uploaded by the branch.",
      link: "https://www.england.nhs.uk/"
    }, {
      id: "it-encryption",
      label: "Devices encrypted",
      requirement: "recommended",
      help: "Every laptop, tablet, phone and USB stick holding patient data is encrypted and PIN-protected. A yearly confirmation by the manager, or a note of where the IT provider’s record is held."
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "ig",
  name: "Information Governance",
  subtitle: "DSPT / UK GDPR",
  items: [{
    id: "dspt",
    name: "Data Security & Protection Toolkit",
    elements: [{
      id: "dspt-published",
      label: "DSPT published & up to standard",
      requirement: "mandatory",
      help: "The DSPT published each year by 30 June with ‘Standards Met’. The confirmation is uploaded and lasts until the next 30 June; or a note if head office submits for the group.",
      link: "https://www.dsptoolkit.nhs.uk/"
    }, {
      id: "dspt-training",
      label: "Annual data-security training completed",
      requirement: "mandatory",
      help: "Every member of staff handling patient data completes data security awareness training each year. The training record is uploaded yearly."
    }, {
      id: "dspt-iao",
      label: "Information asset owner assigned",
      requirement: "recommended",
      help: "A named information governance lead / information asset owner at each branch, who accepts the role in the staff app each year."
    }]
  }, {
    id: "gdpr",
    name: "UK GDPR / data protection",
    elements: [{
      id: "gdpr-ico",
      label: "ICO registration (data protection fee) paid",
      requirement: "mandatory",
      help: "Registered with the ICO and the annual data protection fee paid. The certificate is uploaded and lasts until it expires; or a note of the company’s registration number.",
      link: "https://ico.org.uk/"
    }, {
      id: "gdpr-privacy",
      label: "Privacy notice available to patients",
      requirement: "mandatory",
      help: "In two parts: the privacy notice on display for patients (a yearly photo), and the current full notice (uploaded, or linked on the website)."
    }, {
      id: "gdpr-sar",
      label: "Subject access request process defined",
      requirement: "recommended",
      help: "A procedure for subject access requests — recognised, identity checked, answered within one month. From Pharmace’s SOP module, or uploaded yearly."
    }, {
      id: "gdpr-breach",
      label: "Breach reporting route understood",
      requirement: "mandatory",
      help: "A data breach procedure — staff report at once; reportable breaches notified within 72 hours (DSPT incident tool / ICO). From Pharmace’s SOP module, or uploaded yearly."
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "hs",
  name: "Health & Safety",
  subtitle: "Statutory duties",
  items: [{
    id: "hs-core",
    name: "Workplace health & safety",
    elements: [{
      id: "hs-poster",
      label: "H&S law poster displayed / leaflet issued",
      requirement: "mandatory",
      help: "The HSE ‘Health and safety law’ poster displayed for staff, or the equivalent leaflet given to each. A yearly photo task to a named person.",
      link: "https://www.hse.gov.uk/"
    }, {
      id: "hs-riskassess",
      label: "Risk assessments current",
      requirement: "mandatory",
      help: "Current written risk assessments for the pharmacy’s significant hazards (plus a fire risk assessment), reviewed yearly. Uploaded by the branch, or a note of where they are held."
    }, {
      id: "hs-firstaid",
      label: "First aid provision adequate",
      requirement: "mandatory",
      help: "A stocked, in-date first aid kit and an appointed person / first aider, with staff told who it is. A yearly photo task to a named person."
    }, {
      id: "hs-elinsurance",
      label: "Employers' liability insurance displayed",
      requirement: "mandatory",
      help: "Employers’ liability insurance (at least £5m) with the current certificate available to staff. The certificate is uploaded or photographed, and lasts until the policy expires; the renewal request goes out 30 days before."
    }]
  }, {
    id: "coshh",
    name: "COSHH & waste",
    elements: [{
      id: "coshh-assess",
      label: "COSHH assessments for hazardous substances",
      requirement: "mandatory",
      help: "A COSHH assessment for every hazardous substance used, reviewed yearly. Uploaded by the branch, or a note of where they are held."
    }, {
      id: "waste-segregation",
      label: "Pharmaceutical/clinical waste segregated",
      requirement: "mandatory",
      help: "Medicines, cytotoxic, sharps and CD waste kept in separate labelled containers. A yearly photo task to a named person."
    }, {
      id: "waste-contract",
      label: "Licensed waste carrier contract in place",
      requirement: "mandatory",
      help: "A contract with a registered waste carrier, with the consignment / transfer notes kept. Uploaded yearly, or a note of a group contract."
    }]
  }, {
    id: "riddor",
    name: "Accidents & RIDDOR",
    elements: [{
      id: "riddor-book",
      label: "Accident book maintained",
      requirement: "mandatory",
      help: "Workplace accidents and injuries must be recorded."
    }, {
      id: "riddor-report",
      label: "RIDDOR-reportable events reported",
      requirement: "mandatory",
      help: "Certain injuries, diseases and dangerous occurrences must be reported to the HSE under RIDDOR.",
      link: "https://www.hse.gov.uk/riddor/"
    }]
  }]
},
// ---------------------------------------------------------------------------
{
  id: "nhs",
  name: "NHS Terms of Service",
  subtitle: "CPCF / CPAF",
  items: [{
    id: "essential",
    name: "Essential services & ToS",
    elements: [{
      id: "tos-hours",
      label: "Core & supplementary hours met",
      requirement: "mandatory",
      help: "Contractual opening hours must be maintained; unplanned closures must be managed and reported."
    }, {
      id: "tos-eps",
      label: "EPS operational",
      requirement: "mandatory",
      help: "Electronic Prescription Service must be available as part of essential services."
    }, {
      id: "tos-signposting",
      label: "Signposting & support for self-care",
      requirement: "mandatory",
      help: "In two parts: the manager confirms each year that self-care advice and signposting are given, and the signposting SOP (Pharmace SOP link or upload)."
    }, {
      id: "tos-disposal",
      label: "Unwanted medicines disposal offered",
      requirement: "mandatory",
      help: "In two parts: the manager confirms each year that unwanted medicines are accepted and handled safely, and the disposal SOP (Pharmace SOP link or upload)."
    }]
  }, {
    id: "cpaf",
    name: "CPAF assurance",
    elements: [{
      id: "cpaf-questionnaire",
      label: "Annual CPAF questionnaire completed",
      requirement: "mandatory",
      help: "The manager confirms each year that the CPAF questionnaire has been completed whenever NHS England asked for it.",
      link: "https://www.nhsbsa.nhs.uk/"
    }, {
      id: "cpaf-pages",
      label: "NHS website profile (DoS) accurate",
      requirement: "recommended",
      help: "The manager confirms each year that the pharmacy’s NHS website profile and Directory of Services entry — opening hours, services — have been checked and are correct."
    }, {
      id: "cpaf-gateway",
      label: "NHS gateway criteria evidenced",
      requirement: "recommended",
      help: "The manager confirms each year that evidence for the CPAF / quality criteria NHS England may sample is kept and can be produced."
    }]
  }, {
    id: "pharmacy-first",
    name: "Advanced services governance",
    elements: [{
      id: "adv-pf",
      label: "Pharmacy First requirements met",
      requirement: "recommended",
      help: "Where the pharmacy provides Pharmacy First: the manager confirms each year that the service requirements are met (consultation room, otoscope, signed PGDs and protocols, trained pharmacists). Not applicable if not signed up."
    }, {
      id: "adv-nms",
      label: "NMS consent & records compliant",
      requirement: "recommended",
      help: "New Medicine Service requires valid consent and complete records."
    }, {
      id: "adv-dms",
      label: "Discharge Medicines Service actioned",
      requirement: "optional",
      help: "Where the pharmacy receives Discharge Medicines Service referrals: the manager confirms each year that referrals are checked every working day and actioned as the service specification says. Not applicable if none are received."
    }]
  }]
}];

// =============================================================================
// Roll-up maths. Everything derives from data elements so the tree is honest.
// =============================================================================

// % of an item's elements that are "done" at a given branch.
function itemBranchPct(group, item, branch, liveData, taskGrid, extraData, effData, attData) {
  if (!branchLocation(branch, extraData)?.nation) return null;
  const els = (item.elements || []).filter(el => applicableAt(el, branch, extraData) && extraData?.__grid?.raw?.[`${el.id}|${branch}`]?.source !== "na");
  // An optional measure only counts once the branch has data for it.
  const metrics = (item.metrics || []).filter(m => !m.optional || effData?.[m.module]?.[branch]?.everUsed);
  const total = els.length + metrics.length;
  if (total === 0) return null;
  let done = 0;
  for (const el of els) {
    if (resolveDone(group.id, item.id, branch, el.id, liveData, taskGrid, extraData, attData).done) done++;
  }
  // Each effectiveness metric counts as one pass/fail signal: pass = it meets
  // its threshold over the 12 months (backend-computed onTimeMet / inRangeMet).
  for (const m of metrics) {
    const row = effData?.[m.module]?.[branch];
    if (row && row[m.metField]) done++;
  }
  return done / total * 100;
}

// Item % across the whole fleet = mean of its per-branch %s.
function itemPct(group, item, branches, liveData, taskGrid, extraData, effData, attData) {
  if (branches.length === 0) return null;
  const vals = branches.map(b => itemBranchPct(group, item, b, liveData, taskGrid, extraData, effData, attData)).filter(v => v != null);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// Group (tab) % = mean of its item %s.
function groupPct(group, branches, liveData, taskGrid, extraData, effData, attData) {
  const vals = (group.items || []).map(it => itemPct(group, it, branches, liveData, taskGrid, extraData, effData, attData)).filter(v => v != null);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// =============================================================================
// Component
// =============================================================================
// -----------------------------------------------------------------------------
// Shared data fetch — the SAME loader feeds the main compliance board and the
// printable single-pharmacy report (CompliancePrintReport), so the two can
// never drift apart. Returns { liveData, extraData, effData, taskGrid,
// attData, liveBranches }; optional sources come back null on failure.
// -----------------------------------------------------------------------------
async function fetchAllComplianceData(token) {
  // A single broken module must never blank the whole Regulatory Compliance
  // board. Load each live source independently and keep the successful ones.
  // The organisation members list is the branch source-of-truth; these feeds
  // only provide evidence/status for those branches.
  const liveFailures = [];
  const results = await Promise.all(LIVE_ENDPOINTS.map(async ep => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(ep)}`, {
        headers: token ? {
          Authorization: `Bearer ${token}`
        } : {}
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const rows = Array.isArray(json) ? json : json.rows || json.results || [];
      return [ep, rows];
    } catch (e) {
      liveFailures.push(`${ep}: ${e.message || e}`);
      return [ep, []];
    }
  }));
  const byEndpoint = {};
  const nameSet = new Set();
  for (const [ep, rows] of results) {
    const byName = {};
    for (const r of rows) {
      if (r && r.pharmacyName) {
        byName[r.pharmacyName] = r;
        nameSet.add(r.pharmacyName);
      }
    }
    byEndpoint[ep] = byName;
  }

  // Extra POST sources (root-base routes.js endpoints: near-miss, NMS).
  const extra = {};
  const extraKeys = Object.keys(EXTRA_SOURCES);
  if (extraKeys.length) {
    await Promise.all(extraKeys.map(async key => {
      const cfg = EXTRA_SOURCES[key];
      try {
        const res = await fetch(`${API_BASE}${cfg.path}`, {
          method: cfg.method || "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? {
              Authorization: `Bearer ${token}`
            } : {})
          },
          body: JSON.stringify(withOrgBody(cfg.body || {}))
        });
        if (!res.ok) return;
        const json = await res.json();
        const byName = {};
        for (const r of cfg.rowsOf(json) || []) {
          const nm = cfg.nameOf(r);
          if (nm) {
            byName[nm] = r;
            nameSet.add(nm);
          }
        }
        extra[key] = byName;
      } catch {/* this source optional */}
    }));
  }

  // 12-month effectiveness metrics (fridge, cleaning).  Organisation
  // members store the canonical tenant dbName in members[].pharmacyId, while
  // these endpoints historically returned the pharmacy's common-db display
  // name.  Build an alias map so a branch called "Queenborough" in the org
  // can consume a row whose common-db name is "Tyrell & Jones (Queenborough)".
  const memberNameByDbName = new Map();
  try {
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    for (const m of Array.isArray(members) ? members : []) {
      const dbName = m && m.pharmacyId != null ? String(m.pharmacyId) : "";
      const display = m && (m.pharmacyName || m.name) || "";
      if (dbName && display) memberNameByDbName.set(dbName, display);
    }
  } catch {/* no local membership cache yet */}
  const eff = {};
  await Promise.all(EFFECTIVENESS_MODULES.map(async mod => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/effectiveness?module=${mod}&months=12`)}`, {
        headers: token ? {
          Authorization: `Bearer ${token}`
        } : {}
      });
      if (!res.ok) return;
      const json = await res.json();
      const rows = Array.isArray(json) ? json : json.rows || [];
      const byName = {};
      for (const r of rows) {
        if (!r) continue;
        if (r.pharmacyName) {
          byName[r.pharmacyName] = r;
          nameSet.add(r.pharmacyName);
        }
        // Canonical identity join: backend returns dbName for effectiveness
        // rows.  Index the same row under the organisation member's chosen
        // display name as well, so presentation names never break the data join.
        const alias = r.dbName != null ? memberNameByDbName.get(String(r.dbName)) : null;
        if (alias) {
          byName[alias] = r;
          nameSet.add(alias);
        }
      }
      eff[mod] = byName;
    } catch {/* optional */}
  }));

  // Periodic-task grid (only if any element is task-backed).
  let taskGrid = null;
  if (ANY_TASK_BACKED) {
    try {
      const g = await fetchTaskGrid(token);
      if (g) {
        for (const n of Object.keys(g.rowsByName)) nameSet.add(n);
        taskGrid = g;
      }
    } catch {/* grid optional */}
  }

  // Usage levels — one call per branch per module (needs the branch's
  // pharmacyId, which the periodic-task grid carries).
  const usageIds = Object.keys(USAGE_SOURCES);
  if (usageIds.length && taskGrid && taskGrid.rowsByName) {
    const branchRows = Object.entries(taskGrid.rowsByName).filter(([, r]) => r && r.pharmacyId);
    await Promise.all(usageIds.map(async elementId => {
      const cfg = USAGE_SOURCES[elementId];
      const byName = {};
      await Promise.all(branchRows.map(async ([name, r]) => {
        try {
          const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(cfg.path(r.pharmacyId))}`, {
            headers: token ? {
              Authorization: `Bearer ${token}`
            } : {}
          });
          if (!res.ok) return;
          const u = cfg.classify(await res.json());
          if (u) byName[name] = u;
        } catch {/* this branch falls back to the plain live rule */}
      }));
      if (Object.keys(byName).length) extra[USAGE_KEY(elementId)] = byName;
    }));
  }

  // Uploaded RP records (Working grid → "Email request to upload data").
  // Where Pharmace has no RP sign-in data for a branch, or the branch is set to
  // upload, the analysed spreadsheet stands in for it — same figures, same panel.
  try {
    const gres = await fetch(`${API_BASE}/api/headoffice/compliance-grid/load`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? {
          Authorization: `Bearer ${token}`
        } : {})
      },
      body: "{}"
    });
    if (gres.ok) {
      const g = await gres.json();
      const nameOf = new Map((g.branches || []).map(b => [b.dbName, b.pharmacyName]));
      // Inspector notes (item-wide and per branch) and evidence from Pharmace
      // SOP links / document uploads set up in the Working grid.
      // raw: every Working grid cell by "element|branch" (the data source and
      // what it holds); rowCfg: each item's saved settings; dbByName: branch →
      // tenant database, for the paged record lists.
      const grid = {
        notes: {},
        cellNotes: {},
        cells: {},
        raw: {},
        rowCfg: {},
        dbByName: {},
        jurisdictionByName: Object.fromEntries((g.branches || []).map(b => [b.pharmacyName, locationOf(b, g)])),
        criteriaVersion: g.criteriaVersion
      };
      for (const b of g.branches || []) grid.dbByName[b.pharmacyName] = b.dbName;
      // Rows added in the Working grid ("custom:…") are keyed by their full row
      // id, so a cell's data view finds their source and evidence too.
      const keyOf = rowId => rowId.startsWith("el:") ? rowId.slice(3) : rowId;
      for (const r of g.rows || []) grid.rowCfg[keyOf(r.rowId)] = r;
      for (const r of g.rows || []) if (r.inspectorNote) grid.notes[keyOf(r.rowId)] = r.inspectorNote;
      for (const c of g.cells || []) {
        const el = keyOf(c.rowId),
          name = nameOf.get(c.dbName);
        if (!name) continue;
        grid.raw[`${el}|${name}`] = c;
        if (c.inspectorNote) grid.cellNotes[`${el}|${name}`] = c.inspectorNote;
        if (el === "rp-log") continue;
        if (c.sopShared) {
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: 'sop-management',
            note: c.sopStatement || c.scopeStatement || c.inspectorNote || c.status
          };
          continue;
        }
        // A task to every pharmacist (GPhC registration): one line per person.
        if (c.source === "team" && c.team) {
          const t = c.team;
          grid.cells[`${el}|${name}`] = {
            status: c.status === "due-soon" ? "current" : c.status,
            kind: "team",
            files: (t.people || []).filter(p => p.evidence?.fileUrl).map(p => ({
              filename: `${p.name} — registration`,
              fileUrl: p.evidence.fileUrl
            })),
            note: !t.total ? "Nobody with the chosen role at this branch" : `${t.confirmed} of ${t.total} confirmed: ${(t.people || []).map(p => `${p.name}${p.evidence?.registrationNumber ? ` (registration ${p.evidence.registrationNumber}` : " ("}${p.expiresAt ? `${p.evidence?.registrationNumber ? ", " : ""}expires ${ukDate(p.expiresAt)})` : p.evidence?.registrationNumber ? ")" : "no copy yet)"}`).join("; ")}`
          };
          continue;
        }
        // DSP criteria evidenced by a staff-app task.
        // Rows added in the Working grid are evidenced the same way.
        if ((el.startsWith("ni-") || el.startsWith("dsp-") || el.startsWith("custom:") || GRID_TASK_ONLY.has(el)) && c.source === "task") {
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: "task",
            files: c.evidence?.files?.length ? c.evidence.files : c.evidence?.fileUrl ? [{
              filename: c.evidence.filename || "Evidence",
              fileUrl: c.evidence.fileUrl
            }] : [],
            note: c.evidence?.at ? `Confirmed ${ukDate(c.evidence.at)}${c.evidence.by ? ` by ${c.evidence.by}` : ""}${c.expiresAt ? ` · until ${ukDate(c.expiresAt)}` : ""}` : c.task?.status === "open" ? `Task sent ${ukDate(c.task.sentAt)} to ${c.task.recipientName || "the branch"}` : !c.recipientStaffId ? "No recipient chosen yet — choose who receives the task in the Working grid" : "Not evidenced yet"
          };
          continue;
        }
        // Near-miss log uploaded / linked from a Google Sheet: the analysed
        // figures decide the status (recorded every month / gaps / lapsed).
        const nma = c.evidence?.analysis;
        // Cleaning log uploaded by a branch that doesn't use Pharmace cleaning:
        // the same figures — on-time %, missed cleans, overdue areas — feed the
        // item AND the "Cleaning done on time" 12-month bar.
        if (c.source === "upload" && nma?.kind === "cleaning") {
          const expired = c.status === "expired";
          const usage = expired ? "lapsed" : nma.status === "ok" ? "ok" : nma.status === "lapsed" ? "lapsed" : "gaps";
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: "cleaning",
            usage,
            nm: {
              ...nma,
              reports: c.evidence.reports || []
            },
            files: c.evidence.reports || [],
            note: `From the cleaning log uploaded ${ukDate(nma.latestUpload?.at || c.evidence.at)}${c.sheetLink?.url ? " (shared link, re-read monthly)" : ""}: ${nma.pctOnTimeYear ?? "—"}% of cleans done on time over 12 months · ${nma.missedCleans} missed · ${nma.overdueAreas} area${nma.overdueAreas === 1 ? "" : "s"} overdue`
          };
          if (el === "env-clean") {
            eff.cleaning = eff.cleaning || {};
            const thr = eff.cleaning[name]?.onTimeThreshold ?? nma.onTimeThreshold ?? 90;
            eff.cleaning[name] = {
              ...(eff.cleaning[name] || {}),
              everUsed: true,
              usedRecently: (nma.last30 || 0) > 0,
              pctOnTimeYear: nma.pctOnTimeYear,
              onTimeThreshold: thr,
              onTimeMet: nma.pctOnTimeYear != null && nma.pctOnTimeYear >= thr,
              monthsWithData: (nma.months || []).filter(m => m.cleans > 0).length,
              fromUpload: true
            };
          }
          continue;
        }
        // Fridge temperature log uploaded by a branch that doesn't use Pharmace
        // fridge monitoring: feeds the item AND both 12-month fridge bars.
        if (c.source === "upload" && nma?.kind === "fridge") {
          const expired = c.status === "expired";
          const usage = expired ? "lapsed" : nma.status === "ok" ? "ok" : nma.status === "lapsed" ? "lapsed" : "gaps";
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: "fridge",
            usage,
            nm: {
              ...nma,
              reports: c.evidence.reports || []
            },
            files: c.evidence.reports || [],
            note: `From the fridge log uploaded ${ukDate(nma.latestUpload?.at || c.evidence.at)}${c.sheetLink?.url ? " (shared link, re-read monthly)" : ""}: readings on ${nma.pctOnTimeYear ?? "—"}% of open days · ${nma.pctInRangeYear ?? "—"}% within 2–8 °C${expired ? " — upload now out of date" : ""}`
          };
          if (el === "fridge-log") {
            eff.fridge = eff.fridge || {};
            const prev = eff.fridge[name] || {};
            const onThr = prev.onTimeThreshold ?? nma.onTimeThreshold ?? 90,
              rgThr = prev.inRangeThreshold ?? nma.inRangeThreshold ?? 95;
            eff.fridge[name] = {
              ...prev,
              everUsed: true,
              usedRecently: (nma.last30 || 0) > 0,
              pctOnTimeYear: nma.pctOnTimeYear,
              pctInRangeYear: nma.pctInRangeYear,
              onTimeThreshold: onThr,
              inRangeThreshold: rgThr,
              onTimeMet: nma.pctOnTimeYear != null && nma.pctOnTimeYear >= onThr,
              inRangeMet: nma.pctInRangeYear != null && nma.pctInRangeYear >= rgThr,
              monthsWithData: (nma.months || []).filter(m => m.readings > 0).length,
              fromUpload: true,
              upload: {
                months: nma.months || [],
                fridges: (nma.fridges || []).map(f => f.fridge),
                records: nma.records || [],
                recordsTotal: nma.recordsTotal
              }
            };
          }
          continue;
        }
        if (c.source === "upload" && (nma?.kind === "near-miss" || nma?.kind === "lfpse")) {
          const lf = nma.kind === "lfpse";
          const expired = c.status === "expired";
          const usage = expired ? "lapsed" : nma.status === "ok" ? "ok" : nma.status === "lapsed" ? "lapsed" : "gaps";
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: nma.kind,
            usage,
            nm: {
              ...nma,
              reports: c.evidence.reports || []
            },
            files: c.evidence.reports || [],
            note: `From the ${lf ? "incident" : "near-miss"} log uploaded ${ukDate(nma.latestUpload?.at || c.evidence.at)}${c.sheetLink?.url ? " (shared link, re-read monthly)" : ""}: ${nma.total} ${lf ? "incidents" : "entries"}, ${nma.last30} in the last 30 days${lf ? ` · ${nma.lfpse?.pct ?? 0}% referred to LFPSE${nma.lfpse?.notReferredSeriousCount ? ` · ${nma.lfpse.notReferredSeriousCount} moderate+ harm not referred` : ""}` : nma.emptyMonths?.length ? ` · ${nma.emptyMonths.length} month${nma.emptyMonths.length === 1 ? "" : "s"} with none` : ""}${expired ? " — upload now out of date" : ""}`
          };
          continue;
        }
        // Held in another system: a link, or just a note of where (e.g.
        // "Information held by CD-RX system").
        if (c.source === "link" && (c.linkUrl || c.linkLabel)) {
          grid.cells[`${el}|${name}`] = {
            status: c.status === "missing" ? "linked" : c.status,
            kind: "link",
            files: c.linkUrl ? [{
              filename: c.linkLabel || "Linked source",
              fileUrl: c.linkUrl
            }] : [],
            note: `Evidence kept elsewhere — ${c.linkLabel || c.linkUrl}`
          };
          continue;
        }
        if (c.source === "na") {
          const reason = (g.rows || []).find(r => r.rowId === c.rowId)?.naReason;
          grid.cells[`${el}|${name}`] = {
            status: "na",
            kind: "na",
            note: `Not applicable at this branch${reason ? `: ${reason}` : ""}`
          };
          continue;
        }
        if (c.source === "sop" && c.sop) {
          const m = c.sop.matched || [];
          const read = m.reduce((t, x) => t + (x.signed || 0), 0),
            of = m.reduce((t, x) => t + (x.assigned || 0), 0);
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: "sop",
            note: c.status === "current" ? `Pharmace SOP${m.length > 1 ? "s" : ""} in place: ${m.map(x => `“${x.title}”${x.version ? ` v${x.version}` : ""}`).join(", ")}${of ? ` · ${read}/${of} staff read & signed` : ""}` : m.some(x => !x.held) ? `SOP not held at this branch: ${m.filter(x => !x.held).map(x => `“${x.title}”`).join(", ")}` : `SOP past its review date: ${m.filter(x => !x.inDate).map(x => `“${x.title}”`).join(", ")}`
          };
        } else if (c.source === "upload" && (c.evidence?.files?.length || c.uploads?.length || c.task)) {
          const files = c.evidence?.files?.length ? c.evidence.files : c.evidence?.fileUrl ? [{
            filename: c.evidence.filename,
            fileUrl: c.evidence.fileUrl
          }] : [];
          grid.cells[`${el}|${name}`] = {
            status: c.status,
            kind: "upload",
            files,
            note: c.evidence?.at ? `Uploaded ${ukDate(c.evidence.at)}${c.evidence.by ? ` by ${c.evidence.by}` : ""}: ${files.length} file${files.length === 1 ? "" : "s"}${c.status === "expired" ? " — now out of date" : c.status === "due-soon" ? " — renewal due soon" : ""}` : c.task?.status === "open" ? `Upload requested ${ukDate(c.task.sentAt)} from ${c.task.recipientName || "the branch"} — not received yet` : !c.recipientStaffId ? "No recipient chosen yet — choose who receives the upload request in the Working grid" : "No upload received yet"
          };
        }
      }
      // Revalidation: the GPhC only renews a registration once that year's
      // revalidation records are submitted, so a current registration for every
      // pharmacist and technician (the team rows above) is the evidence.
      for (const b of g.branches || []) {
        const teams = ["reg-pharmacist", "reg-technician"].map(el => (g.cells || []).find(c => c.rowId === `el:${el}` && c.dbName === b.dbName && c.source === "team" && c.team)).filter(c => c && c.team.total > 0);
        if (!teams.length) continue;
        const ok = teams.every(c => c.status === "current" || c.status === "due-soon");
        const conf = teams.reduce((t, c) => t + (c.team.confirmed || 0), 0),
          tot = teams.reduce((t, c) => t + (c.team.total || 0), 0);
        grid.cells[`reg-revalidation|${b.pharmacyName}`] = {
          kind: "derived",
          usage: ok ? "ok" : "gaps",
          note: `From GPhC registrations: ${conf} of ${tot} pharmacist${tot === 1 ? "" : "s"} / technicians with a current registration (renewal needs that year’s revalidation submitted)`
        };
      }
      // Distance selling pharmacies: which branches, and their website reviews.
      grid.dspNames = (g.branches || []).filter(b => g.sites?.[b.dbName]?.dsp).map(b => b.pharmacyName);
      for (const b of g.branches || []) {
        if (!g.sites?.[b.dbName]?.dsp) continue;
        for (const el of ["dsp-website", "dsp-nhs-hlp", "dsp-nhs-publicity"]) {
          const cell = (g.cells || []).find(c => c.rowId === `el:${el}` && c.dbName === b.dbName);
          if (cell?.source && cell.source !== "web") continue; // set to upload / link / N/A instead
          const w = webRowStatus(el, g.sites[b.dbName]);
          grid.cells[`${el}|${b.pharmacyName}`] = {
            kind: "web",
            usage: w.status === "current" ? "ok" : w.status === "due-soon" ? "lapsed" : "gaps",
            note: `Website review: ${w.note}`,
            review: g.sites[b.dbName].webReview || null,
            checks: w.checks || [],
            websiteUrl: g.sites[b.dbName].websiteUrl || "",
            files: g.sites[b.dbName].websiteUrl ? [{
              filename: g.sites[b.dbName].websiteUrl,
              fileUrl: g.sites[b.dbName].websiteUrl
            }] : []
          };
        }
      }
      // Complaints procedure: made of four parts set up in the Working grid;
      // in place only when every part is.
      for (const parentId of PARTS_PARENTS) for (const b of g.branches || []) {
        // Parts fed by another item's Pharmace data (CD key log ← RP sign-in).
        // Parts fed by another item's Pharmace data (CD key log ← RP sign-in),
        // or by a live module rule of their own (CD discrepancies).
        const liveFor = partId => {
          const al = LIVE_ALIAS[partId];
          if (!al) return null;
          const u = extra[USAGE_KEY(al.elementId)]?.[b.pharmacyName];
          if (u && u.usage) return {
            live: true,
            noData: u.usage === "never",
            done: u.usage === "ok",
            usageNote: u.note
          };
          const src = LIVE_SOURCES[al.elementId];
          if (src?.usage && byEndpoint[src.endpoint]) {
            const row = byEndpoint[src.endpoint][b.pharmacyName];
            if (!row) return {
              live: true,
              noData: true,
              done: false,
              usageNote: "Not used at this branch"
            };
            const v = src.usage(row);
            return {
              live: true,
              noData: v.usage === "never",
              done: v.usage === "ok",
              usageNote: v.note
            };
          }
          return null;
        };
        const parts = partsStatus(g, b.dbName, parentId, liveFor);
        if (!parts) continue;
        const okN = parts.filter(x => x.state === "ok").length;
        const usage = okN === parts.length ? "ok" : parts.some(x => x.state === "bad") ? "gaps" : "missing";
        const rec = parts.find(x => x.id === "complaints--records");
        const todo = parts.filter(x => x.state !== "ok").map(x => x.label.replace(/^\d · /, ""));
        grid.cells[`${parentId}|${b.pharmacyName}`] = {
          kind: "parts",
          usage,
          parts,
          files: rec?.reports || [],
          nm: rec?.analysis?.kind === "complaints" ? {
            ...rec.analysis,
            reports: rec.reports || []
          } : null,
          note: `${okN} of ${parts.length} parts in place${todo.length ? ` — still needed: ${todo.join("; ")}` : ""}`
        };
      }
      extra.__grid = grid;
      const key = USAGE_KEY("rp-log");
      for (const c of g.cells || []) {
        if (c.rowId !== "el:rp-log" || !c.evidence?.analysis) continue;
        const name = nameOf.get(c.dbName);
        if (!name) continue;
        const current = extra[key]?.[name];
        // Use the upload when the branch is set to upload, or Pharmace has
        // nothing current for it (never used, or lapsed).
        if (current && c.source !== "upload" && !["never", "lapsed"].includes(current.usage)) continue;
        const u = rpUsage(c.evidence.analysis);
        if (!u) continue;
        extra[key] = extra[key] || {};
        extra[key][name] = {
          ...u,
          note: `From the spreadsheet uploaded ${ukDate(c.evidence.at)} — ${u.note.charAt(0).toLowerCase()}${u.note.slice(1)}`,
          data: {
            ...c.evidence.analysis,
            uploadedAt: c.evidence.at,
            uploadedBy: c.evidence.by,
            fileUrl: c.evidence.fileUrl
          },
          fromUpload: true
        };
      }
    }
  } catch {/* the grid is optional here */}

  // Alternative-evidence attestations (paper records etc.).
  let attData = null;
  try {
    const ares = await fetch(`${API_BASE}/api/headoffice/${withOrgScope("compliance/attestations")}`, {
      headers: token ? {
        Authorization: `Bearer ${token}`
      } : {}
    });
    if (ares.ok) {
      const a = await ares.json();
      attData = a.byBranch || {};
    }
  } catch {/* attestations optional */}
  return {
    liveData: byEndpoint,
    extraData: Object.keys(extra).length ? extra : null,
    effData: Object.keys(eff).length ? eff : null,
    taskGrid,
    attData,
    liveBranches: nameSet.size ? Array.from(nameSet).sort() : null,
    liveFailures
  };
}
export default function RegulatoryCompliance({
  token,
  userData,
  inspector = false,
  initialBranch = "",
  onClose
}) {
  // "Hide names" admin toggle — display-only. All internal data joins key on
  // the REAL pharmacyName; masking is applied only at render points.
  const masked = useMaskNames();
  const [activeGroupId, setActiveGroupId] = useState(GROUPS[0].id);
  // view is fixed to "working" (the old compliance roll-up view is retired).
  // Retain the compliance roll-up implementation and shared detail components below.
  const view = "working";
  // Overview (ComplianceOverview.js) or Working grid. Opening Regulatory
  // Compliance from the menu always lands on the Overview (2026-10).
  const [page, setPage] = useState(inspector ? "grid" : "overview");
  // Optional Overview panels, ticked under "Would you like more views of
  // current status?" — all off by default; remembered per browser.
  const [moreViews, setMoreViews] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("rcOverviewViews") || "[]"));
    } catch {
      return new Set();
    }
  });
  const toggleView = k => setMoreViews(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k);else next.add(k);
    try {
      localStorage.setItem("rcOverviewViews", JSON.stringify([...next]));
    } catch {/* storage unavailable */}
    return next;
  });
  const [reloadKey, setReloadKey] = useState(0);
  // Items start EXPANDED (2026-07): collapsedItemIds tracks the ones the user
  // has closed; switching group re-expands everything. drillItemId records
  // which item a clicked branch belongs to (feeds the drill-down modal) now
  // that several items can be open at once.
  const [collapsedItemIds, setCollapsedItemIds] = useState(() => new Set());
  const [drillItemId, setDrillItemId] = useState(null);
  const [openBranch, setOpenBranch] = useState(null); // branch drilled into

  // liveData: { [endpoint]: { [pharmacyName]: statusRow } }. Fetched once.
  const [liveData, setLiveData] = useState(null);
  const [extraData, setExtraData] = useState(null); // { [extraKey]: { [pharmacyName]: row } }
  const [effData, setEffData] = useState(null); // { [module]: { [pharmacyName]: row } } 12-month effectiveness
  const [taskGrid, setTaskGrid] = useState(null); // { rowsByName, dataTypeById }
  const [attData, setAttData] = useState(null); // { [pharmacyName]: { [elementId]: att } } — alternative-evidence attestations
  const [liveBranches, setLiveBranches] = useState(null); // real branch names from live data
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLiveLoading(true);
      setLiveError("");
      try {
        const d = await fetchAllComplianceData(token);
        if (cancelled) return;
        setLiveData(d.liveData);
        if (d.extraData) setExtraData(d.extraData);
        if (d.effData) setEffData(d.effData);
        if (d.taskGrid) setTaskGrid(d.taskGrid);
        if (d.attData) setAttData(d.attData);
        setLiveBranches(d.liveBranches);
        // Non-fatal: show a warning while retaining all successful modules.
        if (d.liveFailures && d.liveFailures.length) {
          setLiveError(`Some compliance feeds could not be loaded (${d.liveFailures.length}). Available data is still shown.`);
        }
      } catch (e) {
        if (!cancelled) {
          setLiveError(String(e.message || e));
        }
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  // Branch identity comes from the organisation membership, not from whichever
  // compliance endpoint happened to return a row. This keeps all legitimate
  // branches visible even when a module has no data (or one module is down).
  // Only the fleet-wide admin view is derived from live results.
  const branches = useMemo(() => {
    const org = viewerOrg();
    if (org === "Pharmace all") {
      return liveBranches || (liveLoading ? [] : []);
    }
    let members = [];
    try {
      members = JSON.parse(localStorage.getItem("members") || "[]");
    } catch {
      members = [];
    }
    const names = (Array.isArray(members) ? members : []).map(m => m && (m.pharmacyName || m.name || m.pharmacyId) || "").filter(Boolean);
    // If membership has not made it into localStorage yet, use scoped live
    // results as a temporary fallback; never invent placeholder pharmacies.
    if (names.length === 0) return liveBranches || [];
    return [...new Set(names)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [liveBranches, liveLoading]);

  // DSP criteria tabs appear only when at least one branch is flagged as a
  // distance selling pharmacy (Working grid → Sites), and count only those.
  const dspNames = useMemo(() => new Set(extraData?.__grid?.dspNames || []), [extraData]);
  const allGroups = useMemo(() => decorateGroups([...GROUPS, ...PQS_GROUPS, ...(dspNames.size ? DSP_GROUPS : [])], Object.values(extraData?.__grid?.jurisdictionByName || {})), [dspNames, extraData]);
  const branchesFor = useCallback(g => g?.dsp ? branches.filter(b => dspNames.has(b)) : branches, [branches, dspNames]);
  const activeGroup = useMemo(() => allGroups.find(g => g.id === activeGroupId) || allGroups[0], [activeGroupId, allGroups]);
  const activeBranches = useMemo(() => branchesFor(activeGroup), [branchesFor, activeGroup]);

  // The item the drill-down modal is showing (resolved from the drill item id).
  const openItem = useMemo(() => (activeGroup.items || []).find(it => it.id === drillItemId) || null, [activeGroup, drillItemId]);

  // Pre-compute tab %s (recompute when live data or branches change).
  const groupPcts = useMemo(() => {
    const m = {};
    for (const g of allGroups) m[g.id] = groupPct(g, branchesFor(g), liveData, taskGrid, extraData, effData, attData);
    return m;
  }, [allGroups, branchesFor, liveData, taskGrid, extraData, effData, attData]);

  // Roll-up for the Working grid's collapsible headings — the same maths as the
  // tiles and item pills above, so both views always show the same numbers.
  const gridRollup = useMemo(() => {
    const gOf = gid => allGroups.find(g => g.id === gid) || null;
    const iOf = (g, iid) => (g?.items || []).find(i => i.id === iid) || null;
    const args = [liveData, taskGrid, extraData, effData, attData];
    return {
      pct(gid, iid, branchName) {
        const g = gOf(gid);
        if (!g) return null;
        if (g.dsp && branchName && !dspNames.has(branchName)) return null;
        if (iid) {
          const it = iOf(g, iid);
          if (!it) return null;
          return branchName ? itemBranchPct(g, it, branchName, ...args) : itemPct(g, it, branchesFor(g), ...args);
        }
        if (!branchName) return groupPcts[gid] ?? null;
        const vals = (g.items || []).map(it => itemBranchPct(g, it, branchName, ...args)).filter(v => v != null);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      },
      metrics(gid, iid) {
        return iOf(gOf(gid), iid)?.metrics || [];
      },
      metric(m, branchName) {
        if (!branchLocation(branchName, extraData)?.nation) return {
          pct: null,
          threshold: null
        };
        const row = effData?.[m.module]?.[branchName] || null;
        return {
          pct: row ? row[m.field] ?? null : null,
          threshold: row ? row[m.thresholdField] ?? null : null
        };
      },
      bg: pctBg,
      band: pctBand
    };
  }, [allGroups, branchesFor, dspNames, groupPcts, liveData, taskGrid, extraData, effData, attData]);

  // The same refresh handlers the compliance view's item popup uses.
  const refreshTaskGrid = useCallback(async () => {
    try {
      const g = await fetchTaskGrid(token);
      if (g) setTaskGrid(g);
    } catch (_) {}
  }, [token]);
  const applyAttestation = useCallback((branchName, elementId, att) => {
    setAttData(prev => {
      const next = {
        ...(prev || {})
      };
      next[branchName] = {
        ...(next[branchName] || {})
      };
      if (att) next[branchName][elementId] = att;else delete next[branchName][elementId];
      return next;
    });
  }, []);

  // Effectiveness measures that belong with one element (matched on its Pharmace module).
  const metricsForElement = useCallback((groupId, itemId, elementId) => {
    const it = (allGroups.find(g => g.id === groupId)?.items || []).find(i => i.id === itemId);
    // Raw module key (e.g. "fridge"), as the measures use — not the display name.
    const al = LIVE_ALIAS[elementId];
    const id = al ? al.elementId : elementId;
    const mod = LIVE_SOURCES[id] ? LIVE_SOURCES[id].endpoint.split("/")[0] : EXTRA_FOR[id] || null;
    return mod ? (it?.metrics || []).filter(m => m.module === mod) : [];
  }, [allGroups]);

  // The data behind one cell (Working grid cell, Overview → Evidence → "Show the data").
  const renderGridDetail = (row, branchName) => {
    /* The same detail as the compliance view's Review data, opened, with
       any 12-month measures for the same Pharmace module above it. */
    if (isSopManagement(row)) return <SopManagementDetails row={row} dbName={extraData?.__grid?.dbByName?.[branchName]} token={token} readOnly={inspector} onChanged={refreshTaskGrid} />;
    const detailId = row.elementId || row.rowId?.replace(/^el:/, '');
    const savedDetail = extraData?.__grid?.raw?.[`${detailId}|${branchName}`];
    if (NI_PROCEDURE_SOPS.has(detailId) || savedDetail?.source === 'sop' || extraData?.__grid?.rowCfg?.[detailId]?.defaultSource === 'sop') return <LinkedSopDetails row={row} dbName={extraData?.__grid?.dbByName?.[branchName]} token={token} readOnly={inspector} />;
    const ms = row.part ? [] : metricsForElement(row.groupId, row.itemId, row.elementId);
    return <>
      {ms.length > 0 && <MetricsPanel metrics={ms} branch={branchName} effData={effData} token={token} />}
      <ElementList group={{
        id: row.groupId
      }} item={{
        id: row.itemId,
        elements: [{
          id: row.elementId || row.rowId,
          label: row.label,
          requirement: row.requirement || "recommended",
          help: row.help || row.explanation || "",
          link: row.link
        }]
      }} readOnly={inspector} branch={branchName} liveData={liveData} taskGrid={taskGrid} extraData={extraData} attData={attData} token={token} autoOpen={row.elementId || row.rowId} onGridChanged={refreshTaskGrid} onAttestationsChanged={applyAttestation} />
    </>;
  };
  const WorkingGrid = inspector ? InspectorWorkingGrid : ComplianceWorkingGrid;
  return <div style={{
    ...(inspector ? {
      position: "fixed",
      inset: 0,
      zIndex: 9000,
      background: "#fff",
      overflow: "auto"
    } : {}),
    padding: "16px 20px",
    maxWidth: view === "working" ? "none" : 1180
  }} onClickCapture={evidenceClickCapture}>
      <EvidenceViewerHost />
      {!inspector && <div style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8
    }}>
        <div>
          <h1 style={{
          margin: 0,
          fontSize: 22
        }}>{inspector ? "Regulatory compliance - inspection view" : "Regulatory Compliance"}</h1>{inspector && <button onClick={onClose} title="Return to the main application">Close inspection view</button>}
          <div style={{
          color: "#5D5D5D",
          fontSize: 13,
          marginTop: 2
        }}>
            Estate readiness across applicable GPhC, PSNI and national requirements.
            Percentages roll up from individual data elements.
          </div>
          <div style={{
          fontSize: 12,
          marginTop: 4
        }}>
            {liveLoading ? <span style={{
            color: "#AD4E00"
          }}>Please wait while loading branches…</span> : liveError ? <span style={{
            color: "#A8071A"
          }}>{liveError} Organisation branches remain visible; affected elements show no live evidence.</span> : liveBranches ? <span style={{
            color: "#389E0D"
          }}>● Live data loaded. Showing {branches.length} organisation branches.</span> : <span style={{
            color: "#8C8C8C"
          }}>No live evidence returned. Showing organisation branches.</span>}
          </div>
          <style>{`@keyframes rcSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
        {view === "compliance" && <Legend />}
      </div>}

      {!inspector && <div role="tablist" aria-label="Regulatory compliance pages" style={{
      display: "flex",
      gap: 4,
      marginTop: 12,
      borderBottom: "2px solid #F0F0F0"
    }}>
        {[["overview", "📊 Overview"], ["grid", "Working grid"]].map(([k, l]) => <button key={k} role="tab" aria-selected={page === k} onClick={() => setPage(k)} style={{
        border: "none",
        borderBottom: page === k ? "3px solid #0958D9" : "3px solid transparent",
        background: "none",
        padding: "8px 14px",
        marginBottom: -2,
        fontSize: 14,
        fontWeight: page === k ? 700 : 500,
        color: page === k ? "#0958D9" : "#434343",
        cursor: "pointer"
      }}>{l}</button>)}
        {page === "overview" && <div role="group" aria-label="Would you like more views of current status?" style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "4px 14px",
        marginLeft: 16,
        marginBottom: 4,
        alignSelf: "center",
        padding: "5px 12px",
        border: "1px solid #D6E4FF",
        background: "#F0F5FF",
        borderRadius: 8,
        fontSize: 12.5
      }}>
            <span style={{
          fontWeight: 700,
          color: "#1D39C4"
        }}>Would you like more views of current status?</span>
            {OVERVIEW_VIEWS.map(v => <label key={v.key} style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          cursor: "pointer",
          color: "#262626"
        }}>
                <input type="checkbox" checked={moreViews.has(v.key)} onChange={() => toggleView(v.key)} />
                {v.label}
              </label>)}
          </div>}
      </div>}

      {page === "overview" && <ComplianceOverview views={moreViews} groups={GROUPS} token={token} masked={masked} displayName={displayName} liveModule={liveModuleName} rollup={gridRollup} ready={!liveLoading} renderDetail={renderGridDetail} liveStatus={(branchName, groupId, itemId, elementId) => resolveDone(groupId, itemId, branchName, elementId, liveData, taskGrid, extraData, attData)} />}

      {view === "working" && page === "grid" && <WorkingGrid initialBranch={initialBranch} groups={GROUPS} token={token} masked={masked} displayName={displayName} liveModule={liveModuleName} rollup={gridRollup} renderDetail={renderGridDetail} renderItemDetail={(groupId, itemId, branchName, metricId) => {
      /* The whole item at one branch — the same content as the compliance
         view's item popup (measures on top, then every element). */
      const g = allGroups.find(x => x.id === groupId);
      const it = (g?.items || []).find(x => x.id === itemId);
      if (!g || !it) return <div style={{
        padding: 12,
        color: "#5D5D5D"
      }}>Nothing to show for this item.</div>;
      const ms = (it.metrics || []).filter(m => !metricId || m.id === metricId);
      // Rows added to this item in the Working grid are listed after its own elements.
      const added = Object.values(extraData?.__grid?.rowCfg || {}).filter(r => r.custom && r.groupId === groupId && r.itemId === itemId).map(r => ({
        id: r.rowId,
        label: r.label,
        requirement: "recommended",
        help: r.explanation || ""
      }));
      const itAll = added.length ? {
        ...it,
        elements: [...(it.elements || []), ...added]
      } : it;
      return <>
              {ms.length > 0 && <MetricsPanel metrics={ms} branch={branchName} effData={effData} token={token} initialOpen={metricId || null} />}
              {!metricId && <ElementList group={g} item={itAll} readOnly={inspector} branch={branchName} liveData={liveData} taskGrid={taskGrid} extraData={extraData} attData={attData} token={token} onGridChanged={refreshTaskGrid} onAttestationsChanged={applyAttestation} />}
            </>;
    }} liveStatus={(branchName, groupId, itemId, elementId) => resolveDone(groupId, itemId, branchName, elementId, liveData, taskGrid, extraData, attData)} />}

      {view === "compliance" && <>
      {/* ---- Tabs (groups) ---- */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 16,
        borderBottom: "2px solid #F0F0F0",
        paddingBottom: 0
      }}>
        {allGroups.map(g => {
          const pct = groupPcts[g.id];
          const active = g.id === activeGroupId;
          return <button key={g.id} onClick={() => {
            setActiveGroupId(g.id);
            setCollapsedItemIds(new Set());
            setDrillItemId(null);
            setOpenBranch(null);
          }} title={`${g.name} — ${fmtPct(pct)} complete`} style={{
            outline: activeGroupId === g.id ? "2px solid #000" : "none",
            outlineOffset: 2,
            position: "relative",
            border: "1px solid #D9D9D9",
            borderBottom: active ? "2px solid #262626" : "1px solid #D9D9D9",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            background: pctBgStrong(pct),
            padding: "8px 12px",
            cursor: "pointer",
            fontWeight: active ? 700 : 500,
            color: "#262626",
            marginBottom: -2,
            boxShadow: active ? "0 -2px 6px rgba(0,0,0,0.06)" : "none"
          }}>
              <div style={{
              fontSize: 13,
              lineHeight: 1.1
            }}>{g.name}</div>
              <div style={{
              fontSize: 11,
              opacity: 0.7
            }}>{g.subtitle}{g.dsp ? ` · ${branchesFor(g).length} site${branchesFor(g).length === 1 ? "" : "s"}` : ""}</div>
              <div style={{
              fontSize: 16,
              fontWeight: 800,
              marginTop: 2
            }}>{fmtPct(pct)}</div>
            </button>;
        })}
      </div>

      {/* ---- Tab body: item list ---- */}
      <div style={{
        marginTop: 18
      }}>
        <h2 style={{
          fontSize: 16,
          margin: "0 0 8px"
        }}>
          {activeGroup.name} <span style={{
            color: "#8C8C8C",
            fontWeight: 400
          }}>· {activeGroup.subtitle}</span>
        </h2>

        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 8
        }}>
          {(activeGroup.items || []).map(item => {
            const pct = itemPct(activeGroup, item, activeBranches, liveData, taskGrid, extraData, effData, attData);
            const isOpen = !collapsedItemIds.has(item.id);
            const anyLive = (item.elements || []).some(el => elementIsLive(el.id));
            return <div key={item.id} style={{
              border: "1px solid #F0F0F0",
              borderRadius: 8,
              overflow: "hidden"
            }}>
                {/* Item header row */}
                <button onClick={() => {
                setCollapsedItemIds(prev => {
                  const next = new Set(prev);
                  if (next.has(item.id)) next.delete(item.id);else next.add(item.id);
                  return next;
                });
              }} style={{
                outline: isOpen ? "2px solid #000" : "none",
                outlineOffset: -2,
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                background: "#fff",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                color: "#262626"
              }}>
                  <span style={{
                  fontSize: 12,
                  color: "#5D5D5D",
                  width: 14
                }}>{isOpen ? "▼" : "▶"}</span>
                  <span style={{
                  flex: 1,
                  fontWeight: 600,
                  color: "#262626",
                  fontSize: 14
                }}>{item.name}</span>
                  {anyLive && <LiveTag />}
                  <span style={{
                  fontSize: 12,
                  color: "#8C8C8C"
                }}>
                    {(item.elements || []).length} element{(item.elements || []).length === 1 ? "" : "s"}
                  </span>
                  <PctPill pct={pct} />
                </button>

                {/* Branch list for this item */}
                {isOpen && <div style={{
                background: "#FAFAFA",
                borderTop: "1px solid #F0F0F0",
                padding: "8px 12px 12px"
              }}>
                    <div style={{
                  fontSize: 12,
                  color: "#5D5D5D",
                  margin: "2px 0 8px"
                }}>
                      Completion by branch — click a branch to see the data elements.
                    </div>
                    {liveLoading && activeBranches.length === 0 ? <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "18px 12px",
                  color: "#5D5D5D",
                  fontSize: 13
                }}>
                        <span aria-hidden style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid #D9D9D9",
                    borderTopColor: "#5D5D5D",
                    display: "inline-block",
                    animation: "rcSpin 0.8s linear infinite"
                  }} />
                        Please wait while loading branches…
                      </div> : <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 6
                }}>
                      {activeBranches.map(b => {
                    const bPct = itemBranchPct(activeGroup, item, b, liveData, taskGrid, extraData, effData, attData);
                    const bOpen = openBranch === b && drillItemId === item.id;
                    return <button key={b} onClick={() => {
                      setDrillItemId(item.id);
                      setOpenBranch(bOpen && drillItemId === item.id ? null : b);
                    }} title={`${displayName(b, masked)} — ${fmtPct(bPct)}`} style={{
                      outline: bOpen ? "2px solid #000" : "none",
                      outlineOffset: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: bOpen ? "2px solid #262626" : "1px solid #D9D9D9",
                      background: pctBg(bPct),
                      cursor: "pointer",
                      textAlign: "left",
                      boxShadow: bOpen ? "0 0 0 2px rgba(30,41,59,0.15)" : "none",
                      color: "#262626"
                    }}>
                            <span style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#262626"
                      }}>{displayName(b, masked)}</span>
                            <span style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#262626"
                      }}>{fmtPct(bPct)}</span>
                          </button>;
                  })}
                    </div>}
                  </div>}
              </div>;
          })}
        </div>
      </div>

      {/* Branch data-element drill-down — modal so it's always visible no matter
          how far down the 200+ branch grid the clicked tile was. */}
      {openBranch && openItem && <ElementModal group={activeGroup} item={openItem} branch={openBranch} liveData={liveData} taskGrid={taskGrid} extraData={extraData} effData={effData} attData={attData} token={token} onClose={() => setOpenBranch(null)} onGridChanged={async () => {
        try {
          const g = await fetchTaskGrid(token);
          if (g) setTaskGrid(g);
        } catch (_) {}
      }} onAttestationsChanged={(branchName, elementId, att) => {
        setAttData(prev => {
          const next = {
            ...(prev || {})
          };
          next[branchName] = {
            ...(next[branchName] || {})
          };
          if (att) next[branchName][elementId] = att;else delete next[branchName][elementId];
          return next;
        });
      }} />}
      </>}
    </div>;
}

// -----------------------------------------------------------------------------
// Data-element list for one item at one branch. Green = done, red = not done.
// Each row carries the requirement badge + floating help (with optional link).
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// CompliancePrintReport — printable Regulatory Compliance report for ONE
// pharmacy ("Export Regulatory compliance report" on the Single pharmacy
// page). Deliberately NOT a second implementation: it loads its data through
// fetchAllComplianceData and renders every group → item with the SAME
// ElementList component (in printMode, so each element's Review-data panel —
// live drill grids, periodic entry history, source rows — is open), so any
// change to the website's compliance panels flows into the export
// automatically. Printing uses the browser's Print → Save as PDF via
// scoped @media print CSS.
// -----------------------------------------------------------------------------
export function CompliancePrintReport({
  pharmacyName,
  pharmacyDetails = null,
  token,
  onClose
}) {
  const masked = useMaskNames();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const d = await fetchAllComplianceData(token);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);
  const noop = () => {};
  const liveData = data?.liveData || null;
  const taskGrid = data?.taskGrid || null;
  const extraData = data?.extraData || null;
  const effData = data?.effData || null;
  const attData = data?.attData || null;

  // DSP criteria are included only when this pharmacy is a distance selling pharmacy.
  const reportGroups = useMemo(() => decorateGroups([...GROUPS, ...PQS_GROUPS, ...((extraData?.__grid?.dspNames || []).includes(pharmacyName) ? DSP_GROUPS : [])], [branchLocation(pharmacyName, extraData)]), [extraData, pharmacyName]);
  // Branch-level %s from the same helpers the board uses.
  const itemPcts = useMemo(() => {
    if (!data) return {};
    const m = {};
    for (const g of reportGroups) {
      for (const it of g.items || []) {
        m[`${g.id}|${it.id}`] = itemBranchPct(g, it, pharmacyName, liveData, taskGrid, extraData, effData, attData);
      }
    }
    return m;
  }, [data, pharmacyName, liveData, taskGrid, extraData, effData, attData, reportGroups]);
  const groupBranchPct = g => {
    const vals = (g.items || []).map(it => itemPcts[`${g.id}|${it.id}`]).filter(v => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  // Optional pharmacy detail lines from whatever the caller knows.
  const detailBits = [];
  if (pharmacyDetails) {
    for (const k of ["address", "addressLine1", "postcode", "postCode", "phone", "telephone", "email", "odsCode"]) {
      const v = pharmacyDetails[k];
      if (v && typeof v !== "object") detailBits.push(String(v));
    }
  }
  return <div className="rc-print-root" style={{
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    overflowY: "auto",
    background: "#F5F5F5",
    padding: "20px 0"
  }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .rc-print-root, .rc-print-root * { visibility: visible; }
          .rc-print-root { position: absolute !important; left: 0; top: 0;
                           width: 100%; height: auto !important;
                           overflow: visible !important; background: #fff !important;
                           padding: 0 !important; }
          .rc-noprint { display: none !important; }
          .rc-print-item { break-inside: avoid-page; }
        }
      `}</style>
      <div style={{
      maxWidth: 1000,
      margin: "0 auto",
      background: "#fff",
      borderRadius: 10,
      boxShadow: "0 2px 14px rgba(0,0,0,0.15)",
      padding: "22px 26px"
    }}>
        {/* Toolbar (never printed) */}
        <div className="rc-noprint" style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginBottom: 12
      }}>
          <button onClick={() => window.print()} disabled={loading || !!error} style={{
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          border: "1px solid #262626",
          background: "#262626",
          color: "#fff",
          borderRadius: 6,
          padding: "7px 14px"
        }}>
            Print / Save as PDF
          </button>
          <button onClick={onClose} style={{
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          border: "1px solid #D9D9D9",
          background: "#FAFAFA",
          color: "#434343",
          borderRadius: 6,
          padding: "7px 14px"
        }}>
            Close
          </button>
        </div>

        {/* Report header: pharmacy details + date */}
        <div style={{
        borderBottom: "3px solid #262626",
        paddingBottom: 12,
        marginBottom: 16
      }}>
          <h1 style={{
          margin: 0,
          fontSize: 22
        }}>Regulatory compliance report</h1>
          <div style={{
          fontSize: 16,
          fontWeight: 700,
          marginTop: 4
        }}>
            {displayName(pharmacyName, masked)}
          </div>
          {detailBits.length > 0 && <div style={{
          fontSize: 12.5,
          color: "#5D5D5D",
          marginTop: 2
        }}>
              {detailBits.join(" · ")}
            </div>}
          <div style={{
          fontSize: 12.5,
          color: "#5D5D5D",
          marginTop: 2
        }}>
            {viewerOrg() ? `${viewerOrg()} · ` : ""}
            Generated {new Date().toLocaleString("en-GB")}
          </div>
          <div style={{
          fontSize: 11.5,
          color: "#8C8C8C",
          marginTop: 4
        }}>
            Each data element shows its recorded data source (head office /
            paper / third-party / periodic task) and the supporting evidence —
            the same panels shown on the Regulatory Compliance board.
          </div>
        </div>

        {loading && <div style={{
        color: "#AD4E00",
        fontSize: 14,
        padding: "24px 0"
      }}>
            Loading compliance data for {displayName(pharmacyName, masked)}…
          </div>}
        {error && <div style={{
        color: "#A8071A",
        fontSize: 14,
        padding: "24px 0"
      }}>
            Couldn't load compliance data: {error}
          </div>}

        {!loading && !error && data && reportGroups.map(g => {
        const gPct = groupBranchPct(g);
        return <div key={g.id} style={{
          marginBottom: 22
        }}>
              {/* Group heading block — same % colouring as the board tabs */}
              <div style={{
            background: pctBgStrong(gPct),
            border: "1px solid #D9D9D9",
            borderRadius: 8,
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 10
          }}>
                <div>
                  <div style={{
                fontSize: 16,
                fontWeight: 800,
                color: "#262626"
              }}>{g.name}</div>
                  <div style={{
                fontSize: 12,
                color: "#434343",
                opacity: 0.8
              }}>{g.subtitle}</div>
                </div>
                <div style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#262626"
            }}>{fmtPct(gPct)}</div>
              </div>

              {(g.items || []).map(it => {
            const iPct = itemPcts[`${g.id}|${it.id}`];
            return <div key={it.id} className="rc-print-item" style={{
              marginTop: 10
            }}>
                    {/* Item subheading panel */}
                    <div style={{
                background: pctBg(iPct),
                border: "1px solid #F0F0F0",
                borderRadius: 8,
                padding: "6px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 10
              }}>
                      <div style={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#262626"
                }}>
                        {it.name}
                        {it.subtitle ? <span style={{
                    fontWeight: 400,
                    color: "#5D5D5D"
                  }}> · {it.subtitle}</span> : null}
                      </div>
                      <div style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#262626"
                }}>{fmtPct(iPct)}</div>
                    </div>
                    {/* Effectiveness metrics above the checklist — same order
                        as the website's branch popup (ElementModal). */}
                    {(it.metrics || []).length > 0 && <MetricsPanel metrics={it.metrics} branch={pharmacyName} effData={effData} token={token} />}
                    {/* The website's own element list, panels open */}
                    <ElementList group={g} item={it} branch={pharmacyName} liveData={liveData} taskGrid={taskGrid} extraData={extraData} attData={attData} token={token} onGridChanged={noop} onAttestationsChanged={noop} printMode />
                  </div>;
          })}
            </div>;
      })}
      </div>
    </div>;
}

// -----------------------------------------------------------------------------
// Where an item's evidence comes from at a branch, from the Working grid
// (2026-10): the branch's own cell, else the item's default. Drives the Data
// source column and what the Review data panel shows.
// -----------------------------------------------------------------------------
const SRC_TAG = {
  pharmace: "Pharmace",
  task: "Staff app",
  upload: "Email upload",
  sop: "SOP",
  link: "Held elsewhere",
  na: "N/A",
  team: "Each registrant",
  web: "Website",
  parts: "Parts"
};
const SRC_TAG_STYLE = {
  pharmace: ["#F6FFED", "#389E0D", "#B7EB8F"],
  task: ["#E6F7FF", "#0958D9", "#91D5FF"],
  upload: ["#E6F7FF", "#0E8CFF", "#69C0FF"],
  sop: ["#F9F0FF", "#531DAB", "#D3ADF7"],
  link: ["#F9F0FF", "#531DAB", "#EFDBFF"],
  na: ["#FAFAFA", "#8C8C8C", "#F0F0F0"],
  team: ["#E6F7FF", "#0958D9", "#91D5FF"],
  web: ["#FFFBE6", "#AD6800", "#FFE58F"],
  parts: ["#F5F5F5", "#434343", "#D9D9D9"]
};
function gridSourceOf(extraData, elId, branch, noData) {
  const g = extraData?.__grid;
  const c = g?.raw?.[`${elId}|${branch}`] || null;
  if (c?.source) return {
    src: c.source,
    c
  };
  // Distance-selling rows checked by reading the branch's website.
  if (g?.cells?.[`${elId}|${branch}`]?.kind === "web") return {
    src: "web",
    c: null,
    web: g.cells[`${elId}|${branch}`]
  };
  const r = g?.rowCfg?.[elId];
  let d = r?.defaultSource || (r?.validity?.mode && r.validity.mode !== "none" ? "task" : "");
  if (d === "pharmace" && noData && !["lfpse", "adv-nms"].includes(elId) && r?.fallbackTask !== false) d = r?.fallbackSource || "task";
  return {
    src: d || null,
    c: null,
    fromRow: !!d
  };
}
// Pharmace records behind each item (paged lists in Review data).
const ELEMENT_RECORDS = {
  "near-miss": [["near-miss", "Near misses"]],
  "cd-register": [["cd-balance", "Balance checks"], ["cd-discrepancy", "Discrepancies"]],
  "cd-destruction": [["cd-destruction", "Destructions"]],
  "fridge-log": [["fridge", "Fridge readings"]],
  "cdao--discrepancies": [["cd-discrepancy", "Discrepancies"]],
  "cdcab--keys": [["rp", "RP sign-in sessions (the RP in charge holds the CD keys)"]],
  "lfpse": [["patient-safety", "Patient safety incidents"]],
  "riddor-book": [["accident", "Accident book entries"]],
  "riddor-report": [["accident", "Accident book entries"]],
  "sup-mhra": [["mhra", "MHRA alert responses"]]
};
const isImgUrl = u => /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(String(u || "")) || /(amazonaws\.com|firebasestorage)/i.test(String(u || ""));
const dk = {
  row: {
    display: "flex",
    gap: 8,
    fontSize: 12,
    padding: "3px 0"
  },
  k: {
    width: 120,
    color: "#8C8C8C",
    flexShrink: 0
  },
  a: {
    color: "#91D5FF"
  },
  hd: {
    color: "#D9D9D9",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    margin: "12px 0 6px",
    borderTop: "1px solid #262626",
    paddingTop: 8
  }
};
function EvidenceFiles({
  files
}) {
  if (!files || !files.length) return null;
  return <div style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 6
  }}>
    {files.map((f, i) => isImgUrl(f.fileUrl) ? <img key={i} src={f.fileUrl} alt={f.filename || "Evidence"} title="Click to view" onClick={() => openEvidence(f.fileUrl, f.filename || "Evidence")} style={{
      height: 90,
      maxWidth: 160,
      objectFit: "cover",
      borderRadius: 6,
      border: "1px solid #434343",
      cursor: "zoom-in",
      background: "#262626"
    }} /> : <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer" style={{
      ...dk.a,
      fontSize: 12
    }}>📄 {f.filename || "file"}</a>)}
  </div>;
}
// Evidence held in the Working grid for one item at one branch: the source,
// its schedule, what has arrived (photos open in the viewer), and history —
// shown a page at a time as you scroll.
// What the automatic website review found for a distance-selling row: the
// checks behind it, each with the words quoted from the page and where.
function WebReviewPanel({
  w
}) {
  const r = w.review;
  if (!r) return <div style={{
    padding: 4,
    color: "#D9D9D9"
  }}>{w.websiteUrl ? "The website hasn’t been reviewed yet — review it under Working grid → Sites." : "No website address yet — add it under Working grid → Sites."}</div>;
  return <div style={{
    padding: 4
  }}>
      <div style={dk.row}><span style={dk.k}>Data source</span><span>Automatic website review of <a href={w.websiteUrl || r.url} target="_blank" rel="noreferrer" style={dk.a}>{w.websiteUrl || r.url}</a></span></div>
      <div style={dk.row}><span style={dk.k}>Reviewed</span><span>{ukDate(r.reviewedAt)} · {r.via === "ai" ? "read by AI, every quote checked against the page" : "keyword rules"} · {(r.pagesRead || []).length} page{(r.pagesRead || []).length === 1 ? "" : "s"} read · {r.passed} of {r.total} checks met across the site</span></div>
      {r.error && <div style={{
      ...dk.row,
      color: "#FFA39E"
    }}><span style={dk.k}>Problem</span><span>{r.error}</span></div>}
      <div style={dk.hd}>Checks for this item</div>
      {(w.checks || []).map(c => <div key={c.key} style={{
      display: "flex",
      gap: 8,
      fontSize: 12,
      padding: "4px 0",
      borderTop: "1px solid #262626"
    }}>
          <span style={{
        width: 16,
        fontWeight: 700,
        color: c.found ? "#95DE64" : "#FFA39E"
      }}>{c.found ? "✓" : "✗"}</span>
          <div style={{
        flex: 1
      }}>
            <div style={{
          color: "#F0F0F0",
          fontWeight: 600
        }}>{c.label}</div>
            {c.quote && <div style={{
          color: "#BFBFBF",
          fontStyle: "italic",
          marginTop: 2
        }}>“{c.quote}”</div>}
            {c.note && <div style={{
          color: c.found ? "#8C8C8C" : "#FFA39E",
          marginTop: 2
        }}>{c.note}</div>}
            {c.url && <a href={c.url} target="_blank" rel="noreferrer" style={{
          ...dk.a,
          fontSize: 11
        }}>{c.url.replace(/^https?:\/\//, "")}</a>}
          </div>
        </div>)}
      {(r.pagesRead || []).length > 0 && <><div style={dk.hd}>Pages read</div>
        {(r.pagesRead || []).map(p => <div key={p.url} style={{
        fontSize: 11
      }}><a href={p.url} target="_blank" rel="noreferrer" style={dk.a}>{p.url.replace(/^https?:\/\//, "")}</a></div>)}</>}
    </div>;
}
function GridEvidencePanel({
  c,
  src,
  rowCfg
}) {
  const [shown, setShown] = useState(20);
  const ev = c?.evidence || null;
  const files = ev ? [...(ev.files?.length ? ev.files : ev.fileUrl ? [{
    filename: ev.filename || "Evidence",
    fileUrl: ev.fileUrl
  }] : []), ...(ev.reports || [])].filter(f => f && f.fileUrl) : [];
  const hist = [...(c?.history || [])].reverse();
  const srcLine = src === "task" ? `Task in the staff app${c?.recipientName ? ` → ${c.recipientName}` : " — no recipient chosen yet"}` : src === "upload" ? `Email upload request${c?.recipientName ? ` → ${c.recipientName}` : " — no recipient chosen yet"}` : src === "sop" ? "Pharmace SOP(s) linked in the Working grid" : src === "link" ? "Held in another system" : src === "na" ? "Not applicable at this branch" : src === "team" ? "A task to every registrant at the branch" : src === "web" ? "Automatic website review" : SRC_TAG[src] || src || "Not set";
  return <div style={{
    padding: 4
  }}>
      <div style={dk.row}><span style={dk.k}>Data source</span><span>{srcLine}{!c && " (the item’s default — not saved at this branch yet)"}</span></div>
      {c?.status && <div style={dk.row}><span style={dk.k}>Status</span><span>{c.status}{c.expiresAt ? ` · valid until ${ukDate(c.expiresAt)}` : ""}</span></div>}
      {src === "link" && <div style={dk.row}><span style={dk.k}>Held</span><span>{c?.linkLabel || "—"}{c?.linkUrl && <> · <a href={c.linkUrl} target="_blank" rel="noreferrer" style={dk.a}>open ↗</a></>}</span></div>}
      {src === "na" && <div style={dk.row}><span style={dk.k}>Reason</span><span>{rowCfg?.naReason || "—"}</span></div>}
      {src === "sop" && <>
        <div style={dk.row}><span style={dk.k}>Linked SOPs</span><span>{(rowCfg?.sopTitles || []).join(", ") || "none chosen yet"}</span></div>
        {(c?.sop?.matched || []).map(m => <div key={m.title} style={{
        ...dk.row,
        paddingLeft: 128
      }}>
          <span style={{
          color: !m.held ? "#FFA39E" : !m.inDate ? "#FFD666" : "#B7EB8F"
        }}>{m.title}{m.version ? ` v${m.version}` : ""} — {!m.held ? "not held at this branch" : !m.inDate ? "past its review date" : "in place"}{m.held && m.assigned ? ` · ${m.signed}/${m.assigned} staff read & signed` : ""}</span></div>)}
      </>}
      {src === "team" && (c?.team?.people || []).map(p => <div key={p.staffId || p.name} style={{
      ...dk.row,
      paddingLeft: 128
    }}>
        <span>{p.name}{p.evidence?.registrationNumber ? ` · registration ${p.evidence.registrationNumber}` : ""}{p.expiresAt ? ` · expires ${ukDate(p.expiresAt)}` : " · no copy yet"}
          {p.evidence?.fileUrl && <> · <a href={p.evidence.fileUrl} target="_blank" rel="noreferrer" style={dk.a}>registration</a></>}</span></div>)}
      {c?.task && <div style={dk.row}><span style={dk.k}>Task</span><span>{c.task.status === "open" ? `open — sent ${ukDate(c.task.sentAt)} to ${c.task.recipientName || "the branch"}, due ${c.task.dueDate ? ukDate(c.task.dueDate) : "—"}` : c.task.status}</span></div>}
      <div style={dk.row}><span style={dk.k}>Evidence</span><span>{ev?.at ? `${ukDate(ev.at)}${ev.by ? ` by ${ev.by}` : ""}${ev.registrationNumber ? ` · registration ${ev.registrationNumber}` : ""}` : (src === "task" || src === "upload") && !c?.recipientStaffId && !c?.task ? `None yet — no recipient chosen, so no ${src === "upload" ? "request" : "task"} has been sent` : "None received yet"}</span></div>
      {ev?.note && <div style={{
      ...dk.row,
      paddingLeft: 128,
      color: "#D9D9D9"
    }}>“{ev.note}”</div>}
      <div style={{
      paddingLeft: 128
    }}><EvidenceFiles files={files} /></div>
      {hist.length > 0 && <>
        <div style={dk.hd}>History · {hist.length}</div>
        <div style={{
        maxHeight: 200,
        overflowY: "auto"
      }} onScroll={e => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 30) setShown(n => Math.min(hist.length, n + 20));
      }}>
          {hist.slice(0, shown).map((h, i) => <div key={i} style={{
          display: "flex",
          gap: 8,
          fontSize: 11,
          padding: "2px 0",
          borderTop: "1px solid rgba(148,163,184,0.15)"
        }}>
            <span style={{
            width: 110,
            color: "#8C8C8C"
          }}>{h.at ? new Date(h.at).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            }) : "—"}</span>
            <span>{String(h.event || "").replace(/-/g, " ")}{h.by ? ` — ${h.by}` : ""}{h.note ? ` · ${h.note}` : ""}</span></div>)}
        </div>
      </>}
    </div>;
}
// Items made of parts: each part shows its own source; click to see its data.
function PartsPanel({
  parts,
  extraData,
  branch,
  token,
  nmGrid,
  elLabel
}) {
  const [open, setOpen] = useState(null);
  const dbName = extraData?.__grid?.dbByName?.[branch];
  return <div style={{
    padding: 4
  }}>
      {parts.map(x => {
      const gs = gridSourceOf(extraData, x.id, branch, false);
      const src = gs.src || (LIVE_ALIAS[x.id] ? "pharmace" : null);
      const st = SRC_TAG_STYLE[src] || ["#262626", "#8C8C8C", "#434343"];
      const recs = ELEMENT_RECORDS[x.id];
      return <div key={x.id} style={{
        borderBottom: "1px solid #262626"
      }}>
          <div onClick={() => setOpen(open === x.id ? null : x.id)} style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          fontSize: 12,
          padding: "5px 0",
          cursor: "pointer"
        }}>
            <span style={{
            width: 16,
            fontWeight: 700,
            color: x.state === "ok" ? "#B7EB8F" : x.state === "bad" ? "#FFA39E" : "#FFD666"
          }}>{x.state === "ok" ? "✓" : x.state === "bad" ? "✗" : "…"}</span>
            <span style={{
            width: 300,
            fontWeight: 600
          }}>{open === x.id ? "▾ " : "▸ "}{x.label}</span>
            <span style={{
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 4,
            padding: "1px 6px",
            background: st[0],
            color: st[1],
            border: `1px solid ${st[2]}`
          }}>{SRC_TAG[src] || "Not set"}</span>
            <span style={{
            color: "#8C8C8C",
            flex: 1
          }}>{x.note}</span>
          </div>
          {open === x.id && <div style={{
          padding: "4px 0 10px 24px"
        }}>
            {src === "pharmace" && recs ? recs.map(([k, t]) => <PagedRecords key={k} kind={k} dbName={dbName} token={token} title={t} />) : src === "pharmace" ? <div style={{
            fontSize: 12,
            color: "#D9D9D9"
          }}>{x.note || "Read from Pharmace."}</div> : <GridEvidencePanel c={gs.c} src={src} rowCfg={extraData?.__grid?.rowCfg?.[x.id]} />}
          </div>}
        </div>;
    })}
      {nmGrid && <NearMissPanel a={nmGrid} reports={nmGrid.reports} />}
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginTop: 6
    }}>Each part is set up in the Working grid → “{elLabel}”. Click a part to see its data.</div>
    </div>;
}
function ElementList({
  group,
  item,
  branch,
  liveData,
  taskGrid,
  extraData,
  attData,
  token,
  onGridChanged,
  onAttestationsChanged,
  printMode = false,
  readOnly = false,
  autoOpen = null
}) {
  const loc = branchLocation(branch, extraData);
  const els = (item.elements || []).filter(el => rowApplicability(el, loc).applicable).map(el => {
    const d = detailsFor(el, loc);
    return {
      ...el,
      help: d.help,
      link: d.link,
      reference: d.reference
    };
  });
  const [openSrc, setOpenSrc] = useState(autoOpen); // element id whose source panel is open
  const [entryEl, setEntryEl] = useState(null); // element being updated (task-backed) -> TaskEntryDialog
  const [altEl, setAltEl] = useState(null); // element being attested (no data stream) -> AltEvidenceDialog

  // Resolve the grid task behind an element (by def id or by name), plus the
  // branch row - everything the entry dialog needs to post a value.
  const taskFor = elementId => {
    if (!taskGrid) return null;
    let tId = TASK_DEF_FOR[elementId] || null;
    if (!tId && TASK_DEF_BY_KEY[elementId]) tId = taskGrid.idByKey?.[TASK_DEF_BY_KEY[elementId]] || null;
    if (!tId) return null;
    const row = taskGrid.rowsByName?.[branch];
    if (!row || !row.dbName) return null;
    return {
      taskId: tId,
      dataType: taskGrid.dataTypeById?.[tId] || "text",
      options: taskGrid.optionsById?.[tId] || [],
      frequency: taskGrid.frequencyById?.[tId] || null,
      dbName: row.dbName,
      pharmacyId: row.pharmacyId || null,
      cell: row.cells?.[tId] || null
    };
  };
  return <div style={{
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    marginTop: 12
  }}>
      {/* Sticky column header */}
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 12px",
      position: "sticky",
      top: 0,
      zIndex: 2,
      background: "#FAFAFA",
      borderBottom: "1px solid #F0F0F0",
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: "#5D5D5D"
    }}>
        <span style={{
        width: 18,
        textAlign: "center"
      }}>✓</span>
        <span style={{
        flex: 1
      }}>Data element</span>
        <span style={{
        width: 104,
        textAlign: "center"
      }}>Data source</span>
        <span style={{
        width: 88,
        textAlign: "center"
      }}>Review data</span>
        <span style={{
        width: 78,
        textAlign: "center"
      }}>Requirement</span>
        <span style={{
        width: 18,
        textAlign: "center"
      }}>ⓘ</span>
      </div>
      <div>
        {els.map((el, i) => {
        const {
          done,
          live,
          noData,
          alt,
          usage,
          usageNote,
          gridFiles,
          nmGrid,
          gridParts,
          notSet
        } = resolveDone(group.id, item.id, branch, el.id, liveData, taskGrid, extraData, attData);
        const itemNote = extraData?.__grid?.notes?.[el.id],
          branchNote = extraData?.__grid?.cellNotes?.[`${el.id}|${branch}`];
        const us = !alt && (usage || live && noData) ? USAGE_STYLE[usage || "never"] : null;
        const src = sourceInfo(el.id, taskGrid);
        // Print/report mode: every element's Review-data panel is open so
        // the exported report carries the same evidence the website shows.
        const isSrcOpen = printMode || openSrc === el.id;
        const task = taskFor(el.id);
        // Where the evidence comes from at this branch (Working grid).
        const isParts = PARTS_PARENTS.includes(el.id);
        const gs = isParts ? {
          src: "parts",
          c: null
        } : gridSourceOf(extraData, el.id, branch, noData);
        const gsrc = gs.src || (!task && !alt && live && (!noData || ["lfpse", "adv-nms"].includes(el.id)) && !src?.placeholder ? "pharmace" : null);
        const branchDb = extraData?.__grid?.dbByName?.[branch] || taskGrid?.rowsByName?.[branch]?.dbName;
        // Alternative-evidence offer: element has no digital stream for this
        // branch (placeholder or wired-with-no-data) and isn't task-backed.
        const canAlt = !task && (noData || !live || alt) || ALT_OVERRIDES_LIVE.has(el.id);
        // Four visual states: done (green), alt-evidence (teal-green),
        // not-done (red), no-data (grey).
        // With usage levels: never (grey), lapsed (amber), gaps (red), ok (green).
        const rowBg = us ? us.row : alt ? "#E6F7FF" : noData ? "#FAFAFA" : done ? "#F6FFED" : "#FFF1F0";
        const stateColor = us ? us.fg : alt ? "#0E8CFF" : noData ? "#5D5D5D" : done ? "#389E0D" : "#A8071A";
        const mark = us ? us.mark : noData && !alt ? "—" : done ? "✓" : "✗";
        // Source-tag label/colour: Live (data present), No data (wired, no row
        // for this branch), or Placeholder (not wired at all).
        const tagLabel = alt && !gs.src ? "Recorded" : us ? us.tag : notSet ? "Not set" : noData ? "No data" : live ? "Live" : "Not set";
        const tagBorder = us ? us.tagBorder : noData ? "#D9D9D9" : live ? "#B7EB8F" : "#F0F0F0";
        const tagBg = us ? us.tagBg : noData ? "#F0F0F0" : live ? "#F6FFED" : "#F5F5F5";
        const tagColor = us ? us.fg : noData ? "#5D5D5D" : live ? "#389E0D" : "#8C8C8C";
        return <div key={el.id} style={{
          borderTop: i === 0 ? "none" : "1px solid #F5F5F5"
        }}>
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            background: rowBg
          }}>
                <span style={{
              fontSize: 15,
              width: 18,
              textAlign: "center",
              color: stateColor
            }}>
                  {mark}
                </span>
                <span style={{
              flex: 1,
              fontSize: 13,
              color: "#262626"
            }}>
                  {el.label}{el.reference && <span style={{
                display: "block",
                fontSize: 10,
                color: "#475569"
              }}>{el.reference}</span>}
                  {task && task.cell && task.cell.note && !alt && <span style={{
                display: "block",
                fontSize: 11,
                color: "#5D5D5D",
                fontStyle: "italic",
                marginTop: 1
              }} title={task.cell.note}>
                      “{task.cell.note.length > 90 ? task.cell.note.slice(0, 90) + "…" : task.cell.note}”
                    </span>}
                  {us && usageNote && <span style={{
                display: "block",
                fontSize: 11,
                color: us.fg,
                marginTop: 1
              }}>{usageNote}</span>}
                  {gridFiles && gridFiles.length > 0 && <span style={{
                display: "block",
                fontSize: 11,
                marginTop: 1
              }}>
                      {gridFiles.slice(0, 6).map((f, i) => <a key={i} href={f.fileUrl} target="_blank" rel="noreferrer" style={{
                  marginRight: 8
                }}>📄 {f.filename || "file"}</a>)}
                    </span>}
                  {(itemNote || branchNote) && <span style={{
                display: "block",
                fontSize: 11,
                color: "#AD6800",
                background: "#FFFBE6",
                border: "1px solid #FFE58F",
                borderRadius: 4,
                padding: "2px 6px",
                marginTop: 3
              }}>
                      📝 <b>Note for the inspector:</b> {[itemNote, branchNote].filter(Boolean).join(" — ")}
                    </span>}
                  {alt && <span style={{
                display: "block",
                fontSize: 11,
                color: "#0E8CFF",
                marginTop: 1
              }}>
                      {alt.source}{alt.recordedAt ? ` · ${new Date(alt.recordedAt).toLocaleDateString("en-GB")}` : ""}
                    </span>}
                </span>
                {/* DATA SOURCE column — one cell per element, always populated:
                    • periodic-event elements → "Update" button (record a value)
                    • attestable elements     → button showing the recorded
                      source (Head office / Paper / 3rd party…) or "Set source"
                    • live Pharmace-driven elements → static "Head office" tag
                      (the data comes from Pharmace, i.e. the head-office data
                      areas — same thing, so no separate "Pharmace" wording). */}
                <span style={{
              width: 104,
              textAlign: "center",
              flexShrink: 0
            }}>
                  {gsrc && !(task && !gs.src) ? (() => {
                const st = SRC_TAG_STYLE[gsrc] || SRC_TAG_STYLE.parts;
                const tip = gsrc === "pharmace" ? `Read from Pharmace${src?.kind ? ` (${src.kind})` : ""}` : gs.c?.recipientName ? `${SRC_TAG[gsrc]} → ${gs.c.recipientName}` : gsrc === "link" ? `Held in another system: ${gs.c?.linkLabel || gs.c?.linkUrl || ""}` : gsrc === "parts" ? "Made of parts — each has its own source" : `${SRC_TAG[gsrc] || gsrc}${gs.fromRow ? " (item default)" : ""}`;
                return <button onClick={() => setOpenSrc(isSrcOpen ? null : el.id)} title={`${tip} — click to see the data`} style={{
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  border: `1px solid ${st[2]}`,
                  background: st[0],
                  color: st[1],
                  borderRadius: 4,
                  padding: "2px 8px",
                  whiteSpace: "nowrap",
                  outline: isSrcOpen ? "2px solid #000" : "none",
                  outlineOffset: 1
                }}>
                      {gsrc === "parts" ? `Parts · ${PARTS_OF[el.id]?.length || 0}` : SRC_TAG[gsrc] || gsrc}</button>;
              })() : task && !readOnly ? <button onClick={() => setEntryEl({
                el,
                task
              })} title="This item comes from a periodic event — record a new value for it (you can set the date it applies to)" style={{
                outline: entryEl && entryEl.el.id === el.id ? "2px solid #000" : "none",
                outlineOffset: 1,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                border: "1px solid #91D5FF",
                background: "#E6F7FF",
                color: "#0958D9",
                borderRadius: 4,
                padding: "2px 8px",
                whiteSpace: "nowrap"
              }}>
                      Update
                    </button> : canAlt && !readOnly ? <button onClick={() => setAltEl(el)} title={alt ? "View / change / remove the data source recorded for this item" : "Record where the data for this item comes from (head office / paper records / third-party system)"} style={{
                outline: altEl && altEl.id === el.id ? "2px solid #000" : "none",
                outlineOffset: 1,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                border: alt ? "1px solid #69C0FF" : "1px solid #D9D9D9",
                background: alt ? "#E6F7FF" : "#FAFAFA",
                color: alt ? "#0E8CFF" : "#5D5D5D",
                borderRadius: 4,
                padding: "2px 8px",
                whiteSpace: "nowrap"
              }}>
                      {alt ? `${shortAltLabel(alt.source)} ✓` : "Set source"}
                    </button> : <span title="Not set up yet — choose where this item’s evidence comes from in the Working grid (or the Setup assistant)" style={{
                display: "inline-block",
                fontSize: 11,
                fontWeight: 700,
                border: "1px dashed #D9D9D9",
                background: "#fff",
                color: "#8C8C8C",
                borderRadius: 4,
                padding: "2px 8px",
                whiteSpace: "nowrap"
              }}>
                      Not set
                    </span>}
                </span>
                {/* REVIEW DATA column — the ▸ toggle opens the review panel for
                    every element so the underlying data is always inspectable */}
                <span style={{
              width: 88,
              textAlign: "center",
              flexShrink: 0
            }}>
                  <button onClick={() => setOpenSrc(isSrcOpen ? null : el.id)} title="Show the evidence and records for this item" style={{
                outline: isSrcOpen ? "2px solid #000" : "none",
                outlineOffset: 1,
                fontSize: 10,
                fontWeight: 700,
                cursor: "pointer",
                border: `1px solid ${tagBorder}`,
                background: tagBg,
                color: tagColor,
                borderRadius: 4,
                padding: "1px 6px",
                whiteSpace: "nowrap"
              }}>
                    {isSrcOpen ? "▾ " : "▸ "}{tagLabel}
                  </button>
                </span>
                <span style={{
              width: 78,
              textAlign: "center"
            }}><ReqBadge requirement={el.requirement} /></span>
                <span style={{
              width: 18,
              textAlign: "center"
            }}><HelpInfo help={el.help} link={el.link} /></span>
              </div>
              {/* Source / debug panel */}
              {isSrcOpen && src && <div style={{
            background: "#262626",
            color: "#F0F0F0",
            padding: "10px 14px",
            fontSize: 12,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            lineHeight: 1.5,
            borderLeft: !gsrc && !live ? "3px solid #8C8C8C" : "3px solid #52C41A"
          }}>

                  {usage === "never" && !src.placeholder && !gs.src && <div style={{
              color: "#D9D9D9",
              fontWeight: 700,
              marginBottom: 6
            }}>
                      ℹ {usageNote}. Either start using it in Pharmace, or record the
                      alternative evidence this branch uses (e.g. a paper record).
                    </div>}
                  {noData && usage !== "never" && !src.placeholder && !gs.src && <div style={{
              color: "#D9D9D9",
              fontWeight: 700,
              marginBottom: 6
            }}>
                      ℹ Wired to a live source, but this branch has no data — the
                      endpoint returned no row for it (module not active / no
                      records for this pharmacy). Shown as “not done”.
                    </div>}
                  {/* Cleaning + live Pharmace data: skip the API/source rows —
                      show the per-area breakdown (on-time % + current state)
                      and the individual cleaning events instead. */}
                  {gridParts || isParts ? (/* Items made of parts: each part's source and its data. */
            gridParts ? <PartsPanel parts={gridParts} extraData={extraData} branch={branch} token={token} nmGrid={nmGrid} elLabel={el.label} /> : <div style={{
              padding: 8,
              color: "#D9D9D9"
            }}>None of the parts is set up yet — set them up in the Working grid → “{el.label}” (or the Setup assistant).</div>) : gs.src === "web" && gs.web ? (/* Distance-selling website, read and checked automatically. */
            <WebReviewPanel w={gs.web} />) : gs.src && gs.src !== "pharmace" && !nmGrid && el.id !== "rp-log" ? (
            /* Staff-app task / email upload / SOP / held elsewhere / N/A /
               registrants: what the Working grid holds for this branch. */
            <GridEvidencePanel c={gs.c} src={gs.src} rowCfg={extraData?.__grid?.rowCfg?.[el.id]} />) : ELEMENT_RECORDS[el.id] && (gsrc === "pharmace" || el.id === "near-miss" && alt && /^head office/i.test(alt.source || "")) && branchDb ? (/* Pharmace records behind the item, a page at a time. */
            <div>{usageNote && <div style={{
                color: "#D9D9D9",
                marginBottom: 4
              }}>{usageNote}</div>}
                      {ELEMENT_RECORDS[el.id].map(([k, t]) => <PagedRecords key={k} kind={k} dbName={branchDb} token={token} title={t} />)}</div>) : el.id === "fridge-log" && nmGrid?.kind === "fridge" ? (/* Fridge log uploaded by the branch: the same figures as Pharmace fridge monitoring. */
            <FridgePanel a={nmGrid} reports={nmGrid.reports} />) : el.id === "env-clean" && nmGrid?.kind === "cleaning" ? (/* Cleaning log uploaded by the branch: the same figures as Pharmace cleaning. */
            <CleaningPanel a={nmGrid} reports={nmGrid.reports} />) : (el.id === "near-miss" || el.id === "lfpse") && nmGrid ? (
            /* Near-miss log uploaded by the branch (Working grid): the
               analysed figures — per month, trend, gaps, error types. */
            <NearMissPanel a={nmGrid} reports={nmGrid.reports} />) : el.id === "env-clean" && live && !noData && !src.placeholder ? <CleaningAreasGrid pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} /> : el.id === "wb-staffing" && gsrc === "pharmace" ? <WorkforceScheduleGrid dbName={branchDb} token={token} printMode={printMode} /> : ["tos-hours", "ni-tos-hours"].includes(el.id) && (alt && /^head office/i.test(alt.source || "") || !alt && live && !noData && !src.placeholder) ? <PharmacyOpeningHours dbName={extraData?.__grid?.dbByName?.[branch]} token={token} /> : (el.id === "riddor-book" || el.id === "riddor-report") && alt && /^head office/i.test(alt.source || "") ? (
            /* Accident book / RIDDOR + "Head office holds evidence":
               list this branch's accidents — the RIDDOR element shows
               only entries ticked reportable, with the HSE reference. */
            <AccidentListGrid pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} riddorOnly={el.id === "riddor-report"} />) : el.id === "sup-mhra" && alt && /^head office/i.test(alt.source || "") ? (
            /* MHRA + "Head office holds evidence": response
               percentages for this branch as coloured bars. */
            <MhraStatusBars pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} />) : el.id === "adv-nms" && (gsrc === "pharmace" || alt && /^head office/i.test(alt.source || "")) ? (
            /* NMS consent + "Head office holds evidence": bar showing
               the % of NMS records with patient consent recorded. */
            <>
                      <NmsConsentBar pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} />
                      <NmsActivityTable groups={extraData?.["nms-analysis"]?.[branch]?.nms || []} />
                    </>) : el.id === "lfpse" && alt && /^head office/i.test(alt.source || "") ? (
            /* Patient safety + "Head office holds evidence": show the
               incident dates recorded in Pharmace as the supporting
               evidence grid. */
            <PatientIncidentsGrid pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} />) : el.id === "rp-log" && live && !noData && !src.placeholder ? (
            /* RP record + live Pharmace data: signed-in-now, % days
               covered, and the individual sign-in sessions. */
            <RpStatusGrid pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} token={token} preloaded={extraData?.[USAGE_KEY("rp-log")]?.[branch]?.data || null} />) : task ? (
            /* Periodic-event element: Review data shows the recorded
               entries for this item, newest first. */
            <TaskEntryHistoryGrid label={el.label} taskId={task.taskId} dbName={task.dbName} dataType={task.dataType} token={token} refreshKey={task.cell?.entryId || task.cell?.recordedAt || ""} />) : !gsrc && alt ? (/* Recorded with the older "data source" button, not in the Working grid. */
            <div style={{
              color: "#D9D9D9",
              lineHeight: 1.6
            }}>
                      <div><b>Recorded as:</b> {alt.source}{alt.recordedAt ? ` · ${new Date(alt.recordedAt).toLocaleDateString("en-GB")}` : ""}{alt.recordedBy ? ` by ${alt.recordedBy}` : ""}</div>
                      {alt.note && <div>“{alt.note}”</div>}
                      <div style={{
                color: "#8C8C8C",
                marginTop: 4
              }}>This is an older record, made with the data-source button. It counts for now, but holds no evidence: set the item up in the Working grid → “{el.label}” (or the Setup assistant) so the evidence is collected and shown here.</div>
                    </div>) : !gsrc && !live ? <div style={{
              color: "#D9D9D9"
            }}>Not set up yet — choose where this item’s evidence comes from in the Working grid → “{el.label}” (or the Setup assistant). Until then it counts as not in place.</div> : <>
                      <SrcRow k="Source" v={src.kind} />
                      <SrcRow k="Call" v={`${src.method} ${src.path}`} mono />
                      <SrcRow k="Params" v={src.params} />
                      <SrcRow k="Branch match" v={src.field} />
                      <SrcRow k="Rule" v={src.rule} highlight />
                    </>}
                </div>}
            </div>;
      })}
      </div>
      {entryEl && <PeriodicTaskEntryDialog token={token} task={{
      taskId: entryEl.task.taskId,
      description: entryEl.el.label,
      dataType: entryEl.task.dataType,
      frequency: entryEl.task.frequency,
      options: entryEl.task.options || []
    }} target={{
      dbName: entryEl.task.dbName,
      pharmacyId: entryEl.task.pharmacyId,
      pharmacyName: branch
    }} title={`Update: ${entryEl.el.label}`} existingEntry={entryEl.task.cell?.entryId ? {
      entryId: entryEl.task.cell.entryId,
      value: entryEl.task.cell.value,
      recordedAt: entryEl.task.cell.recordedAt,
      note: entryEl.task.cell.note,
      recordedBy: entryEl.task.cell.recordedBy
    } : null} onClose={() => setEntryEl(null)} onSaved={() => {
      setEntryEl(null);
      onGridChanged && onGridChanged();
    }} />}
      {altEl && <AltEvidenceDialog el={altEl} branch={branch} token={token} existing={attData && attData[branch] ? attData[branch][altEl.id] : null} dbName={taskGrid?.rowsByName?.[branch]?.dbName || null} pharmacyId={taskGrid?.rowsByName?.[branch]?.pharmacyId || null} onClose={() => setAltEl(null)} onChanged={att => {
      setAltEl(null);
      onAttestationsChanged && onAttestationsChanged(branch, altEl.id, att);
    }} />}
    </div>;
}

// -----------------------------------------------------------------------------
// PagedRecords (2026-10) — the records behind a Pharmace-fed item, newest
// first, loaded a page at a time from compliance/records as the reader
// scrolls to the end of the list (or presses "Load more").
// -----------------------------------------------------------------------------
const fmtRecDT = d => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x)) return String(d);
  const t = x.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit"
  });
  return `${x.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  })}${t === "00:00" ? "" : ` ${t}`}`;
};
const pick = (o, ...ks) => {
  for (const k of ks) {
    const v = o[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};
const yesNo = (v, y = "Yes", n = "No") => v === true ? y : v === false ? n : v ?? "—";
const RECORD_COLUMNS = {
  "near-miss": [["Date", r => fmtRecDT(r._date)], ["Error type", r => pick(r, "errorTypeId", "errorType") || "—"], ["Detail", r => [...new Set([pick(r, "drugName"), pick(r, "description", "details"), pick(r, "errorDetail"), pick(r, "errorCause"), pick(r, "otherInfo")].filter(Boolean).map(String))].join(" · ") || "—"], ["Comments", r => pick(r, "comments", "comment", "notes") || "—"]],
  cleaning: [["Date", r => fmtRecDT(pick(r, "cleanedAt", "_date"))], ["Area", r => pick(r, "areaName") || "—"], ["Cleaned by", r => pick(r, "cleanedByName", "staffIdName", "staffName", "cleanedBy") || "—"], ["On time", r => yesNo(r.onTime)], ["Notes", r => pick(r, "notes", "note", "comment", "comments") || "—"]],
  rp: [["Pharmacist", r => pick(r, "rpPharmacistIdName", "pharmacistName", "rpName") || "—"], ["Signed in", r => fmtRecDT(r.signInTime)], ["Signed out", r => r.signOutTime ? fmtRecDT(r.signOutTime) : r.isSignedOut === false ? "Still open" : "—"], ["CD keys", r => r.cdKeysReturnedToSafe == null ? "—" : r.cdKeysReturnedToSafe ? "Returned to safe" : "Handed over"]],
  "cd-balance": [["Date", r => fmtRecDT(r._date)], ["Register", r => pick(r, "registerName") || "—"], ["Balance", r => pick(r, "checkedBalance", "totalBalanceIncExpired", "currentBalance", "balance") ?? "—"], ["Previous", r => pick(r, "previousBalance") ?? "—"], ["Checked by", r => pick(r, "staffName", "createdByName", "staffIdName", "checkedByName") || "—"]],
  "cd-discrepancy": [["Date", r => fmtRecDT(pick(r, "entryDate", "_date"))], ["Register", r => pick(r, "registerName") || "—"], ["Description", r => pick(r, "description") || "—"], ["Status", r => r.status === true ? "Resolved" : r.status === false ? "Open" : r.status ?? "—"], ["Raised by", r => pick(r, "staffName", "createdByName", "staffIdName") || "—"]],
  "cd-destruction": [["Date", r => fmtRecDT(r._date)], ["What", r => pick(r, "destructionSource", "reason") || "—"], ["Destroyed by", r => pick(r, "destroyedByName", "destroyedBy") || "—"], ["Witness", r => pick(r, "witnessedByName", "witnessedBy") || "— none —"]],
  fridge: [["Date", r => fmtRecDT(pick(r, "incidentDate", "readingAt", "_date"))], ["Fridge", r => pick(r, "fridgeName") || "—"], ["Current °C", r => pick(r, "currentTemperature", "currentTemp", "temperature") ?? "—"], ["Min °C", r => pick(r, "minTemperature", "minTemp") ?? "—"], ["Max °C", r => pick(r, "maxTemperature", "maxTemp") ?? "—"], ["In range", r => {
    const lo = Number(pick(r, "minTemperature", "minTemp")),
      hi = Number(pick(r, "maxTemperature", "maxTemp"));
    return Number.isFinite(lo) && Number.isFinite(hi) ? lo >= 2 && hi <= 8 ? "Yes" : "No — outside 2–8°C" : "—";
  }], ["Recorded by", r => pick(r, "takenByName", "staffIdName", "createdByName", "checkedByName", "userIdName", "staffName") || "—"], ["Comments", r => pick(r, "comments", "comment", "notes") || "—"]],
  mhra: [["Date", r => fmtRecDT(r._date)], ["Alert", r => [pick(r, "alertTitle"), pick(r, "alertId")].filter(Boolean).join(" · ") || "—"], ["Affected", r => r.isAffected === true ? "Yes" : r.isAffected === false ? "No" : "Not yet known"], ["Outcome", r => pick(r, "affectedStockDetails", "notAffectedReason") || "—"], ["Actioned by", r => pick(r, "actionTakenByStaff") || "—"],
  // Like SOPs read & signed: the staff it was cascaded to, and how many have read it.
  ["Staff read", r => r.staffNotified ? `${r.staffRead}/${r.staffNotified} read${r.staffNotRead ? ` · ${r.staffNotRead} not read` : ""}` : "Not cascaded", r => !r.staffNotified ? "#8C8C8C" : r.staffNotRead ? "#FF7875" : "#95DE64"]],
  "date-checking": [["Date", r => fmtRecDT(r._date)], ["Area", r => pick(r, "areaName") || "—"], ["Checked by", r => pick(r, "checkedByName", "staffIdName", "createdByName", "staffName") || "—"], ["Notes", r => pick(r, "notes", "note", "comments", "comment") || "—"]]
};
const AUTO_SKIP = /^(id|_date|createdAt|updatedAt|isDeleted|__v)$|Id$|_id$/;
function autoColumns(items) {
  const keys = [];
  for (const r of items.slice(0, 20)) for (const [k, v] of Object.entries(r)) {
    if (AUTO_SKIP.test(k) || keys.includes(k) || v === null || typeof v === "object") continue;
    keys.push(k);
    if (keys.length >= 5) break;
  }
  const nice = k => k.replace(/Name$/, "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, c => c.toUpperCase());
  return [["Date", r => fmtRecDT(r._date)], ...keys.map(k => [nice(k), r => {
    const v = r[k];
    if (typeof v === "boolean") return yesNo(v);
    if (typeof v === "string" && /^\d{4}-\d\d-\d\dT/.test(v)) return fmtRecDT(v);
    return v ?? "—";
  }])];
}
export function PagedRecords({
  kind,
  dbName,
  pharmacyId,
  token,
  title,
  pageSize = 20,
  maxHeight = 260
}) {
  const boxRef = useRef(null);
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(undefined); // undefined = not loaded yet; null = no more
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async cursor => {
    if (!dbName && !pharmacyId) return;
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({
        kind,
        limit: String(pageSize),
        ...(dbName ? {
          dbName
        } : {
          pharmacyId
        }),
        ...(cursor ? {
          before: cursor.before,
          beforeId: cursor.beforeId
        } : {})
      });
      const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/records?${qs.toString()}`)}`, {
        headers: token ? {
          Authorization: `Bearer ${token}`
        } : {}
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setItems(x => cursor ? [...x, ...(j.items || [])] : j.items || []);
      setNext(j.next || null);
      if (j.total != null) setTotal(j.total);
    } catch (e) {
      setErr(e.message);
      setNext(n => n === undefined ? null : n);
    } finally {
      setLoading(false);
    }
  }, [kind, dbName, pharmacyId, token, pageSize]);
  useEffect(() => {
    setItems([]);
    setNext(undefined);
    setTotal(null);
    load(null);
  }, [load]);
  const onScroll = e => {
    const el = e.currentTarget;
    if (next && !loading && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) load(next);
  };
  // If a page doesn't fill the box there is nothing to scroll, so fetch the next one.
  useEffect(() => {
    const el = boxRef.current;
    if (el && el.clientHeight > 0 && next && !loading && el.scrollHeight <= el.clientHeight + 4) load(next);
  }, [items, next, loading, load]);
  // Columns that are empty for every record loaded so far are left out.
  const cols = (RECORD_COLUMNS[kind] || autoColumns(items)).filter(([h, f], i) => i === 0 || !items.length || items.some(r => {
    const v = f(r);
    return v != null && v !== "" && v !== "—";
  }));
  const hd = {
    color: "#D9D9D9",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    margin: "12px 0 6px",
    borderTop: "1px solid #262626",
    paddingTop: 8
  };
  return <div style={{
    fontFamily: "inherit"
  }}>
      <div style={hd}>{title} {total != null && <span style={{
        fontWeight: 400,
        color: "#8C8C8C",
        textTransform: "none"
      }}>· {items.length} of {total} loaded{next ? " — scroll for more" : ""}</span>}</div>
      {err && <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn’t load the records: {err}</div>}
      {!err && next !== undefined && !items.length && !loading && <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>No records.</div>}
      {items.length > 0 && <div ref={boxRef} onScroll={onScroll} className="rc-dark-scroll" style={{
      overflowY: "scroll",
      maxHeight,
      paddingRight: 2
    }}>
        <style>{`.rc-dark-scroll { scrollbar-color: #ffffff #262626; scrollbar-width: auto; }
          .rc-dark-scroll::-webkit-scrollbar { width: 12px; }
          .rc-dark-scroll::-webkit-scrollbar-track { background: #262626; border-radius: 6px; }
          .rc-dark-scroll::-webkit-scrollbar-thumb { background: #ffffff; border-radius: 6px; border: 2px solid #262626; }`}</style>
        <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0",
        width: "100%"
      }}>
          <thead><tr>{cols.map(([h]) => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px",
              position: "sticky",
              top: 0,
              background: "#262626",
              color: "#8C8C8C",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10
            }}>{h}</th>)}</tr></thead>
          <tbody>{items.map(r => <tr key={r.id} style={{
            borderTop: "1px solid rgba(148,163,184,0.2)"
          }}>
            {cols.map(([h, f, col]) => <td key={h} style={{
              padding: "4px 8px",
              verticalAlign: "top",
              whiteSpace: h === "Date" ? "nowrap" : "normal",
              ...(col ? {
                color: col(r),
                fontWeight: 700
              } : {})
            }}>{String(f(r) ?? "—")}</td>)}</tr>)}</tbody>
        </table>
        {loading && <div style={{
        color: "#8C8C8C",
        fontSize: 11,
        padding: 6
      }}>Loading more…</div>}
        {next && !loading && <button onClick={() => load(next)} style={{
        margin: 6,
        fontSize: 11
      }}>Load more</button>}
      </div>}
      {loading && !items.length && <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading…</div>}
    </div>;
}

// -----------------------------------------------------------------------------
// CleaningAreasGrid — per-area cleaning lines inside the "Live" source panel
// of the "Cleaning log up to date" element. For each active area: on-time %
// over the last 12 months, last cleaned / next due, and whether it is
// currently up to date and clean (same concepts as the date-checking view).
// -----------------------------------------------------------------------------
function CleaningAreasGrid({
  pharmacyId,
  token
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/cleaning-areas?pharmacyId=${pharmacyId}`)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const fmt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const pctColor = p => p == null ? "#8C8C8C" : p >= 90 ? "#95DE64" : p >= 60 ? "#FFC53D" : "#FF7875";
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 6
    }}>
        Areas — on-time % (12 mo) & current state
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Area breakdown unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading areas…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load areas: {err}</div> : !(data?.areas || []).length ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>No active cleaning areas at this pharmacy.</div> : <div style={{
      overflowY: "auto",
      maxHeight: 260
    }}>
          <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0",
        width: "100%"
      }}>
            <thead>
              <tr>
                {["Area", "Frequency", "Last cleaned", "Next due", "On time (12 mo)", "Current state"].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px",
              position: "sticky",
              top: 0,
              background: "#262626",
              color: "#8C8C8C",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10
            }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.areas.map((a, i) => <tr key={i} style={{
            borderTop: "1px solid rgba(148,163,184,0.2)"
          }}>
                  <td style={{
              padding: "4px 8px"
            }}>{a.areaName}</td>
                  <td style={{
              padding: "4px 8px",
              color: "#8C8C8C",
              textTransform: "capitalize"
            }}>{a.frequency || "—"}</td>
                  <td style={{
              padding: "4px 8px"
            }}>{fmt(a.lastCleaned)}</td>
                  <td style={{
              padding: "4px 8px"
            }}>{fmt(a.nextDue)}</td>
                  <td style={{
              padding: "4px 8px",
              fontWeight: 800,
              color: pctColor(a.onTimePct)
            }} title={a.cleans ? `${a.cleans} clean${a.cleans === 1 ? "" : "s"} recorded in the last 12 months` : "No cleans recorded in the last 12 months"}>
                    {a.onTimePct == null ? "—" : `${a.onTimePct}%`}
                  </td>
                  <td style={{
              padding: "4px 8px",
              fontWeight: 700,
              color: a.upToDate ? "#95DE64" : a.neverLogged ? "#8C8C8C" : "#FF7875"
            }}>
                    {a.upToDate ? "Up to date & clean" : a.neverLogged ? "Never logged" : "Overdue"}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      {/* Every cleaning event, newest first, a page at a time as you scroll. */}
      <PagedRecords kind="cleaning" pharmacyId={pharmacyId} token={token} title="Cleaning events" />
    </div>;
}

// -----------------------------------------------------------------------------
// WorkforceHoursGrid — workforce detail inside the "Live" source panel of the
// "Core & supplementary hours met" element. Per role: active staff count and
// hours worked over the last complete calendar month (timesheet attendance
// data), plus the branch's latest item count from Periodic Tasks and the
// derived hours per 1000 items. Unknown figures render as n/k.
// -----------------------------------------------------------------------------
function WorkforceHoursGrid({
  pharmacyId,
  token
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/workforce-hours?pharmacyId=${pharmacyId}`)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const NK = <span style={{
    color: "#8C8C8C"
  }}>n/k</span>;
  const items = data?.itemCount ?? null;
  const per1000 = hours => hours != null && items != null && items > 0 ? (Math.round(hours / (items / 1000) * 10) / 10).toFixed(1) : null;
  const roles = data?.roles || [];
  const totals = data?.totals || null;
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 6
    }}>
        Workforce — roles, hours worked{data?.month ? ` (${data.month})` : ""} &
        items dispensed
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Workforce breakdown unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading workforce…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load workforce: {err}</div> : !roles.length ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>No active staff recorded for this pharmacy.</div> : <>
          <div style={{
        overflowY: "auto",
        maxHeight: 260
      }}>
            <table style={{
          borderCollapse: "collapse",
          fontSize: 11,
          color: "#F0F0F0",
          width: "100%"
        }}>
              <thead>
                <tr>
                  {["Role", "Staff", `Hours / month`, "Items / month", "Hours per 1000 items"].map(h => <th key={h} style={{
                textAlign: "left",
                padding: "4px 8px",
                position: "sticky",
                top: 0,
                background: "#262626",
                color: "#8C8C8C",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontSize: 10
              }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {roles.map((r, i) => <tr key={i} style={{
              borderTop: "1px solid rgba(148,163,184,0.2)"
            }}>
                    <td style={{
                padding: "4px 8px"
              }}>{r.role}</td>
                    <td style={{
                padding: "4px 8px"
              }}>{r.staffCount}</td>
                    <td style={{
                padding: "4px 8px"
              }} title="Hours worked in the last complete calendar month, from clock-in/out attendance minus breaks taken">
                      {r.hoursMonth != null ? r.hoursMonth.toFixed(1) : NK}
                    </td>
                    <td style={{
                padding: "4px 8px"
              }} title={data?.itemTaskDescription ? `Latest “${data.itemTaskDescription}” value from Periodic Tasks` : "No item-count entry found in Periodic Tasks"}>
                      {items != null ? items.toLocaleString() : NK}
                    </td>
                    <td style={{
                padding: "4px 8px",
                fontWeight: 700
              }}>
                      {per1000(r.hoursMonth) ?? NK}
                    </td>
                  </tr>)}
                {totals && <tr style={{
              borderTop: "2px solid rgba(148,163,184,0.45)",
              fontWeight: 800
            }}>
                    <td style={{
                padding: "4px 8px"
              }}>Total</td>
                    <td style={{
                padding: "4px 8px"
              }}>{totals.staffCount}</td>
                    <td style={{
                padding: "4px 8px"
              }}>
                      {totals.hoursMonth != null ? totals.hoursMonth.toFixed(1) : NK}
                    </td>
                    <td style={{
                padding: "4px 8px"
              }}>
                      {items != null ? items.toLocaleString() : NK}
                    </td>
                    <td style={{
                padding: "4px 8px"
              }}>
                      {per1000(totals.hoursMonth) ?? NK}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
          <div style={{
        color: "#5D5D5D",
        fontSize: 10.5,
        marginTop: 6
      }}>
            Hours = clock-in/out attendance minus breaks (timesheet data);
            items = latest Periodic Tasks entry
            {data?.itemRecordedAt ? ` (recorded ${new Date(data.itemRecordedAt).toLocaleDateString("en-GB")})` : ""}. n/k = not known.
          </div>
        </>}
    </div>;
}

// -----------------------------------------------------------------------------
// RpStatusGrid — Responsible Pharmacist detail inside the "Live" source panel
// of the RP elements: whether an RP is signed in right now (and who / since
// when), the % of the last 30 days with any RP cover, and the individual
// sign-in sessions.
// -----------------------------------------------------------------------------
function RpStatusGrid({
  pharmacyId,
  token,
  preloaded = null
}) {
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (preloaded) {
      setData(preloaded);
      setLoading(false);
      return;
    }
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/rp-status?pharmacyId=${pharmacyId}`)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token, preloaded]);
  const fmtDT = d => d ? new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }) : "—";
  const pct = data?.pctDaysCovered;
  const pctColor = pct == null ? "#8C8C8C" : pct >= 90 ? "#95DE64" : pct >= 60 ? "#FFC53D" : "#FF7875";
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      {data?.source === "upload" && <div style={{
      background: "rgba(45,212,191,0.12)",
      border: "1px solid rgba(45,212,191,0.35)",
      color: "#91D5FF",
      borderRadius: 6,
      padding: "6px 8px",
      fontSize: 11,
      marginBottom: 10,
      lineHeight: 1.5
    }}>
          {data.uploads > 1 ? `Combined from ${data.uploads} uploaded spreadsheets (latest ${data.uploadedAt ? new Date(data.uploadedAt).toLocaleDateString("en-GB") : ""}${data.uploadedBy ? ` by ${data.uploadedBy}` : ""}) — ${data.rowsRead} distinct sessions` : <>From the spreadsheet uploaded {data.uploadedAt ? new Date(data.uploadedAt).toLocaleDateString("en-GB") : ""}{data.uploadedBy ? ` by ${data.uploadedBy}` : ""} — {data.rowsRead} sessions read</>}
          {data.periodFrom ? ` (${new Date(data.periodFrom).toLocaleDateString("en-GB")} – ${new Date(data.periodTo).toLocaleDateString("en-GB")})` : ""}{data.rowsSkipped ? `, ${data.rowsSkipped} rows skipped` : ""}.
          {" "}Columns {data.mapping?.via === "ai" ? "identified by AI" : "matched by heading"}: {Object.entries(data.mapping?.columns || {}).filter(([, v]) => v).map(([k, v]) => `${k} “${v}”`).join(", ")}.
          {" "}Figures are calculated from the rows, not by the AI.{data.fileUrl && <> <a href={data.fileUrl} target="_blank" rel="noreferrer" style={{
          color: "#69C0FF"
        }}>Open the file ↗</a></>}
        </div>}
      {!pharmacyId && !preloaded ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>RP detail unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading RP record…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load RP record: {err}</div> : <>
          {/* Headline stats: signed in now + % days covered */}
          <div style={{
        display: "flex",
        gap: 24,
        flexWrap: "wrap",
        marginBottom: 10
      }}>
            <div>
              <div style={{
            color: "#8C8C8C",
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4
          }}>RP signed in now</div>
              <div style={{
            fontSize: 14,
            fontWeight: 800,
            color: data.currentlySignedIn ? "#95DE64" : "#FF7875"
          }}>
                {data.currentlySignedIn ? "Yes" : "No"}
                {data.currentlySignedIn && data.currentRp && <span style={{
              fontWeight: 400,
              fontSize: 12,
              color: "#F0F0F0"
            }}>
                    {" "}— {data.currentRp.name || "RP"}{data.currentRp.since ? ` since ${fmtDT(data.currentRp.since)}` : ""}
                  </span>}
              </div>
            </div>
            <div>
              <div style={{
            color: "#8C8C8C",
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4
          }}>
                {data.tradingDaysOnly ? "Trading days covered" : "Days covered"} ({data.newUserWindow ? "since first use, " : "last "}{data.windowDays || 28} days)
              </div>
              <div style={{
            fontSize: 14,
            fontWeight: 800,
            color: pctColor
          }}>
                {pct == null ? "—" : `${pct}%`}
                <span style={{
              fontWeight: 400,
              fontSize: 12,
              color: "#8C8C8C"
            }}>
                  {" "}({data.daysCovered}/{data.tradingDays ?? data.windowDays} {data.tradingDaysOnly ? "trading days" : "days"} with RP cover)
                </span>
              </div>
            </div>
          </div>

          {/* Individual sign-in sessions: from Pharmace, every session a page at a
              time as you scroll; from an uploaded sheet, the sessions read from it. */}
          {!data.uploadedAt && pharmacyId ? <PagedRecords kind="rp" pharmacyId={pharmacyId} token={token} title="Sign-in sessions" /> : <>
          {/* Individual sign-in sessions */}
          <div style={{
          color: "#D9D9D9",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          margin: "0 0 6px",
          borderTop: "1px solid #262626",
          paddingTop: 8
        }}>
            Sign-in sessions ({(data.sessions || []).length}{(data.sessions || []).length === 30 ? ", latest" : ""})
          </div>
          {!(data.sessions || []).length ? <div style={{
          color: "#8C8C8C",
          fontSize: 12
        }}>No RP sessions recorded.</div> : <div style={{
          overflowY: "auto",
          maxHeight: 240
        }}>
              <table style={{
            borderCollapse: "collapse",
            fontSize: 11,
            color: "#F0F0F0",
            width: "100%"
          }}>
                <thead>
                  <tr>
                    {["Pharmacist", "Signed in", "Signed out", "CD keys"].map(h => <th key={h} style={{
                  textAlign: "left",
                  padding: "4px 8px",
                  position: "sticky",
                  top: 0,
                  background: "#262626",
                  color: "#8C8C8C",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  fontSize: 10
                }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.map((x, i) => <tr key={i} style={{
                borderTop: "1px solid rgba(148,163,184,0.2)",
                background: x.open ? "rgba(251,191,36,0.12)" : "transparent"
              }}>
                      <td style={{
                  padding: "4px 8px"
                }}>{x.pharmacistName || "—"}</td>
                      <td style={{
                  padding: "4px 8px",
                  whiteSpace: "nowrap"
                }}>{fmtDT(x.signInTime)}</td>
                      <td style={{
                  padding: "4px 8px",
                  whiteSpace: "nowrap",
                  color: x.open ? "#FFC53D" : "#F0F0F0",
                  fontWeight: x.open ? 700 : 400
                }}>
                        {x.open ? "Still open" : fmtDT(x.signOutTime)}
                      </td>
                      <td style={{
                  padding: "4px 8px",
                  color: "#8C8C8C"
                }}>
                        {x.cdKeysReturnedToSafe == null ? "—" : x.cdKeysReturnedToSafe ? "Returned to safe" : "Handed over"}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
          </>}
        </>}
    </div>;
}

// -----------------------------------------------------------------------------
// PatientIncidentsGrid — patient-safety incident dates inside the source panel
// of the LFPSE element when "Head office holds evidence" is the recorded
// source: the incidents logged in Pharmace, newest first, as the supporting
// evidence grid.
// -----------------------------------------------------------------------------
function PatientIncidentsGrid({
  pharmacyId,
  token
}) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/single/patientSafety?pharmacyId=${pharmacyId}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setRows(json?.incidents || []);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const fmt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 6
    }}>
        Patient safety incidents recorded ({(rows || []).length})
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Incident record unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading incidents…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load incidents: {err}</div> : !(rows || []).length ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>No patient safety incidents recorded in Pharmace for this branch.</div> : <div style={{
      overflowY: "auto",
      maxHeight: 260
    }}>
          <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0",
        width: "100%"
      }}>
            <thead>
              <tr>
                {["Incident date", "Harm level", "LFPSE", "LFPSE ref", "RP notified"].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px",
              position: "sticky",
              top: 0,
              background: "#262626",
              color: "#8C8C8C",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10
            }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => <tr key={r.incidentId || i} style={{
            borderTop: "1px solid rgba(148,163,184,0.2)",
            background: r.lfpseIncomplete ? "rgba(248,113,113,0.12)" : "transparent"
          }}>
                  <td style={{
              padding: "4px 8px",
              whiteSpace: "nowrap"
            }}>{fmt(r.incidentDate)}</td>
                  <td style={{
              padding: "4px 8px",
              color: "#8C8C8C"
            }}>{r.harmLevelId ?? "—"}</td>
                  <td style={{
              padding: "4px 8px"
            }}>
                    {r.submitLfpse ? <span style={{
                color: r.lfpseIncomplete ? "#FF7875" : "#95DE64",
                fontWeight: 700
              }}>
                          {r.lfpseIncomplete ? "Ticked — no ref" : "Submitted"}
                        </span> : <span style={{
                color: "#8C8C8C"
              }}>Not required</span>}
                  </td>
                  <td style={{
              padding: "4px 8px"
            }}>{r.lfpseRef || "—"}</td>
                  <td style={{
              padding: "4px 8px",
              color: r.rpNotified ? "#95DE64" : "#8C8C8C"
            }}>
                    {r.rpNotified ? "Yes" : "—"}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
}

// -----------------------------------------------------------------------------
// MhraStatusBars — MHRA response percentages inside the source panel of the
// "MHRA drug/safety alerts actioned" element when "Head office holds
// evidence" is the recorded source: % acknowledged, % relevant, % actioned,
// each as a coloured bar with the underlying counts.
// -----------------------------------------------------------------------------
// The staff × alert grid behind "MHRA alerts read by staff" — the same
// format as the SOP assignment grid: one row per cascaded alert with how many
// have read it, one column per staff member, each cell that person's state.
const MHRA_READ_STATES = {
  read: {
    sym: "✓",
    color: "#95DE64",
    label: "Read"
  },
  pending: {
    sym: "●",
    color: "#FFC53D",
    label: "Not read yet"
  },
  overdue: {
    sym: "✗",
    color: "#FF7875",
    label: "Not read — overdue"
  }
};
function MhraReadGrid({
  mhra
}) {
  const alerts = mhra?.alerts || [],
    items = mhra?.items || [];
  if (!alerts.length) return <div style={{
    color: "#8C8C8C",
    fontSize: 12
  }}>No live MHRA alert has been cascaded to staff at this pharmacy.</div>;
  const staffNames = [...new Set(items.map(a => a.staffName))].sort((a, b) => a.localeCompare(b));
  const cell = new Map(items.map(a => [`${a.mrhaAlertId}||${a.staffName}`, a]));
  const fmt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const th = {
    padding: "4px 6px",
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#262626",
    color: "#8C8C8C",
    fontWeight: 700,
    fontSize: 10
  };
  return <>
      <div style={{
      display: "flex",
      gap: 14,
      fontSize: 10.5,
      color: "#8C8C8C",
      marginBottom: 6,
      flexWrap: "wrap"
    }}>
        {Object.values(MHRA_READ_STATES).map(st => <span key={st.label}><span style={{
          color: st.color,
          fontWeight: 700
        }}>{st.sym}</span> {st.label}</span>)}
        <span>— Not sent to them</span>
      </div>
      <div style={{
      overflow: "auto",
      maxHeight: 280
    }}>
        <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0"
      }}>
          <thead>
            <tr>
              <th style={{
              ...th,
              textAlign: "left",
              left: 0,
              zIndex: 3,
              padding: "4px 8px",
              textTransform: "uppercase",
              letterSpacing: 0.4
            }}>Alert</th>
              <th style={{
              ...th,
              textAlign: "center",
              whiteSpace: "nowrap"
            }}>READ</th>
              {staffNames.map(sn => <th key={sn} title={sn} style={{
              ...th,
              maxWidth: 76,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "center"
            }}>{sn}</th>)}
            </tr>
          </thead>
          <tbody>
            {alerts.map(a => <tr key={a.mrhaAlertId} style={{
            borderTop: "1px solid rgba(148,163,184,0.2)"
          }}>
                <td title={`${a.title}${a.alertId ? ` (${a.alertId})` : ""}${a.alertDate ? ` · ${fmt(a.alertDate)}` : ""}`} style={{
              padding: "4px 8px",
              position: "sticky",
              left: 0,
              zIndex: 1,
              background: "#262626",
              maxWidth: 190,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>{a.title}</td>
                <td title={`${a.read} of ${a.notified} staff have read it · ${a.notRead} not read`} style={{
              padding: "3px 8px",
              textAlign: "center",
              whiteSpace: "nowrap",
              fontWeight: 700,
              color: a.notRead ? "#FF7875" : "#95DE64"
            }}>
                  {a.read}/{a.notified}{a.notRead ? <span style={{
                fontWeight: 400,
                color: "#8C8C8C"
              }}> · {a.notRead} not</span> : ""}</td>
                {staffNames.map(sn => {
              const x = cell.get(`${a.mrhaAlertId}||${sn}`);
              if (!x) return <td key={sn} style={{
                textAlign: "center",
                color: "#5D5D5D",
                padding: "3px 6px"
              }}>—</td>;
              const st = MHRA_READ_STATES[x.status] || MHRA_READ_STATES.pending;
              return <td key={sn} title={`${sn} — ${a.title}\n${st.label}\nSent ${fmt(x.sentAt)}${x.dueDate ? ` · due ${fmt(x.dueDate)}` : ""}${x.readAt ? ` · read ${fmt(x.readAt)}` : ""}`} style={{
                textAlign: "center",
                padding: "3px 6px",
                color: st.color,
                fontWeight: 800,
                cursor: "default"
              }}>{st.sym}</td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
    </>;
}
function MhraStatusBars({
  pharmacyId,
  token
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/compliance/mhra-status?pharmacyId=${pharmacyId}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const barColor = p => p == null ? "#5D5D5D" : p >= 90 ? "#95DE64" : p >= 60 ? "#FFC53D" : "#FF7875";
  const Bar = ({
    label,
    pct,
    detail
  }) => <div style={{
    marginBottom: 10
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      marginBottom: 3
    }}>
        <span style={{
        color: "#D9D9D9",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4
      }}>{label}</span>
        <span style={{
        fontSize: 13,
        fontWeight: 800,
        color: barColor(pct)
      }}>
          {pct == null ? "—" : `${pct}%`}
          <span style={{
          fontWeight: 400,
          fontSize: 11,
          color: "#8C8C8C",
          marginLeft: 6
        }}>{detail}</span>
        </span>
      </div>
      <div style={{
      height: 8,
      borderRadius: 4,
      background: "rgba(148,163,184,0.25)",
      overflow: "hidden"
    }}>
        <div style={{
        height: "100%",
        width: `${Math.max(0, Math.min(100, pct || 0))}%`,
        background: barColor(pct),
        borderRadius: 4
      }} />
      </div>
    </div>;
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 8
    }}>
        MHRA alert response — this branch
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>MHRA detail unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading MHRA figures…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load MHRA figures: {err}</div> : !data || !data.total ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>No live MHRA alerts to respond to.</div> : <>
          <Bar label="Acknowledged" pct={data.pctAcknowledged} detail={`${data.acknowledged}/${data.total} alerts with a recorded response`} />
          <Bar label="Relevant to this branch" pct={data.pctRelevant} detail={`${data.relevant}/${data.total} alerts marked as affecting stock`} />
          <Bar label="Actioned" pct={data.pctActioned} detail={data.relevant ? `${data.actionedOfRelevant}/${data.relevant} relevant alerts with action recorded` : "no relevant alerts"} />
          <Bar label="Read by staff" pct={data.pctStaffRead ?? null} detail={data.staffNotified ? `${data.staffRead}/${data.staffNotified} read · ${data.staffNotRead} not read, across ${data.alertsCascaded} cascaded alert${data.alertsCascaded === 1 ? "" : "s"}` : "no alerts cascaded to staff"} />
        </>}
    </div>;
}

// -----------------------------------------------------------------------------
// NmsConsentBar — inside the source panel of the "NMS consent & records
// compliant" element when "Head office holds evidence" is the recorded
// source: a single coloured bar showing the percentage of this branch's NMS
// records with patient consent recorded. n/k when the consent flag can't be
// established from the data.
// -----------------------------------------------------------------------------
function NmsActivityTable({
  groups
}) {
  if (!groups.length) return null;
  const cell = {
    padding: "6px 8px",
    borderBottom: "1px solid #59606b",
    textAlign: "left"
  };
  return <table style={{
    width: "100%",
    borderCollapse: "collapse",
    color: "#f0f0f0",
    fontSize: 12,
    marginTop: 12
  }}>
    <caption style={{
      textAlign: "left",
      fontWeight: 700,
      marginBottom: 6
    }}>NMS activity recorded in Pharmace</caption>
    <thead><tr>{["Progress", "Status", "Weeks since entry", "Records"].map(label => <th key={label} style={cell}>{label}</th>)}</tr></thead>
    <tbody>{groups.flatMap((group, index) => (group.breakdown || []).map((item, n) => <tr key={`${index}-${n}`}>
      <td style={cell}>{group.type === "COMPLETED" ? "Completed" : "In progress"}</td>
      <td style={cell}>{item.statusId == null ? "Not recorded" : String(item.statusId)}</td>
      <td style={cell}>{item.weeksSinceCreated == null ? "—" : item.weeksSinceCreated}</td>
      <td style={cell}>{item.count || 0}</td>
    </tr>))}</tbody>
  </table>;
}
function NmsConsentBar({
  pharmacyId,
  token
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/nms-consent?pharmacyId=${pharmacyId}`)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const pct = data?.pct ?? null;
  const barColor = p => p == null ? "#5D5D5D" : p >= 90 ? "#95DE64" : p >= 60 ? "#FFC53D" : "#FF7875";
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 8
    }}>
        NMS consent — this branch
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>NMS detail unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading NMS figures…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load NMS figures: {err}</div> : !data || !data.total ? <div style={{
      color: "#AD4E00",
      background: "#FFF7E6",
      border: "1px solid #FFE58F",
      borderRadius: 6,
      padding: 10,
      fontSize: 12
    }}>NMS is connected — no records entered for this pharmacy yet.</div> : pct == null ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>
          {data.total.toLocaleString()} NMS record{data.total === 1 ? "" : "s"} found,
          but consent status: <span style={{
        fontWeight: 700
      }}>n/k</span> — no
          consent flag recognised in the NMS data.
        </div> : <div style={{
      marginBottom: 4
    }}>
          <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 3
      }}>
            <span style={{
          color: "#D9D9D9",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4
        }}>Consent obtained</span>
            <span style={{
          fontSize: 13,
          fontWeight: 800,
          color: barColor(pct)
        }}>
              {pct}%
              <span style={{
            fontWeight: 400,
            fontSize: 11,
            color: "#8C8C8C",
            marginLeft: 6
          }}>
                {data.consented}/{data.total} NMS records with consent recorded
              </span>
            </span>
          </div>
          <div style={{
        height: 8,
        borderRadius: 4,
        background: "rgba(148,163,184,0.25)",
        overflow: "hidden"
      }}>
            <div style={{
          height: "100%",
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: barColor(pct),
          borderRadius: 4
        }} />
          </div>
        </div>}
    </div>;
}

// -----------------------------------------------------------------------------
// AccidentListGrid — accident-book entries inside the source panel of the
// Accidents & RIDDOR elements when "Head office holds evidence" is the
// recorded source. riddorOnly=false: every accident. riddorOnly=true: only
// entries ticked RIDDOR-reportable, with the HSE reference number.
// -----------------------------------------------------------------------------
function AccidentListGrid({
  pharmacyId,
  token,
  riddorOnly = false
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!pharmacyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/single/accidentBook?pharmacyId=${pharmacyId}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pharmacyId, token]);
  const fmt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const STATUS = {
    NOT_YET_KNOWN: ["#FFC53D", "Decision pending"],
    REPORTABLE: ["#FF7875", "RIDDOR reportable"],
    NOT_REPORTABLE: ["#8C8C8C", "Not reportable"]
  };

  // Flatten the three buckets into one dated list; RIDDOR view keeps only
  // the reportable entries.
  const rows = data ? (riddorOnly ? data.reportable || [] : [...(data.notYetKnown || []), ...(data.reportable || []), ...(data.notReportable || [])]).slice().sort((a, b) => new Date(b.accidentDate || 0) - new Date(a.accidentDate || 0)) : [];
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 6
    }}>
        {riddorOnly ? `RIDDOR-reportable accidents (${rows.length})` : `Accident book entries (${rows.length})`}
      </div>
      {!pharmacyId ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Accident record unavailable for this branch.</div> : loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading accidents…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load accidents: {err}</div> : rows.length === 0 ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>
          {riddorOnly ? "No accidents ticked as RIDDOR reportable." : "No accidents recorded at this branch."}
        </div> : <div style={{
      overflowY: "auto",
      maxHeight: 260
    }}>
          <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0",
        width: "100%"
      }}>
            <thead>
              <tr>
                {(riddorOnly ? ["Date", "Person", "Detail", "Reported to HSE", "RIDDOR reference"] : ["Date", "Person", "Location", "Detail", "RIDDOR status"]).map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px",
              position: "sticky",
              top: 0,
              background: "#262626",
              color: "#8C8C8C",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10
            }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
            const st = STATUS[r.riddorStatus] || STATUS.NOT_REPORTABLE;
            const detail = r.accidentDetail ? r.accidentDetail.length > 70 ? r.accidentDetail.slice(0, 70) + "…" : r.accidentDetail : "—";
            return <tr key={r.entryId || i} title={r.accidentDetail || undefined} style={{
              borderTop: "1px solid rgba(148,163,184,0.2)"
            }}>
                    <td style={{
                padding: "4px 8px",
                whiteSpace: "nowrap"
              }}>{fmt(r.accidentDate)}</td>
                    <td style={{
                padding: "4px 8px"
              }}>{r.patientName || r.reportedByName || "—"}</td>
                    {riddorOnly ? <>
                        <td style={{
                  padding: "4px 8px"
                }}>{detail}</td>
                        <td style={{
                  padding: "4px 8px",
                  whiteSpace: "nowrap"
                }}>{fmt(r.riddorReportedAt)}</td>
                        <td style={{
                  padding: "4px 8px",
                  fontWeight: 700,
                  color: r.riddorReferenceNumber ? "#95DE64" : "#FF7875"
                }}>
                          {r.riddorReferenceNumber || "No reference recorded"}
                        </td>
                      </> : <>
                        <td style={{
                  padding: "4px 8px"
                }}>{r.accidentLocation || "—"}</td>
                        <td style={{
                  padding: "4px 8px"
                }}>{detail}</td>
                        <td style={{
                  padding: "4px 8px",
                  fontWeight: 700,
                  color: st[0]
                }}>{st[1]}</td>
                      </>}
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </div>;
}

// -----------------------------------------------------------------------------
// TaskEntryHistoryGrid — the "Review data" panel for periodic-event elements.
// Fetches every recorded entry for (task × branch) from
// GET /api/headoffice/periodic-tasks/history and lists them newest-first
// (the endpoint already sorts recordedAt desc; re-sorted defensively here).
// refreshKey changes when a new value is saved via Update, re-fetching.
// -----------------------------------------------------------------------------
function TaskEntryHistoryGrid({
  label,
  taskId,
  dbName,
  dataType,
  token,
  refreshKey
}) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!taskId || !dbName) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/history?taskId=${encodeURIComponent(taskId)}&dbName=${encodeURIComponent(dbName)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = (json?.entries || []).slice().sort((a, b) => new Date(b.recordedAt || 0) - new Date(a.recordedAt || 0));
        if (!cancelled) setEntries(list);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, dbName, token, refreshKey]);
  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
  const fmtVal = v => {
    if (v === null || v === undefined || v === "") return "—";
    if (dataType === "boolean") return v === true || v === "true" || v === "yes" ? "Yes" : "No";
    if (dataType === "date") return fmtDate(v);
    return String(v);
  };
  return <div style={{
    fontFamily: "system-ui, -apple-system, sans-serif"
  }}>
      <div style={{
      color: "#D9D9D9",
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      marginBottom: 6
    }}>
        Recorded entries — {label} ({(entries || []).length})
      </div>
      {loading ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>Loading entries…</div> : err ? <div style={{
      color: "#FFA39E",
      fontSize: 12
    }}>Couldn't load entries: {err}</div> : !(entries || []).length ? <div style={{
      color: "#8C8C8C",
      fontSize: 12
    }}>
          No entries recorded yet for this item at this branch — use the Update
          button to record the first one.
        </div> : <div style={{
      overflowY: "auto",
      maxHeight: 260
    }}>
          <table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0",
        width: "100%"
      }}>
            <thead>
              <tr>
                {["Date", "Value", "Recorded by", "Note"].map(h => <th key={h} style={{
              textAlign: "left",
              padding: "4px 8px",
              position: "sticky",
              top: 0,
              background: "#262626",
              color: "#8C8C8C",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10
            }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => <tr key={e._id || i} style={{
            borderTop: "1px solid rgba(148,163,184,0.2)"
          }}>
                  <td style={{
              padding: "4px 8px",
              whiteSpace: "nowrap"
            }}>
                    {e.periodKey ? e.periodKey : fmtDate(e.recordedAt)}
                  </td>
                  <td style={{
              padding: "4px 8px",
              fontWeight: 700
            }}>{fmtVal(e.value)}</td>
                  <td style={{
              padding: "4px 8px",
              whiteSpace: "nowrap"
            }}>{e.recordedBy || "—"}</td>
                  <td style={{
              padding: "4px 8px",
              color: "#D9D9D9"
            }}>
                    {e.note ? e.note.length > 120 ? e.note.slice(0, 120) + "…" : e.note : "—"}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
}

// -----------------------------------------------------------------------------
// AltEvidenceDialog: record (or remove) an "alternative evidence of compliance"
// attestation for an element with no digital data stream at this branch -
// e.g. the record is kept on paper. Stored per (pharmacy, element) and shown
// as a teal "Alt evidence" state on the board.
// -----------------------------------------------------------------------------
function AltEvidenceDialog({
  el,
  branch,
  token,
  existing,
  dbName,
  pharmacyId,
  onClose,
  onChanged
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  // One consistent option list for every element. "Head office holds
  // evidence" = the data is in Pharmace (head-office data areas), so no
  // separate "Pharmace (live data)" option is offered.
  const sourceOptions = ALT_SOURCES;
  const [source, setSource] = useState(existing?.source && ALT_SOURCES.includes(existing.source) ? existing.source : existing?.source ? "Other" : ALT_SOURCES[0]);
  const [otherText, setOtherText] = useState(existing?.source && !ALT_SOURCES.includes(existing.source) ? existing.source : "");
  const [note, setNote] = useState(existing?.note || "");
  const [dateStr, setDateStr] = useState(existing?.recordedAt ? new Date(existing.recordedAt).toISOString().slice(0, 10) : todayStr);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const effSource = source === "Other" ? otherText.trim() : source;
  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = {
        dbName,
        pharmacyName: branch,
        elementId: el.id,
        source: effSource,
        note: note.trim(),
        recordedAt: new Date(`${dateStr}T12:00:00`).toISOString()
      };
      const d = await postAttestation(token, "set", body);
      onChanged(d.attestation || {
        ...body
      });
    } catch (e) {
      setErr(String(e.message || e));
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!(await appConfirm("Remove the recorded data source for this item? The element will show as having no data again."))) return;
    setBusy(true);
    setErr("");
    try {
      await postAttestation(token, "clear", {
        dbName,
        elementId: el.id
      });
      onChanged(null);
    } catch (e) {
      setErr(String(e.message || e));
      setBusy(false);
    }
  };
  const inputStyle = {
    padding: "7px 10px",
    fontSize: 13,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    width: "100%",
    boxSizing: "border-box",
    outline: "none"
  };
  const noDb = !dbName;
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1200
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "100%",
      maxWidth: 430,
      background: "#fff",
      borderRadius: 10,
      boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
      padding: 18
    }}>
        <div style={{
        fontSize: 14,
        fontWeight: 800,
        color: "#262626",
        marginBottom: 2
      }}>
          Data source: {el.label}
        </div>
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 10
      }}>{branch}</div>
        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        background: "#E6F7FF",
        border: "1px solid #91D5FF",
        borderRadius: 6,
        padding: "8px 10px",
        marginBottom: 12,
        lineHeight: 1.45
      }}>
          Record where the data for this item comes from. “Head office holds
          evidence” means it is held in Pharmace (the head-office data areas);
          otherwise choose paper records or a third-party system. The recorded
          source is shown against the element and it counts as compliant until
          the record is removed.
        </div>
        {noDb && <div style={{
        fontSize: 12,
        color: "#CF1322",
        marginBottom: 10
      }}>
            This branch isn’t in the periodic-task grid, so its database
            reference can’t be resolved — attestation can’t be saved.
          </div>}

        <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "#5D5D5D",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4
      }}>Data source</label>
        <select value={source} onChange={e => setSource(e.target.value)} style={inputStyle}>
          {sourceOptions.map(sName => <option key={sName} value={sName}>{sName}</option>)}
        </select>
        {source === "Other" && <input type="text" value={otherText} placeholder="Describe the source…" maxLength={80} onChange={e => setOtherText(e.target.value)} style={{
        ...inputStyle,
        marginTop: 6
      }} />}

        <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "#5D5D5D",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        margin: "12px 0 4px"
      }}>
          Note (optional)
        </label>
        <input type="text" value={note} maxLength={500} placeholder="e.g. Signed indemnity certificate in the SOP folder" onChange={e => setNote(e.target.value)} style={inputStyle} />

        <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 700,
        color: "#5D5D5D",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        margin: "12px 0 4px"
      }}>
          Date verified
        </label>
        <input type="date" value={dateStr} max={todayStr} onChange={e => setDateStr(e.target.value)} style={inputStyle} />

        {err && <div style={{
        fontSize: 12,
        color: "#CF1322",
        marginTop: 10
      }}>{err}</div>}
        <div style={{
        display: "flex",
        gap: 8,
        justifyContent: "space-between",
        marginTop: 16
      }}>
          <span>
            {existing && <button onClick={remove} disabled={busy} style={{
            padding: "7px 14px",
            fontSize: 13,
            border: "1px solid #FFA39E",
            background: "#FFF1F0",
            color: "#CF1322",
            borderRadius: 6,
            cursor: "pointer"
          }}>Remove</button>}
          </span>
          <span style={{
          display: "flex",
          gap: 8
        }}>
            <button onClick={onClose} disabled={busy} style={{
            padding: "7px 14px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            background: "#fff",
            color: "#5D5D5D",
            borderRadius: 6,
            cursor: "pointer"
          }}>Cancel</button>
            <button onClick={save} disabled={busy || !effSource || !dateStr || noDb} style={{
            padding: "7px 14px",
            fontSize: 13,
            border: "none",
            fontWeight: 700,
            background: busy ? "#69C0FF" : "#0E8CFF",
            color: "#fff",
            borderRadius: 6,
            cursor: "pointer"
          }}>
              {busy ? "Saving…" : existing ? "Update" : "Save"}
            </button>
          </span>
        </div>
      </div>
    </div>;
}

// One labelled line in the source/debug panel.
function SrcRow({
  k,
  v,
  mono,
  highlight
}) {
  return <div style={{
    display: "flex",
    gap: 8,
    marginBottom: 3
  }}>
      <span style={{
      color: "#5D5D5D",
      minWidth: 92,
      flexShrink: 0
    }}>{k}:</span>
      <span style={{
      color: highlight ? "#B7EB8F" : "#F0F0F0",
      fontFamily: mono || highlight ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
      wordBreak: "break-word"
    }}>{v}</span>
    </div>;
}

// Modal overlay wrapping the data-element list. Used for the branch drill-down
// so it's always visible regardless of how far down the branch grid the user
// clicked. Closes on backdrop click or the × button.
function ElementModal({
  group,
  item,
  branch,
  liveData,
  taskGrid,
  extraData,
  effData,
  attData,
  token,
  onClose,
  onGridChanged,
  onAttestationsChanged
}) {
  const masked = useMaskNames();
  // Close on Escape.
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const metrics = item.metrics || [];
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "3vh 16px 3vh",
    zIndex: 1000
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "100%",
      maxWidth: 660,
      maxHeight: "92vh",
      minHeight: "40vh",
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      borderRadius: 12,
      boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      overflow: "hidden"
    }}>
        {/* Fixed modal header (does not scroll) */}
        <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0",
        background: "#fff",
        flexShrink: 0
      }}>
          <div style={{
          fontWeight: 700,
          fontSize: 15,
          color: "#262626"
        }}>
            {displayName(branch, masked)}
            <span style={{
            color: "#8C8C8C",
            fontWeight: 400,
            fontSize: 13
          }}> · {item.name}</span>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
          border: "none",
          background: "#F5F5F5",
          color: "#434343",
          cursor: "pointer",
          width: 30,
          height: 30,
          borderRadius: 8,
          fontSize: 18,
          lineHeight: 1,
          fontWeight: 700
        }}>
            ×
          </button>
        </div>
        {/* Scrollable body. Effectiveness metrics (12-month % bars) sit above the
            point-in-time element checklist. */}
        <div style={{
        overflowY: "auto",
        flex: 1,
        padding: "0 12px 12px"
      }}>
          {metrics.length > 0 && <MetricsPanel metrics={metrics} branch={branch} effData={effData} token={token} />}
          <ElementList group={group} item={item} branch={branch} liveData={liveData} taskGrid={taskGrid} extraData={extraData} attData={attData} token={token} onGridChanged={onGridChanged} onAttestationsChanged={onAttestationsChanged} />
        </div>
      </div>
    </div>;
}

// -----------------------------------------------------------------------------
// MetricsPanel — the 12-month effectiveness view for a process item (fridge,
// cleaning…). Each metric is a coloured % bar vs its threshold, expandable to
// the four-tier maturity drill-down (ever used → recently → current → year %).
// -----------------------------------------------------------------------------
function MetricsPanel({
  metrics,
  branch,
  effData,
  token,
  initialOpen = null
}) {
  const [openTier, setOpenTier] = useState(initialOpen);
  return <div style={{
    marginTop: 12,
    marginBottom: 12
  }}>
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: "#5D5D5D",
      marginBottom: 6
    }}>
        Process effectiveness · last 12 months
      </div>
      {metrics.map(m => {
      const row = effData?.[m.module]?.[branch] || null;
      const pct = row ? row[m.field] : null;
      const threshold = row ? row[m.thresholdField] : null;
      const band = pctBand(pct, threshold ?? 90);
      const isOpen = openTier === m.id;
      return <div key={m.id} style={{
        border: isOpen ? "2px solid #000" : "1px solid #F0F0F0",
        borderRadius: 8,
        marginBottom: 8,
        overflow: "hidden"
      }}>
            <button onClick={() => setOpenTier(isOpen ? null : m.id)} style={{
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          border: "none",
          background: "#fff",
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 12
        }}>
              <div style={{
            flex: 1
          }}>
                <div style={{
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
                  <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#262626"
              }}>{m.label}</span>
                  <HelpInfo help={m.help} />
                </div>
                {/* Bar */}
                <div style={{
              marginTop: 6,
              height: 10,
              background: "#F5F5F5",
              borderRadius: 999,
              overflow: "hidden",
              position: "relative"
            }}>
                  <div style={{
                width: pct == null ? "0%" : `${Math.max(2, Math.min(100, pct))}%`,
                height: "100%",
                background: band.fg,
                borderRadius: 999
              }} />
                  {threshold != null && <div title={`Target ${threshold}%`} style={{
                position: "absolute",
                top: -2,
                bottom: -2,
                left: `${threshold}%`,
                width: 2,
                background: "#262626",
                opacity: 0.5
              }} />}
                </div>
                <div style={{
              marginTop: 3,
              fontSize: 11,
              color: "#5D5D5D"
            }}>
                  Target {threshold != null ? `${threshold}%` : "—"} · {band.label}
                  {row && row.monthsWithData != null && ` · ${row.monthsWithData} mo data`}
                </div>
              </div>
              <span style={{
            fontSize: 18,
            fontWeight: 800,
            color: band.fg,
            minWidth: 56,
            textAlign: "right"
          }}>
                {pct == null ? "—" : `${Math.round(pct)}%`}
              </span>
              <span style={{
            fontSize: 12,
            color: "#8C8C8C"
          }}>{isOpen ? "▾" : "▸"}</span>
            </button>
            {isOpen && <TierBreakdown row={row} metric={m} pct={pct} threshold={threshold} token={token} />}
          </div>;
    })}
    </div>;
}

// The four-tier maturity drill-down for one metric, plus a per-sub-unit
// month-by-month history table (the Head-Office-style detail).
function TierBreakdown({
  row,
  metric,
  pct,
  threshold,
  token
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Fetch the monthly detail for this pharmacy + module on first expand.
  useEffect(() => {
    let cancelled = false;
    const pid = row?.pharmacyId;
    if (row?.fromUpload && row.upload) return; // the branch's uploaded log holds the history
    if (!pid) return;
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(`compliance/effectiveness-detail?module=${metric.module}&pharmacyId=${pid}&months=12`)}`, {
          headers: token ? {
            Authorization: `Bearer ${token}`
          } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setDetail(json);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row, metric.module, token]);
  const tiers = [{
    k: "Ever used",
    v: row ? row.everUsed ? "Yes" : "No" : "—",
    ok: !!row?.everUsed,
    note: metric.module === "mhra" ? "Has any live MHRA alert been cascaded to staff as a task?" : "Has this tool ever been used (any data in the last 12 months)?"
  }, {
    k: "Used recently",
    v: row ? row.usedRecently ? "Yes" : "No" : "—",
    ok: !!row?.usedRecently,
    note: "Used in the current or previous month?"
  }, {
    k: "12-month rate",
    v: pct == null ? "—" : `${Math.round(pct)}%`,
    ok: pct != null && threshold != null && pct >= threshold,
    note: `Process working over the year vs the ${threshold ?? "—"}% target.`
  }];

  // Which percentage field of the monthly cells this metric is about.
  const monthField = metric.field === "pctInRangeYear" ? "inRangePct" : "onTimePct";
  return <div style={{
    background: "#262626",
    padding: "10px 14px"
  }}>
      {tiers.map((t, i) => <div key={i} style={{
      display: "flex",
      alignItems: "baseline",
      gap: 8,
      marginBottom: 6
    }}>
          <span style={{
        width: 16,
        color: t.ok ? "#B7EB8F" : "#FFA39E",
        fontWeight: 700
      }}>
            {t.ok ? "✓" : "✗"}
          </span>
          <span style={{
        minWidth: 110,
        color: "#F0F0F0",
        fontSize: 12,
        fontWeight: 600
      }}>{t.k}</span>
          <span style={{
        minWidth: 50,
        color: t.ok ? "#B7EB8F" : "#FFA39E",
        fontSize: 12,
        fontWeight: 700
      }}>{t.v}</span>
          <span style={{
        color: "#8C8C8C",
        fontSize: 11
      }}>{t.note}</span>
        </div>)}

      {row?.fromUpload && row.upload ? <UploadedLogDetail upload={row.upload} metric={metric} threshold={threshold} /> : <>
      {/* Detail section: monthly history for fridge/cleaning/date-checking;
          for SOP metrics it is the underlying records themselves. */}
      <div style={{
        marginTop: 10,
        borderTop: "1px solid #262626",
        paddingTop: 8
      }}>
        <div style={{
          color: "#D9D9D9",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 6
        }}>
          {metric.module === "sop" ? metric.id === "sop-reviewed" ? "SOP register — version & planned review dates" : "Assignment grid — staff × SOP" : metric.module === "mhra" ? "Cascade grid — staff × MHRA alert" : "Monthly history"}
        </div>
        {loading ? <div style={{
          color: "#8C8C8C",
          fontSize: 12
        }}>Loading…</div> : err ? <div style={{
          color: "#FFA39E",
          fontSize: 12
        }}>Couldn’t load detail: {err}</div> : detail?.sop && metric.id === "sop-reviewed" ?
        // SOP register: every live SOP, its version date and planned review.
        (detail.sop.sops || []).length === 0 ? <div style={{
          color: "#8C8C8C",
          fontSize: 12
        }}>No SOPs recorded at this pharmacy.</div> : <div style={{
          overflowY: "auto",
          maxHeight: 280
        }}>
            <table style={{
            borderCollapse: "collapse",
            fontSize: 11,
            color: "#F0F0F0",
            width: "100%"
          }}>
              <thead>
                <tr>
                  {["SOP", "Category", "Version date", "Planned review", "Status"].map(h => <th key={h} style={{
                  textAlign: "left",
                  padding: "4px 8px",
                  position: "sticky",
                  top: 0,
                  background: "#262626",
                  color: "#8C8C8C",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  fontSize: 10
                }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {detail.sop.sops.map((x, i) => <tr key={i} style={{
                borderTop: "1px solid rgba(148,163,184,0.2)"
              }}>
                    <td style={{
                  padding: "4px 8px"
                }}>{x.title}</td>
                    <td style={{
                  padding: "4px 8px",
                  color: "#8C8C8C"
                }}>{x.category || "—"}</td>
                    <td style={{
                  padding: "4px 8px"
                }}>{x.versionDate ? new Date(x.versionDate).toLocaleDateString("en-GB") : "—"}</td>
                    <td style={{
                  padding: "4px 8px"
                }}>{x.reviewDate ? new Date(x.reviewDate).toLocaleDateString("en-GB") : "—"}</td>
                    <td style={{
                  padding: "4px 8px",
                  fontWeight: 700,
                  color: x.inDate ? "#95DE64" : "#FF7875"
                }}>
                      {x.inDate ? "In date" : "Review overdue"}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : detail?.sop && metric.id === "sop-read" ?
        // SOP × staff MATRIX — the same grid format as the SOP compliance
        // grid used elsewhere on the site: one row per SOP, one column per
        // staff member, each cell that person's sign-off state.
        (detail.sop.assignments || []).length === 0 ? <div style={{
          color: "#8C8C8C",
          fontSize: 12
        }}>No live SOP assignments at this pharmacy.</div> : (() => {
          const assignments = detail.sop.assignments;
          const staffNames = [...new Set(assignments.map(a => a.staffName))].sort((a, b) => a.localeCompare(b));
          const sopTitles = [...new Set(assignments.map(a => a.sopTitle))].sort((a, b) => a.localeCompare(b));
          const cell = new Map(); // "sop||staff" -> assignment
          for (const a of assignments) cell.set(`${a.sopTitle}||${a.staffName}`, a);
          const fmt = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
          const CELL_STATES = {
            signed: {
              sym: "✓",
              color: "#95DE64",
              label: "Signed"
            },
            pending: {
              sym: "●",
              color: "#FFC53D",
              label: "Pending"
            },
            overdue: {
              sym: "✗",
              color: "#FF7875",
              label: "Overdue"
            }
          };
          return <>
                <div style={{
              display: "flex",
              gap: 14,
              fontSize: 10.5,
              color: "#8C8C8C",
              marginBottom: 6
            }}>
                  {Object.values(CELL_STATES).map(st => <span key={st.label}><span style={{
                  color: st.color,
                  fontWeight: 700
                }}>{st.sym}</span> {st.label}</span>)}
                  <span>— Not assigned</span>
                </div>
                <div style={{
              overflow: "auto",
              maxHeight: 280
            }}>
                  <table style={{
                borderCollapse: "collapse",
                fontSize: 11,
                color: "#F0F0F0"
              }}>
                    <thead>
                      <tr>
                        <th style={{
                      textAlign: "left",
                      padding: "4px 8px",
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 3,
                      background: "#262626",
                      color: "#8C8C8C",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 10
                    }}>SOP</th>
                        {staffNames.map(sn => <th key={sn} title={sn} style={{
                      padding: "4px 6px",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#262626",
                      color: "#8C8C8C",
                      fontWeight: 700,
                      fontSize: 10,
                      maxWidth: 76,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "center"
                    }}>
                            {sn}
                          </th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {sopTitles.map(st => <tr key={st} style={{
                    borderTop: "1px solid rgba(148,163,184,0.2)"
                  }}>
                          <td title={st} style={{
                      padding: "4px 8px",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      background: "#262626",
                      maxWidth: 190,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>{st}</td>
                          {staffNames.map(sn => {
                      const a = cell.get(`${st}||${sn}`);
                      if (!a) return <td key={sn} style={{
                        textAlign: "center",
                        color: "#5D5D5D",
                        padding: "3px 6px"
                      }}>—</td>;
                      const stx = CELL_STATES[a.status] || CELL_STATES.pending;
                      return <td key={sn} title={`${sn} — ${st}
${stx.label}
Assigned ${fmt(a.assignedAt)} · due ${fmt(a.dueDate)}${a.signedAt ? ` · signed ${fmt(a.signedAt)}` : ""}`} style={{
                        textAlign: "center",
                        padding: "3px 6px",
                        color: stx.color,
                        fontWeight: 800,
                        cursor: "default"
                      }}>
                                {stx.sym}
                              </td>;
                    })}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </>;
        })() : detail?.mhra ? <MhraReadGrid mhra={detail.mhra} /> : !detail || !Array.isArray(detail.units) || !detail.units.length ? <div style={{
          color: "#8C8C8C",
          fontSize: 12
        }}>{detail?.note || "No monthly data."}</div> : <div style={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: 260
        }}>
            <table style={{
            borderCollapse: "collapse",
            fontSize: 11,
            color: "#F0F0F0"
          }}>
              <thead>
                <tr>
                  <th style={{
                  textAlign: "left",
                  padding: "2px 8px",
                  color: "#8C8C8C",
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 3,
                  background: "#262626"
                }}>
                    {detail.activityBased ? "Activity" : metric.module === "fridge" ? "Fridge" : "Area"}
                  </th>
                  {detail.monthKeys.slice().reverse().map(mk => <th key={mk} style={{
                  padding: "2px 6px",
                  color: "#8C8C8C",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#262626"
                }}>
                      {mk.slice(2)}
                    </th>)}
                </tr>
              </thead>
              <tbody>
                {detail.units.map(u => {
                const cells = {};
                u.months.forEach(m => {
                  cells[m.monthKey] = m;
                });
                return <tr key={u.unitId}>
                      <td style={{
                    padding: "2px 8px",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    left: 0,
                    background: "#262626"
                  }}>{u.unitName}</td>
                      {detail.monthKeys.slice().reverse().map(mk => {
                    const c = cells[mk];
                    if (detail.activityBased) {
                      // Activity months: colour by active/inactive, show check count.
                      const active = c ? c.active : false;
                      const checks = c ? c.checksPerformed : 0;
                      return <td key={mk} title={c ? `${c.checksPerformed} checks · ${c.uniqueAreasChecked} areas · ${c.drugsLogged} drugs logged` : "No data"} style={{
                        padding: "2px 6px",
                        textAlign: "center",
                        fontWeight: 600,
                        background: active ? "#16633433" : "#991b1b33",
                        color: active ? "#B7EB8F" : "#FFA39E"
                      }}>
                              {active ? checks : "·"}
                            </td>;
                    }
                    const v = c ? c[monthField] : null;
                    const bg = v == null ? "transparent" : v >= (threshold ?? 90) ? "#16633433" : v >= (threshold ?? 90) - 10 ? "#854d0e33" : "#991b1b33";
                    const fg = v == null ? "#5D5D5D" : v >= (threshold ?? 90) ? "#B7EB8F" : v >= (threshold ?? 90) - 10 ? "#FFE58F" : "#FFA39E";
                    return <td key={mk} style={{
                      padding: "2px 6px",
                      textAlign: "center",
                      background: bg,
                      color: fg,
                      fontWeight: 600
                    }}>
                            {v == null ? "·" : `${Math.round(v)}`}
                          </td>;
                  })}
                    </tr>;
              })}
              </tbody>
            </table>
            <div style={{
            color: "#5D5D5D",
            fontSize: 10,
            marginTop: 4
          }}>
              {detail.activityBased ? "Each cell = number of date-checks done that month (green = active, red = none). Hover for detail. Newest on the right." : `Values are ${metric.field === "pctInRangeYear" ? "% of readings in range" : "% done on time"} per month. Newest on the right.`}
            </div>
          </div>}
      </div>
      {/* The raw records behind the analysis, newest first, a page at a time. */}
      {RAW_RECORDS_FOR[metric.module] && row?.pharmacyId && <PagedRecords kind={metric.module} pharmacyId={row.pharmacyId} token={token} title={RAW_RECORDS_FOR[metric.module]} maxHeight={320} />}
      </>}
    </div>;
}
// A 12-month measure that comes from the branch's uploaded log (fridge): the
// month-by-month history per fridge and the readings, from the analysis.
function UploadedLogDetail({
  upload,
  metric,
  threshold
}) {
  const months = (upload.months || []).slice(-12);
  const field = metric.field === "pctInRangeYear" ? "inRangePct" : "onTimePct";
  const t = threshold ?? 90;
  const hd = {
    color: "#D9D9D9",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    margin: "12px 0 6px",
    borderTop: "1px solid #262626",
    paddingTop: 8
  };
  const records = upload.records || [];
  return <div>
    <div style={hd}>Monthly history <span style={{
        fontWeight: 400,
        textTransform: "none",
        color: "#8C8C8C"
      }}>· from the uploaded fridge log</span></div>
    <div style={{
      overflowX: "auto"
    }}><table style={{
        borderCollapse: "collapse",
        fontSize: 11,
        color: "#F0F0F0"
      }}>
      <thead><tr><th style={{
              textAlign: "left",
              padding: "2px 6px",
              color: "#8C8C8C"
            }}>Fridge</th>{months.map(m => <th key={m.month} style={{
              padding: "2px 6px",
              color: "#8C8C8C"
            }}>{m.month.slice(2).replace("-", "-")}</th>)}</tr></thead>
      <tbody>{(upload.fridges || []).map(f => <tr key={f}><td style={{
              padding: "2px 6px",
              whiteSpace: "nowrap"
            }}>{f}</td>{months.map(m => {
              const v = m.byFridge?.[f]?.[field];
              const ok = v != null && v >= t,
                near = v != null && v >= t - 10;
              return <td key={m.month} style={{
                padding: "2px 6px",
                textAlign: "center",
                fontWeight: 600,
                background: v == null ? "transparent" : ok ? "#13542433" : near ? "#874d0033" : "#82071e33",
                color: v == null ? "#595959" : ok ? "#95DE64" : near ? "#FFD666" : "#FFA39E"
              }}>{v == null ? "·" : Math.round(v)}</td>;
            })}</tr>)}</tbody>
    </table></div>
    <div style={{
      color: "#8C8C8C",
      fontSize: 10,
      marginTop: 4
    }}>Values are {field === "inRangePct" ? "% of readings within 2–8 °C" : "% of open days with a reading"} per month. Newest on the right.</div>
    {records.length > 0 && <>
      <div style={hd}>Readings <span style={{
          fontWeight: 400,
          textTransform: "none",
          color: "#8C8C8C"
        }}>· latest {records.length}{upload.recordsTotal > records.length ? ` of ${upload.recordsTotal}` : ""}, from the uploaded log</span></div>
      <div className="rc-dark-scroll" style={{
        overflowY: "scroll",
        maxHeight: 320
      }}>
        <table style={{
          borderCollapse: "collapse",
          fontSize: 11,
          color: "#F0F0F0",
          width: "100%"
        }}>
          <thead><tr>{["Date", "Time", "Fridge", "Current °C", "Min °C", "Max °C", "In range", "By", "Comments"].map(h => <th key={h} style={{
                textAlign: "left",
                padding: "4px 8px",
                position: "sticky",
                top: 0,
                background: "#141414",
                color: "#8C8C8C",
                fontSize: 10,
                textTransform: "uppercase"
              }}>{h}</th>)}</tr></thead>
          <tbody>{records.map((x, i) => <tr key={i} style={{
              borderTop: "1px solid rgba(140,140,140,0.2)"
            }}>
            <td style={{
                padding: "3px 8px",
                whiteSpace: "nowrap"
              }}>{x.date ? x.date.split("-").reverse().join("/") : "—"}</td><td style={{
                padding: "3px 8px"
              }}>{x.time || "—"}</td>
            <td style={{
                padding: "3px 8px"
              }}>{x.fridge}</td><td style={{
                padding: "3px 8px"
              }}>{x.current ?? "—"}</td><td style={{
                padding: "3px 8px"
              }}>{x.min ?? "—"}</td><td style={{
                padding: "3px 8px"
              }}>{x.max ?? "—"}</td>
            <td style={{
                padding: "3px 8px",
                color: x.inRange ? "#95DE64" : "#FFA39E"
              }}>{x.inRange ? "Yes" : "No — outside 2–8°C"}</td><td style={{
                padding: "3px 8px"
              }}>{x.staff || "—"}</td><td style={{
                padding: "3px 8px"
              }}>{x.notes || "—"}</td></tr>)}</tbody>
        </table></div></>}
  </div>;
}
// Effectiveness modules whose individual records are listed under the analysis.
const RAW_RECORDS_FOR = {
  fridge: "Readings",
  cleaning: "Cleaning events",
  "date-checking": "Date checks"
};

// Tag marking an element/item as backed by real live data.
function LiveTag() {
  return <span title="Backed by live data from a Head Office module" style={{
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "1px 5px",
    borderRadius: 4,
    background: "#F6FFED",
    color: "#389E0D",
    border: "1px solid #B7EB8F",
    whiteSpace: "nowrap"
  }}>
      ● Live
    </span>;
}

// Tag marking an element as still on placeholder data.
function PlaceholderTag() {
  return <span title="Placeholder data — not yet wired to a real source" style={{
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "1px 5px",
    borderRadius: 4,
    background: "#F5F5F5",
    color: "#8C8C8C",
    border: "1px solid #F0F0F0",
    whiteSpace: "nowrap"
  }}>
      Placeholder
    </span>;
}

// Small rounded % pill used on item rows.
function PctPill({
  pct
}) {
  return <span style={{
    minWidth: 48,
    textAlign: "center",
    padding: "3px 8px",
    borderRadius: 999,
    background: pctBg(pct),
    border: "1px solid rgba(0,0,0,0.08)",
    fontWeight: 800,
    fontSize: 13,
    color: "#262626"
  }}>
      {fmtPct(pct)}
    </span>;
}

// Legend for the requirement badges + colour meaning.
function Legend() {
  return <div style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 11,
    color: "#5D5D5D",
    flexWrap: "wrap"
  }}>
      <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4
    }}>
        <span style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: pctBg(100),
        border: "1px solid #D9D9D9"
      }} /> 100%
        <span style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: pctBg(50),
        border: "1px solid #D9D9D9",
        marginLeft: 6
      }} /> 50%
        <span style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: pctBg(5),
        border: "1px solid #D9D9D9",
        marginLeft: 6
      }} /> 0%
      </span>
      <span style={{
      display: "inline-flex",
      gap: 6
    }}>
        <ReqBadge requirement="mandatory" />
        <ReqBadge requirement="recommended" />
        <ReqBadge requirement="optional" />
      </span>
    </div>;
}
